import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { createBarLabelRenderer, createLineLabelRenderer, createPieLabelLineRenderer, createPieLabelRenderer } from '../utils/chartLabels';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import BarChartIcon from '@mui/icons-material/BarChart';
import RemoveIcon from '@mui/icons-material/Remove';
import { Alert, Button, Snackbar } from '@mui/material';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import { useRBAC } from '../auth/RBACProvider';
import apiService, {
  buildGoogleAdsCampaignSummaryFallback,
  GoogleAdsCampaignSummary,
  GoogleAdsMetric,
  GoogleAdsMetricsResponse,
  PaidSearchCampaignMetric,
  PaidSearchCampaignResponse,
} from '../services/api';
import logger from '../utils/logger';
import { normalizeAccountKey, resolveCanonicalAccount } from '../utils/accountUtils';

type DatePreset = '7d' | '30d' | 'custom';
type TrendDirection = 'up' | 'down' | 'flat';
type MetricStatus = 'live' | 'proxy' | 'unavailable';
type ChannelKey = 'email' | 'linkedin' | 'google' | 'telemarketing' | 'meta' | 'other';
type BreakdownMode = 'cumulative' | 'daily' | 'weekly' | 'monthly';
type DashboardWidgetId =
  // ── Standard dashboard widgets ───────────────────────────
  | 'kpi-summary'
  | 'cost-summary'
  | 'channel-funnel'
  | 'website-lead-flow'
  | 'email-followers'
  | 'traffic-source'
  | 'geo-visitors'
  | 'geo-visitors-country'
  | 'geo-visitors-city'
  | 'pageviews'
  | 'keywords'
  | 'social-posts'
  | 'ads-performance'
  | 'leads-table'
  | 'insights'
  // ── LinkedIn cross-page widgets ──────────────────────────
  | 'linkedin-campaigns'
  | 'linkedin-monthly-spend'
  | 'linkedin-follower-stats'
  | 'linkedin-lead-forms'
  | 'linkedin-organic-posts'
  // ── Google Analytics cross-page widgets ─────────────────
  | 'ga4-users-stat'
  | 'ga4-sessions-stat'
  | 'ga4-engagement-stat'
  // ── Google Ads cross-page widgets ────────────────────────
  | 'gads-keywords'
  | 'gads-stat-impressions'
  | 'gads-stat-clicks'
  | 'gads-stat-cost'
  | 'gads-campaign-table'
  // ── Email cross-page widgets ─────────────────────────────
  | 'email-campaigns'
  | 'email-stat-openrate'
  | 'email-stat-ctr'
  | 'email-stat-sent'
  | 'email-top-campaigns'
  // ── Leads cross-page widgets ─────────────────────────────
  | 'leads-stat'
  | 'bofu-monthly-trend'
  | 'qualified-monthly-trend'
  | 'keyword-ranking-monthly'
  // ── Phase 1 widget expansion ─────────────────────────────
  | 'ga4-users-total-stat'
  | 'ga4-new-users-stat'
  | 'ga4-sessions-total-stat'
  | 'ga4-pageviews-total-stat'
  | 'ga4-pageviews-per-user-stat'
  | 'ga4-avg-time-per-page-stat'
  | 'ga4-percent-users-stat'
  | 'ga4-pageviews-by-path'
  | 'ga4-fintrans-landing-page-views-by-country'
  | 'ga4-traffic-by-device'
  | 'ga4-total-vs-organic-weekly'
  | 'ga4-paid-search-campaign-performance'
  | 'ga4-paid-search-users-by-campaign'
  | 'ga4-seo-weekly-stats'
  | 'ga4-insights-aggregated'
  | 'ga4-pages-screens'
  | 'ga4-regions'
  | 'gsc-total-clicks-stat'
  | 'gsc-total-impressions-stat'
  | 'gsc-average-ctr-stat'
  | 'gsc-average-position-stat'
  | 'gsc-weekly-performance-trend'
  | 'gsc-queries-table'
  | 'gsc-pages-table'
  | 'gsc-countries-table'
  | 'gsc-devices-table'
  | 'gsc-weekly-table'
  | 'email-total-campaigns-stat'
  | 'email-delivery-rate-stat'
  | 'email-bounce-rate-stat'
  | 'email-unsubscribe-rate-stat'
  | 'email-subject-open-rate-chart'
  | 'linkedin-monthly-spend-trend'
  | 'linkedin-campaign-details'
  | 'linkedin-creative-details'
  | 'linkedin-organic-total-posts-stat'
  | 'linkedin-organic-impressions-stat'
  | 'linkedin-organic-clicks-stat'
  | 'linkedin-organic-engagements-stat'
  | 'linkedin-organic-reactions-stat'
  | 'linkedin-organic-comments-stat'
  | 'linkedin-organic-reposts-stat'
  | 'linkedin-organic-avg-ctr-stat'
  | 'linkedin-organic-avg-engagement-rate-stat'
  | 'linkedin-organic-followers-stat'
  | 'linkedin-organic-impressions-vs-clicks'
  | 'linkedin-organic-ctr-engagement'
  | 'linkedin-active-lead-forms-stat'
  | 'linkedin-total-leads-captured-stat'
  | 'gads-impressions-stat'
  | 'gads-clicks-stat'
  | 'gads-total-cost-stat'
  | 'gads-avg-ctr-stat'
  | 'gads-avg-cpc-stat'
  | 'gads-conversions-stat'
  | 'gads-conv-rate-stat'
  | 'gads-cost-per-conv-stat'
  | 'gads-ctr-trend'
  | 'gads-impressions-clicks-trend'
  | 'gads-performance-by-device'
  | 'gads-performance-by-day-hour'
  | 'gads-campaign-performance-summary';

type GallerySource = 'dashboard' | 'linkedin' | 'google-analytics' | 'search-console' | 'google-ads' | 'email' | 'leads';
type ResizeDirection = 'n' | 's' | 'e' | 'w';
type KeywordMatchTypeFilter = 'ALL' | 'BROAD' | 'EXACT' | 'PHRASE';
type KeywordRankingMetric = 'impressions' | 'clicks' | 'ctr' | 'conversions';

const PRIMARY_CHANNEL_KEYS: ChannelKey[] = ['email', 'linkedin', 'google', 'telemarketing', 'meta'];
const SHARED_GOOGLE_ADS_WIDGET_IDS: DashboardWidgetId[] = ['kpi-summary', 'cost-summary', 'channel-funnel', 'ads-performance'];
const CHANNEL_LABELS: Record<ChannelKey, string> = {
  email: 'Email',
  linkedin: 'LinkedIn',
  google: 'Google',
  telemarketing: 'Telemarketing',
  meta: 'Meta',
  other: 'Other',
};

interface CampaignBudgets {
  email_campaigns?: number;
  telemarketing?: number;
  linkedin?: number;
  google?: number;
  meta?: number;
  currency?: string;
}

interface ClientRecord {
  account?: string;
  client_name?: string;
  industry?: string;
  campaign_budgets?: CampaignBudgets;
  channel_mappings?: Array<{
    channel?: string;
    external_id?: string;
    is_active?: boolean;
  }>;
}

const hasActiveChannelMapping = (client: ClientRecord | null | undefined, channel: string): boolean => (
  Array.isArray(client?.channel_mappings)
    ? client!.channel_mappings!.some((mapping) => (
      normalizeAccountKey(mapping?.channel) === normalizeAccountKey(channel)
      && mapping?.is_active !== false
      && Boolean(String(mapping?.external_id ?? '').trim())
    ))
    : false
);

interface LeadSource {
  type?: string;
}

interface UTMData {
  utm_source?: string;
  utm_medium?: string;
}

interface LeadRecord {
  id?: string;
  account?: string;
  date?: string;
  name?: string;
  company?: string;
  email?: string;
  stage?: string;
  qualified?: boolean;
  bofu_month?: string;
  qualified_month?: string;
  qualified_date?: string;
  owner?: string;
  first_touch_source?: string;
  first_touch_campaign?: string;
  source?: LeadSource;
  utm_data?: UTMData;
}

interface ChannelFunnelStats {
  leads: number;
  mqls: number;
  sqls: number;
}

interface LeadSnapshot {
  totalLeads: number;
  totalMqls: number;
  totalSqls: number;
  byChannel: Record<ChannelKey, ChannelFunnelStats>;
}

interface ChannelSpendState {
  amount: number | null;
  status: MetricStatus;
  note: string;
}

interface ChannelMetricsRow {
  channel: ChannelKey;
  spend: number | null;
  spendStatus: MetricStatus;
  spendNote: string;
  leads: number;
  mqls: number;
  sqls: number;
  cpl: number | null;
  cpmql: number | null;
  cpsql: number | null;
}

interface GeoRow {
  label: string;
  users: number;
}

interface SourceRow {
  source: string;
  users: number;
}

interface PageviewRow {
  pagePath: string;
  pageViews: number;
}

interface KeywordRow {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

interface MonthlyTrendRow {
  monthKey: string;
  monthLabel: string;
  value: number;
}

interface MonthlyKeywordTrendRow {
  monthKey: string;
  monthLabel: string;
  keyword: string;
  matchType: string;
  impressions: number;
  clicks: number;
  ctr: number;
  conversions: number;
}

interface EmailSummary {
  sent: number;
  delivered: number;
  deliveryRate: number;
  opens: number;
  openRate: number;
  clicks: number;
  ctr: number;
}

interface SocialPostRow {
  channel: string;
  label: string;
  impressions: number;
  engagementRate: number;
  ctr: number;
}

interface FollowerRow {
  channel: string;
  totalFollowers: number | null;
  netGrowth: number | null;
  status: MetricStatus;
  note: string;
}

interface AdsRow {
  channel: string;
  impressions: number | null;
  clicks: number | null;
  ctr: number | null;
  conversions: number | null;
  conversionRate: number | null;
  cac: number | null;
  status: MetricStatus;
  note: string;
}

interface LeadFlowRow {
  label: string;
  value: number;
}

interface Trend {
  pct: number;
  direction: TrendDirection;
}

interface DashboardData {
  clientName: string;
  industry: string;
  currency: string;
  totalSpend: number;
  totalSpendTrend: Trend;
  leads: number;
  leadsTrend: Trend;
  mqls: number;
  mqlsTrend: Trend;
  sqls: number;
  sqlsTrend: Trend;
  cpl: number | null;
  cplTrend: Trend;
  cpmql: number | null;
  cpmqlTrend: Trend;
  cpsql: number | null;
  cpsqlTrend: Trend;
  websiteVisitors: number;
  organicVisitors: number;
  websiteConversionRate: number;
  channelRows: ChannelMetricsRow[];
  leadsTableRows: LeadRecord[];
  countryRows: GeoRow[];
  cityRows: GeoRow[];
  sourceRows: SourceRow[];
  pageviewRows: PageviewRow[];
  keywordRows: KeywordRow[];
  emailSummary: EmailSummary;
  socialPosts: SocialPostRow[];
  followers: FollowerRow[];
  adsPerformance: AdsRow[];
  leadFlow: LeadFlowRow[];
  worked: string[];
  attention: string[];
  priorities: string[];
  summaryMessage: string;
}

const createEmptyDashboardData = (account: string): DashboardData => ({
  clientName: account,
  industry: 'Not specified',
  currency: 'INR',
  totalSpend: 0,
  totalSpendTrend: { pct: 0, direction: 'flat' },
  leads: 0,
  leadsTrend: { pct: 0, direction: 'flat' },
  mqls: 0,
  mqlsTrend: { pct: 0, direction: 'flat' },
  sqls: 0,
  sqlsTrend: { pct: 0, direction: 'flat' },
  cpl: null,
  cplTrend: { pct: 0, direction: 'flat' },
  cpmql: null,
  cpmqlTrend: { pct: 0, direction: 'flat' },
  cpsql: null,
  cpsqlTrend: { pct: 0, direction: 'flat' },
  websiteVisitors: 0,
  organicVisitors: 0,
  websiteConversionRate: 0,
  channelRows: [],
  leadsTableRows: [],
  countryRows: [],
  cityRows: [],
  sourceRows: [],
  pageviewRows: [],
  keywordRows: [],
  emailSummary: {
    sent: 0,
    delivered: 0,
    deliveryRate: 0,
    opens: 0,
    openRate: 0,
    clicks: 0,
    ctr: 0,
  },
  socialPosts: [],
  followers: [],
  adsPerformance: [],
  leadFlow: [],
  worked: [],
  attention: [],
  priorities: [],
  summaryMessage: '',
});

interface RangeInfo {
  start: string;
  end: string;
  label: string;
  days: number;
}

interface DashboardFilterState {
  account: string | null;
  datePreset: DatePreset;
  customStartDate: string;
  customEndDate: string;
}

type DashboardSyncState = 'idle' | 'loading_saved' | 'syncing' | 'complete' | 'partial_error';

interface LinkedInAccountInfo {
  account?: string;
  account_id?: string;
  organization_id?: string;
}

interface DashboardWidgetDefinition {
  id: DashboardWidgetId;
  title: string;
  description: string;
  source: GallerySource;
  defaultColSpan: number;
  defaultHeight: number;
  dataProvider?: string;
  defaultVisualization?: ChartType;
  allowedChartTypes?: ChartType[];
  permissions?: Array<'viewer' | 'user' | 'admin' | 'super_admin'>;
  enabled?: boolean;
  deprecated?: boolean;
  replacementWidgetIds?: DashboardWidgetId[];
  displayOrder?: number;
  supportedBreakdowns?: BreakdownMode[];
  defaultBreakdown?: BreakdownMode;
  breakdownMode?: 'client' | 'server';
}

type ChartType = 'bar' | 'line' | 'pie' | 'table';

interface VisualizationConfig {
  chartType: ChartType;
  horizontal: boolean;
}

interface DashboardWidgetLayout {
  colSpan: number;
  height: number;
  hidden: boolean;
  visualizationConfig?: VisualizationConfig;
}

interface ResizeSession {
  id: DashboardWidgetId;
  direction: ResizeDirection;
  startX: number;
  startY: number;
  startColSpan: number;
  startHeight: number;
}

type TrendWidgetId = 'bofu-monthly-trend' | 'qualified-monthly-trend' | 'keyword-ranking-monthly';
type TrendMonthOption = 3 | 6 | 9 | 12;
type EndpointStatus = 'idle' | 'loading' | 'loaded' | 'empty' | 'error' | 'unauthorized_or_mapping_missing';
type InsightSection = 'worked' | 'attention' | 'priorities';

interface InsightDraft {
  worked: string[];
  attention: string[];
  priorities: string[];
}

interface EndpointDiagnostic {
  key: string;
  status: EndpointStatus;
  message: string;
  userLabel?: string;
  userMessage?: string;
}

interface WidgetRegistryApiItem {
  widget_id: string;
  title?: string;
  description?: string;
  source?: string;
  data_provider?: string;
  default_visualization?: string;
  allowed_chart_types?: string[];
  default_size?: {
    colSpan?: number;
    height?: number;
  };
  permissions?: string[];
  enabled?: boolean;
  deprecated?: boolean;
  replacement_widget_ids?: string[];
  display_order?: number;
  supported_breakdowns?: string[];
  default_breakdown?: string;
  breakdown_mode?: string;
}

type EmailTopCampaignSortBy = 'delivered' | 'openRate' | 'ctr';

const sanitizeInsightRows = (rows: string[]): string[] => (
  rows
    .map((row) => row.trim())
    .filter((row) => row.length > 0)
);

const sanitizeInsightDraft = (draft: InsightDraft): InsightDraft => ({
  worked: sanitizeInsightRows(draft.worked),
  attention: sanitizeInsightRows(draft.attention),
  priorities: sanitizeInsightRows(draft.priorities),
});

const FALLBACK_RANGE_DAYS = 7;
const MAX_LEADS_PAGES = 40;
const LEADS_PAGE_SIZE = 250;
const KEYWORD_MONTHLY_LIMIT = 100;
const TABLE_PAGE_SIZES: number[] = [5, 10, 25, 50];
const LEADS_TABLE_ROWS = TABLE_PAGE_SIZES;
const DASHBOARD_LAYOUT_STORAGE_PREFIX = 'wizrdom-dashboard-layout-v1';
const DASHBOARD_GRID_COLUMNS = 12;
const DASHBOARD_COLUMN_PIXEL_STEP = 92;
const DASHBOARD_MIN_WIDGET_HEIGHT = 180;
const DASHBOARD_MAX_WIDGET_HEIGHT = 920;
const TREND_MONTH_OPTIONS: TrendMonthOption[] = [12, 9, 6, 3];
const DEFAULT_TREND_MONTHS: Record<TrendWidgetId, TrendMonthOption> = {
  'bofu-monthly-trend': 9,
  'qualified-monthly-trend': 9,
  'keyword-ranking-monthly': 9,
};
const BREAKDOWN_OPTIONS: BreakdownMode[] = ['cumulative', 'daily', 'weekly', 'monthly'];
const BREAKDOWN_LABELS: Record<BreakdownMode, string> = {
  cumulative: 'Cumulative',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};
const BREAKDOWN_SELECTOR_WIDGETS: ReadonlySet<DashboardWidgetId> = new Set<DashboardWidgetId>([
  'traffic-source',
  'geo-visitors-country',
  'geo-visitors-city',
  'pageviews',
  'ga4-pageviews-by-path',
  'ga4-traffic-by-device',
  'gads-ctr-trend',
  'gads-impressions-clicks-trend',
  'gads-performance-by-device',
  'linkedin-organic-impressions-vs-clicks',
  'linkedin-organic-ctr-engagement',
]);
const BREAKDOWN_TIMESERIES_WIDGETS: ReadonlySet<DashboardWidgetId> = new Set<DashboardWidgetId>([
  'traffic-source',
  'geo-visitors-country',
  'geo-visitors-city',
  'pageviews',
  'ga4-pageviews-by-path',
  'ga4-traffic-by-device',
  'gads-performance-by-device',
]);
const WEEKLY_ONLY_WIDGETS: ReadonlySet<DashboardWidgetId> = new Set<DashboardWidgetId>([
  'ga4-seo-weekly-stats',
  'gsc-weekly-performance-trend',
  'gsc-weekly-table',
]);
const DEFAULT_BREAKDOWN_BY_WIDGET: Partial<Record<DashboardWidgetId, BreakdownMode>> = {
  'traffic-source': 'cumulative',
  'geo-visitors-country': 'cumulative',
  'geo-visitors-city': 'cumulative',
  'pageviews': 'cumulative',
  'ga4-pageviews-by-path': 'cumulative',
  'ga4-traffic-by-device': 'cumulative',
  'gads-ctr-trend': 'cumulative',
  'gads-impressions-clicks-trend': 'cumulative',
  'gads-performance-by-device': 'cumulative',
  'linkedin-organic-impressions-vs-clicks': 'cumulative',
  'linkedin-organic-ctr-engagement': 'cumulative',
};

const ENDPOINT_FRIENDLY_LABELS: Record<string, string> = {
  'client-config': 'Client profile',
  'ga4-summary': 'Website totals',
  'ga4-country': 'Country-level website visitors',
  'ga4-city': 'City-level website visitors',
  'ga4-source-medium': 'Website source/medium',
  'ga4-page': 'Pageviews by page',
  'ga4-landing-page-country-views': 'Landing page views by configured country route',
  'ga4-device': 'Website device breakdown',
  'ga4-weekly': 'Website weekly SEO stats',
  'ga4-first-channel': 'First user channel performance',
  'ga4-weekly-user-acquisition': 'Weekly total and Organic Search traffic',
  'ga4-paid-search-campaigns': 'Paid Search campaign acquisition',
  'search-console-summary': 'Search performance summary',
  'search-console-query': 'Organic keyword search performance',
  'search-console-page': 'Organic page performance',
  'search-console-country': 'Organic country performance',
  'search-console-device': 'Organic device performance',
  'search-console-weekly': 'Organic weekly performance',
  'email-current': 'Email campaign performance',
  'email-previous': 'Previous period email comparison',
  'linkedin-current': 'LinkedIn ads performance',
  'linkedin-previous': 'Previous period LinkedIn comparison',
  'linkedin-campaign-details': 'LinkedIn campaign details',
  'linkedin-organic': 'LinkedIn organic posts',
  'linkedin-accounts': 'LinkedIn account mapping',
  'linkedin-lead-responses': 'LinkedIn lead responses',
  'google-ads-current': 'Google Ads performance',
  'google-ads-previous': 'Previous period Google Ads comparison',
  'google-ads-device': 'Google Ads device performance',
  'leads-current': 'Current leads data',
  'leads-previous': 'Previous period leads data',
  'gads-keywords': 'Google Ads keywords',
  'email-campaigns': 'Email campaign table',
  'linkedin-lead-forms': 'LinkedIn lead forms',
  'bofu-monthly-trend': 'BOFU monthly trend',
  'qualified-monthly-trend': 'Qualified leads monthly trend',
  'keyword-ranking-monthly': 'Monthly keyword ranking trend',
};

const LEGACY_WIDGET_REPLACEMENTS: Partial<Record<DashboardWidgetId, DashboardWidgetId[]>> = {
  'geo-visitors': ['geo-visitors-country', 'geo-visitors-city'],
};

const DASHBOARD_WIDGET_BASE_DEFINITIONS: DashboardWidgetDefinition[] = [
  { id: 'kpi-summary', title: 'Top Funnel KPIs', description: 'Total spend, leads, MQLs and SQLs with period-over-period trends.', source: 'dashboard', defaultColSpan: 12, defaultHeight: 190 },
  { id: 'cost-summary', title: 'Cost Efficiency KPIs', description: 'Cost per lead, MQL and SQL across all channels.', source: 'dashboard', defaultColSpan: 12, defaultHeight: 180 },
  { id: 'channel-funnel', title: 'Spend and Funnel by Channel', description: 'Spend, leads and funnel conversion broken down by marketing channel.', source: 'dashboard', defaultColSpan: 12, defaultHeight: 440 },
  { id: 'website-lead-flow', title: 'Website and Lead Flow', description: 'Website visitor volume and lead conversion flow visualization.', source: 'dashboard', defaultColSpan: 12, defaultHeight: 420 },
  { id: 'email-followers', title: 'Email and Followers', description: 'Email delivery and engagement metrics alongside social follower counts.', source: 'dashboard', defaultColSpan: 12, defaultHeight: 420 },
  { id: 'traffic-source', title: 'Website Visitors by Source', description: 'Bar chart of website sessions grouped by traffic source and medium.', source: 'dashboard', defaultColSpan: 6, defaultHeight: 390 },
  { id: 'geo-visitors-country', title: 'Website Visitors by Country', description: 'Country-level website visitor trend, chart, or table.', source: 'dashboard', defaultColSpan: 6, defaultHeight: 390 },
  { id: 'geo-visitors-city', title: 'Website Visitors by City', description: 'City-level website visitor trend, chart, or table.', source: 'dashboard', defaultColSpan: 6, defaultHeight: 390 },
  { id: 'pageviews', title: 'Pageviews Distribution', description: 'Top pages ranked by page view count over the selected period.', source: 'dashboard', defaultColSpan: 6, defaultHeight: 420 },
  { id: 'keywords', title: 'Keyword Ranking Table', description: 'Organic search keywords with clicks, impressions, CTR and average position.', source: 'dashboard', defaultColSpan: 6, defaultHeight: 420 },
  { id: 'social-posts', title: 'Social Posts by Channel', description: 'LinkedIn organic post impressions and engagement rate.', source: 'dashboard', defaultColSpan: 6, defaultHeight: 420 },
  { id: 'ads-performance', title: 'Ads Performance', description: 'Impressions, clicks, CTR and conversions across paid ad channels.', source: 'dashboard', defaultColSpan: 6, defaultHeight: 420 },
  { id: 'leads-table', title: 'Leads Table', description: 'Paginated table of all leads with stage, source and owner details.', source: 'dashboard', defaultColSpan: 12, defaultHeight: 520 },
  { id: 'insights', title: 'Marketing Insights', description: "AI-generated summary of what's working, what needs attention and priorities.", source: 'dashboard', defaultColSpan: 12, defaultHeight: 330 },
  // LinkedIn
  { id: 'linkedin-campaigns', title: 'LinkedIn Campaign Performance', description: 'Per-campaign breakdown of impressions, clicks, CTR, spend and conversions from LinkedIn Ads.', source: 'linkedin', defaultColSpan: 12, defaultHeight: 440 },
  { id: 'linkedin-monthly-spend', title: 'LinkedIn Monthly Spend', description: 'Bar chart of LinkedIn ad spend aggregated by month across all campaigns.', source: 'linkedin', defaultColSpan: 6, defaultHeight: 360 },
  { id: 'linkedin-follower-stats', title: 'LinkedIn Followers', description: 'Total LinkedIn followers, net growth and follower change vs previous period.', source: 'linkedin', defaultColSpan: 6, defaultHeight: 200 },
  { id: 'linkedin-lead-forms', title: 'LinkedIn Lead Gen Forms', description: 'Lead generation form opens, submissions and submission rate per form.', source: 'linkedin', defaultColSpan: 6, defaultHeight: 360 },
  { id: 'linkedin-organic-posts', title: 'LinkedIn Organic Posts', description: 'Organic post impressions, likes, comments, shares and engagement rate.', source: 'linkedin', defaultColSpan: 6, defaultHeight: 360 },
  // Google Analytics
  { id: 'ga4-users-stat', title: 'Website Users', description: 'Total website users from Google Analytics 4 for the selected period.', source: 'google-analytics', defaultColSpan: 4, defaultHeight: 180 },
  { id: 'ga4-sessions-stat', title: 'Website Sessions', description: 'Total GA4 sessions with conversion rate for the selected period.', source: 'google-analytics', defaultColSpan: 4, defaultHeight: 180 },
  { id: 'ga4-engagement-stat', title: 'Engagement Rate', description: 'GA4 average engagement rate — share of sessions with meaningful interaction.', source: 'google-analytics', defaultColSpan: 4, defaultHeight: 180 },
  // Google Ads
  { id: 'gads-keywords', title: 'Google Ads Keywords', description: 'Top Google Ads keywords with match type, impressions, clicks, CTR and conversion metrics.', source: 'google-ads', defaultColSpan: 12, defaultHeight: 420 },
  { id: 'gads-stat-impressions', title: 'Google Ads Impressions', description: 'Total Google Ads impressions for the selected period.', source: 'google-ads', defaultColSpan: 4, defaultHeight: 180 },
  { id: 'gads-stat-clicks', title: 'Google Ads Clicks', description: 'Total Google Ads clicks and click-through rate.', source: 'google-ads', defaultColSpan: 4, defaultHeight: 180 },
  { id: 'gads-stat-cost', title: 'Google Ads Spend', description: 'Total Google Ads spend for the selected period.', source: 'google-ads', defaultColSpan: 4, defaultHeight: 180 },
  { id: 'gads-campaign-table', title: 'Google Ads Campaigns', description: 'Campaign-level breakdown of impressions, clicks, CTR, cost and conversions.', source: 'google-ads', defaultColSpan: 12, defaultHeight: 420 },
  // Email
  { id: 'email-campaigns', title: 'Email Campaign Performance', description: 'Per-campaign email metrics including send volume, open rate and click-through rate.', source: 'email', defaultColSpan: 12, defaultHeight: 420 },
  { id: 'email-stat-openrate', title: 'Email Open Rate', description: 'Average email open rate and total opens for the selected period.', source: 'email', defaultColSpan: 4, defaultHeight: 180 },
  { id: 'email-stat-ctr', title: 'Email Click Rate', description: 'Email click-through rate (CTR) and total clicks for the selected period.', source: 'email', defaultColSpan: 4, defaultHeight: 180 },
  { id: 'email-stat-sent', title: 'Emails Sent', description: 'Total emails sent and delivery rate for the selected period.', source: 'email', defaultColSpan: 4, defaultHeight: 180 },
  { id: 'email-top-campaigns', title: 'Top Email Campaigns', description: 'Ranked list of top performing email campaigns by open rate.', source: 'email', defaultColSpan: 6, defaultHeight: 360 },
  // Leads
  { id: 'leads-stat', title: 'Lead Funnel Summary', description: 'Total leads, MQLs and SQLs with period-over-period trends.', source: 'leads', defaultColSpan: 12, defaultHeight: 180 },
  { id: 'bofu-monthly-trend', title: 'Monthly BOFU Leads Trend', description: 'BOFU lead counts for the last 9 months, using stage=BOFU and bofu_month.', source: 'leads', defaultColSpan: 6, defaultHeight: 360 },
  { id: 'qualified-monthly-trend', title: 'Monthly Qualified Leads Trend', description: 'Qualified lead counts for the last 9 months, using qualified=TRUE and qualified_month.', source: 'leads', defaultColSpan: 6, defaultHeight: 360 },
  { id: 'keyword-ranking-monthly', title: 'Monthly Keyword Ranking Trend', description: 'Monthly keyword ranking table with match type and ranking metric filters for the last 9 months.', source: 'google-ads', defaultColSpan: 12, defaultHeight: 560 },
];

const RETIRED_WIDGET_METADATA: Partial<Record<DashboardWidgetId, Partial<DashboardWidgetDefinition>>> = {
  'leads-stat': { enabled: false, deprecated: true, replacementWidgetIds: ['kpi-summary'] },
  'linkedin-monthly-spend': { enabled: false, deprecated: true, replacementWidgetIds: ['linkedin-monthly-spend-trend'] },
  'linkedin-campaigns': { enabled: false, deprecated: true, replacementWidgetIds: ['linkedin-campaign-details', 'linkedin-creative-details'] },
  'linkedin-follower-stats': { enabled: false, deprecated: true, replacementWidgetIds: ['linkedin-organic-followers-stat'] },
  'linkedin-lead-forms': { enabled: false, deprecated: true, replacementWidgetIds: ['linkedin-active-lead-forms-stat', 'linkedin-total-leads-captured-stat'] },
  'ga4-users-stat': { enabled: false, deprecated: true, replacementWidgetIds: ['ga4-users-total-stat'] },
  'ga4-sessions-stat': { enabled: false, deprecated: true, replacementWidgetIds: ['ga4-sessions-total-stat'] },
  'ga4-engagement-stat': { enabled: false, deprecated: true, replacementWidgetIds: ['ga4-percent-users-stat'] },
  'gads-stat-impressions': { enabled: false, deprecated: true, replacementWidgetIds: ['gads-impressions-stat'] },
  'gads-stat-clicks': { enabled: false, deprecated: true, replacementWidgetIds: ['gads-clicks-stat'] },
  'gads-stat-cost': { enabled: false, deprecated: true, replacementWidgetIds: ['gads-total-cost-stat'] },
  'gads-campaign-table': { enabled: false, deprecated: true, replacementWidgetIds: ['gads-campaign-performance-summary'] },
  'email-stat-openrate': { enabled: false, deprecated: true, replacementWidgetIds: ['email-total-campaigns-stat', 'email-delivery-rate-stat'] },
  'email-stat-ctr': { enabled: false, deprecated: true, replacementWidgetIds: ['email-top-campaigns'] },
  'email-stat-sent': { enabled: false, deprecated: true, replacementWidgetIds: ['email-delivery-rate-stat'] },
  'ga4-insights-aggregated': { enabled: false, deprecated: true, replacementWidgetIds: ['ga4-pages-screens', 'ga4-regions'] },
  'linkedin-monthly-spend-trend': { enabled: false, deprecated: true, replacementWidgetIds: ['linkedin-campaign-details'] },
  'gads-performance-by-day-hour': { enabled: false, deprecated: true, replacementWidgetIds: ['gads-impressions-clicks-trend', 'gads-performance-by-device'] },
};

const PHASE_ONE_WIDGET_DEFINITIONS: DashboardWidgetDefinition[] = [
  { id: 'ga4-users-total-stat', title: 'Web Campaign Insights - Users', description: 'Total website users from GA4 for the selected period.', source: 'google-analytics', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'ga4.summary' },
  { id: 'ga4-new-users-stat', title: 'Web Campaign Insights - New Users', description: 'New GA4 users for the selected period.', source: 'google-analytics', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'ga4.summary' },
  { id: 'ga4-sessions-total-stat', title: 'Web Campaign Insights - Sessions', description: 'Total GA4 sessions for the selected period.', source: 'google-analytics', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'ga4.summary' },
  { id: 'ga4-pageviews-total-stat', title: 'Web Campaign Insights - Page Views', description: 'Total GA4 page views for the selected period.', source: 'google-analytics', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'ga4.summary' },
  { id: 'ga4-pageviews-per-user-stat', title: 'Web Campaign Insights - Page Views per User', description: 'Average GA4 page views per user.', source: 'google-analytics', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'ga4.summary' },
  { id: 'ga4-avg-time-per-page-stat', title: 'Web Campaign Insights - Avg. Time Per Page', description: 'Average engagement time per page from GA4.', source: 'google-analytics', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'ga4.summary' },
  { id: 'ga4-percent-users-stat', title: 'Web Campaign Insights - % Users', description: 'Share of new users among total users.', source: 'google-analytics', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'ga4.summary' },
  { id: 'ga4-pageviews-by-path', title: 'Web Campaign Insights - Page Views by Page Path', description: 'Top GA4 page paths by views.', source: 'google-analytics', defaultColSpan: 6, defaultHeight: 420, dataProvider: 'ga4.by_page', defaultVisualization: 'bar', allowedChartTypes: ['bar', 'line', 'pie', 'table'] },
  { id: 'ga4-fintrans-landing-page-views-by-country', title: 'Fintrans Landing Page Views By Country', description: 'GA4 page views for explicitly configured country landing-page routes.', source: 'google-analytics', defaultColSpan: 6, defaultHeight: 420, dataProvider: 'ga4.landing_page_views_by_country', defaultVisualization: 'pie', allowedChartTypes: ['bar', 'line', 'pie', 'table'] },
  { id: 'ga4-traffic-by-device', title: 'Web Campaign Insights - User Traffic by Device', description: 'GA4 users grouped by device category.', source: 'google-analytics', defaultColSpan: 6, defaultHeight: 420, dataProvider: 'ga4.by_device', defaultVisualization: 'bar', allowedChartTypes: ['bar', 'line', 'pie', 'table'] },
  { id: 'ga4-total-vs-organic-weekly', title: 'Web Campaign Insights - Total Traffic vs Organic Search Traffic', description: 'GA4 total users compared with Organic Search users across the last eight complete Tuesday-Monday weeks.', source: 'google-analytics', defaultColSpan: 12, defaultHeight: 420, dataProvider: 'ga4.weekly_user_acquisition', defaultVisualization: 'bar', allowedChartTypes: ['bar', 'line', 'table'] },
  { id: 'ga4-paid-search-campaign-performance', title: 'Web Campaign Insights - Paid Search Campaign Performance', description: 'Paid Search campaign spend, delivery, users, conversions and CAC for the selected period.', source: 'google-analytics', defaultColSpan: 12, defaultHeight: 460, dataProvider: 'ga4.paid_search_campaigns' },
  { id: 'ga4-paid-search-users-by-campaign', title: 'Web Campaign Insights - Total Users by First User Campaign', description: 'GA4 total users grouped by their first Paid Search campaign for the selected period.', source: 'google-analytics', defaultColSpan: 12, defaultHeight: 420, dataProvider: 'ga4.paid_search_campaigns', defaultVisualization: 'bar', allowedChartTypes: ['bar', 'line', 'table'] },
  { id: 'ga4-seo-weekly-stats', title: 'Web Campaign Insights - SEO Weekly Stats', description: 'Weekly GA4 SEO metrics (Monday to Sunday).', source: 'google-analytics', defaultColSpan: 12, defaultHeight: 440, dataProvider: 'ga4.weekly' },
  { id: 'ga4-insights-aggregated', title: 'Web Campaign Insights - GA4 Insights Aggregated Data', description: 'Aggregated GA4 insight summary across pages, devices and regions.', source: 'google-analytics', defaultColSpan: 12, defaultHeight: 360, dataProvider: 'ga4.aggregated', enabled: false, deprecated: true, replacementWidgetIds: ['ga4-pages-screens', 'ga4-regions'] },
  { id: 'ga4-pages-screens', title: 'Web Campaign Insights - Pages & Screens', description: 'Page path, views, active users and engagement time.', source: 'google-analytics', defaultColSpan: 12, defaultHeight: 420, dataProvider: 'ga4.by_page' },
  { id: 'ga4-regions', title: 'Web Campaign Insights - Regions', description: 'Country and city level GA4 region breakdown.', source: 'google-analytics', defaultColSpan: 12, defaultHeight: 420, dataProvider: 'ga4.regions' },
  { id: 'gsc-total-clicks-stat', title: 'Search Console Insights - Total Clicks', description: 'Total Search Console clicks.', source: 'search-console', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'search_console.summary' },
  { id: 'gsc-total-impressions-stat', title: 'Search Console Insights - Total Impressions', description: 'Total Search Console impressions.', source: 'search-console', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'search_console.summary' },
  { id: 'gsc-average-ctr-stat', title: 'Search Console Insights - Average CTR', description: 'Average Search Console CTR.', source: 'search-console', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'search_console.summary' },
  { id: 'gsc-average-position-stat', title: 'Search Console Insights - Average Position', description: 'Average Search Console position.', source: 'search-console', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'search_console.summary' },
  { id: 'gsc-weekly-performance-trend', title: 'Search Console Insights - Weekly Performance Trend', description: 'Weekly Search Console clicks and impressions trend.', source: 'search-console', defaultColSpan: 12, defaultHeight: 420, dataProvider: 'search_console.weekly', defaultVisualization: 'line', allowedChartTypes: ['line', 'bar', 'table'] },
  { id: 'gsc-queries-table', title: 'Search Console Insights - Queries', description: 'Search Console query performance.', source: 'search-console', defaultColSpan: 12, defaultHeight: 420, dataProvider: 'search_console.by_query' },
  { id: 'gsc-pages-table', title: 'Search Console Insights - Pages', description: 'Search Console page performance.', source: 'search-console', defaultColSpan: 12, defaultHeight: 420, dataProvider: 'search_console.by_page' },
  { id: 'gsc-countries-table', title: 'Search Console Insights - Countries', description: 'Search Console country performance.', source: 'search-console', defaultColSpan: 12, defaultHeight: 420, dataProvider: 'search_console.by_country' },
  { id: 'gsc-devices-table', title: 'Search Console Insights - Devices', description: 'Search Console device performance.', source: 'search-console', defaultColSpan: 12, defaultHeight: 420, dataProvider: 'search_console.by_device' },
  { id: 'gsc-weekly-table', title: 'Search Console Insights - Weekly', description: 'Search Console weekly table.', source: 'search-console', defaultColSpan: 12, defaultHeight: 420, dataProvider: 'search_console.weekly' },
  { id: 'email-total-campaigns-stat', title: 'Email Campaign Insights - Total Campaigns', description: 'Total email campaigns for the selected period.', source: 'email', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'email.summary' },
  { id: 'email-delivery-rate-stat', title: 'Email Campaign Insights - Delivery Rate', description: 'Email delivery rate for the selected period.', source: 'email', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'email.summary' },
  { id: 'email-bounce-rate-stat', title: 'Email Campaign Insights - Bounce Rate', description: 'Email bounce rate for the selected period.', source: 'email', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'email.summary' },
  { id: 'email-unsubscribe-rate-stat', title: 'Email Campaign Insights - Unsubscribe Rate', description: 'Email unsubscribe rate for the selected period.', source: 'email', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'email.summary' },
  { id: 'email-subject-open-rate-chart', title: 'Email Campaign Insights - Campaign Success by Subject Line', description: 'Open rate by subject line for top email campaigns.', source: 'email', defaultColSpan: 12, defaultHeight: 420, dataProvider: 'email.subject_line_open_rate', defaultVisualization: 'bar', allowedChartTypes: ['bar', 'line', 'pie', 'table'] },
  { id: 'linkedin-monthly-spend-trend', title: 'LinkedIn Campaign Insights - Monthly Spend Trend', description: 'Monthly LinkedIn ad spend trend.', source: 'linkedin', defaultColSpan: 6, defaultHeight: 360, dataProvider: 'linkedin.monthly_spend', defaultVisualization: 'bar', allowedChartTypes: ['bar', 'line', 'table'], enabled: false, deprecated: true, replacementWidgetIds: ['linkedin-campaign-details'] },
  { id: 'linkedin-campaign-details', title: 'LinkedIn Campaign Insights - Campaign Details', description: 'Detailed LinkedIn campaign metrics grouped by campaign type.', source: 'linkedin', defaultColSpan: 12, defaultHeight: 420, dataProvider: 'linkedin.campaign_details' },
  { id: 'linkedin-creative-details', title: 'LinkedIn Campaign Insights - Creative Details', description: 'Detailed LinkedIn creative performance grouped by campaign type.', source: 'linkedin', defaultColSpan: 12, defaultHeight: 420, dataProvider: 'linkedin.creative_details' },
  { id: 'linkedin-organic-total-posts-stat', title: 'LinkedIn Organic Insights - Total Posts', description: 'Total LinkedIn organic posts.', source: 'linkedin', defaultColSpan: 3, defaultHeight: 180, dataProvider: 'linkedin.organic_summary' },
  { id: 'linkedin-organic-impressions-stat', title: 'LinkedIn Organic Insights - Impressions', description: 'Total LinkedIn organic impressions.', source: 'linkedin', defaultColSpan: 3, defaultHeight: 180, dataProvider: 'linkedin.organic_summary' },
  { id: 'linkedin-organic-clicks-stat', title: 'LinkedIn Organic Insights - Clicks', description: 'Total LinkedIn organic clicks.', source: 'linkedin', defaultColSpan: 3, defaultHeight: 180, dataProvider: 'linkedin.organic_summary' },
  { id: 'linkedin-organic-engagements-stat', title: 'LinkedIn Organic Insights - Engagements', description: 'Total LinkedIn organic engagements.', source: 'linkedin', defaultColSpan: 3, defaultHeight: 180, dataProvider: 'linkedin.organic_summary' },
  { id: 'linkedin-organic-reactions-stat', title: 'LinkedIn Organic Insights - Reactions', description: 'Total LinkedIn organic reactions.', source: 'linkedin', defaultColSpan: 3, defaultHeight: 180, dataProvider: 'linkedin.organic_summary' },
  { id: 'linkedin-organic-comments-stat', title: 'LinkedIn Organic Insights - Comments', description: 'Total LinkedIn organic comments.', source: 'linkedin', defaultColSpan: 3, defaultHeight: 180, dataProvider: 'linkedin.organic_summary' },
  { id: 'linkedin-organic-reposts-stat', title: 'LinkedIn Organic Insights - Reposts', description: 'Total LinkedIn organic reposts.', source: 'linkedin', defaultColSpan: 3, defaultHeight: 180, dataProvider: 'linkedin.organic_summary' },
  { id: 'linkedin-organic-avg-ctr-stat', title: 'LinkedIn Organic Insights - Avg. CTR', description: 'Average LinkedIn organic CTR.', source: 'linkedin', defaultColSpan: 3, defaultHeight: 180, dataProvider: 'linkedin.organic_summary' },
  { id: 'linkedin-organic-avg-engagement-rate-stat', title: 'LinkedIn Organic Insights - Avg. Engagement Rate', description: 'Average LinkedIn organic engagement rate.', source: 'linkedin', defaultColSpan: 3, defaultHeight: 180, dataProvider: 'linkedin.organic_summary' },
  { id: 'linkedin-organic-followers-stat', title: 'LinkedIn Organic Insights - Followers', description: 'Total LinkedIn followers.', source: 'linkedin', defaultColSpan: 3, defaultHeight: 180, dataProvider: 'linkedin.followers' },
  { id: 'linkedin-organic-impressions-vs-clicks', title: 'LinkedIn Organic Insights - Impressions vs Clicks (Top 10 Posts)', description: 'Top 10 LinkedIn organic posts by impressions and clicks.', source: 'linkedin', defaultColSpan: 12, defaultHeight: 420, dataProvider: 'linkedin.organic_posts', defaultVisualization: 'bar', allowedChartTypes: ['bar', 'line', 'table'] },
  { id: 'linkedin-organic-ctr-engagement', title: 'LinkedIn Organic Insights - CTR and Engagement Rate (Top 10 Posts)', description: 'Top 10 LinkedIn organic posts by CTR and engagement rate.', source: 'linkedin', defaultColSpan: 12, defaultHeight: 420, dataProvider: 'linkedin.organic_posts', defaultVisualization: 'line', allowedChartTypes: ['line', 'bar', 'table'] },
  { id: 'linkedin-active-lead-forms-stat', title: 'LinkedIn Campaign Insights - Active Lead Forms', description: 'Count of active LinkedIn lead forms.', source: 'linkedin', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'linkedin.lead_forms' },
  { id: 'linkedin-total-leads-captured-stat', title: 'LinkedIn Campaign Insights - Total Leads Captured', description: 'Total LinkedIn lead responses captured.', source: 'linkedin', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'linkedin.lead_responses' },
  { id: 'gads-impressions-stat', title: 'Google Ads Campaign Insights - Impressions', description: 'Total Google Ads impressions.', source: 'google-ads', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'google_ads.summary' },
  { id: 'gads-clicks-stat', title: 'Google Ads Campaign Insights - Clicks', description: 'Total Google Ads clicks.', source: 'google-ads', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'google_ads.summary' },
  { id: 'gads-total-cost-stat', title: 'Google Ads Campaign Insights - Total Cost', description: 'Total Google Ads cost.', source: 'google-ads', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'google_ads.summary' },
  { id: 'gads-avg-ctr-stat', title: 'Google Ads Campaign Insights - Avg CTR', description: 'Average Google Ads CTR.', source: 'google-ads', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'google_ads.summary' },
  { id: 'gads-avg-cpc-stat', title: 'Google Ads Campaign Insights - Avg CPC', description: 'Average Google Ads CPC.', source: 'google-ads', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'google_ads.summary' },
  { id: 'gads-conversions-stat', title: 'Google Ads Campaign Insights - Conversions', description: 'Total Google Ads conversions.', source: 'google-ads', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'google_ads.summary' },
  { id: 'gads-conv-rate-stat', title: 'Google Ads Campaign Insights - Conv. Rate', description: 'Google Ads conversion rate.', source: 'google-ads', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'google_ads.summary' },
  { id: 'gads-cost-per-conv-stat', title: 'Google Ads Campaign Insights - Cost/Conv', description: 'Google Ads cost per conversion.', source: 'google-ads', defaultColSpan: 4, defaultHeight: 180, dataProvider: 'google_ads.summary' },
  { id: 'gads-ctr-trend', title: 'Google Ads Campaign Insights - CTR Trend', description: 'Google Ads CTR trend over time.', source: 'google-ads', defaultColSpan: 12, defaultHeight: 420, dataProvider: 'google_ads.metrics', defaultVisualization: 'line', allowedChartTypes: ['line', 'bar', 'table'] },
  { id: 'gads-impressions-clicks-trend', title: 'Google Ads Campaign Insights - Impressions & Clicks Trend', description: 'Google Ads impressions and clicks trend over time.', source: 'google-ads', defaultColSpan: 12, defaultHeight: 420, dataProvider: 'google_ads.metrics', defaultVisualization: 'bar', allowedChartTypes: ['bar', 'line', 'table'] },
  { id: 'gads-performance-by-device', title: 'Google Ads Campaign Insights - Performance by Device', description: 'Google Ads device performance breakdown.', source: 'google-ads', defaultColSpan: 6, defaultHeight: 420, dataProvider: 'google_ads.device_breakdown', defaultVisualization: 'bar', allowedChartTypes: ['bar', 'line', 'pie', 'table'] },
  { id: 'gads-campaign-performance-summary', title: 'Campaign Performance Summary', description: 'Campaign-level Google Ads performance summary.', source: 'google-ads', defaultColSpan: 12, defaultHeight: 420, dataProvider: 'google_ads.campaign_summary' },
];

const DASHBOARD_WIDGETS: DashboardWidgetDefinition[] = [
  ...DASHBOARD_WIDGET_BASE_DEFINITIONS.map((widget) => ({
    ...widget,
    dataProvider: widget.dataProvider || widget.source,
    defaultVisualization: widget.defaultVisualization || 'table',
    allowedChartTypes: widget.allowedChartTypes || ['table'],
    permissions: widget.permissions || ['viewer', 'user', 'admin', 'super_admin'],
    enabled: widget.enabled ?? true,
    deprecated: widget.deprecated ?? false,
    replacementWidgetIds: widget.replacementWidgetIds || [],
    displayOrder: widget.displayOrder ?? 0,
    supportedBreakdowns: widget.supportedBreakdowns || (BREAKDOWN_SELECTOR_WIDGETS.has(widget.id) ? ['cumulative', 'daily', 'weekly', 'monthly'] : []),
    defaultBreakdown: widget.defaultBreakdown || DEFAULT_BREAKDOWN_BY_WIDGET[widget.id] || 'cumulative',
    breakdownMode: widget.breakdownMode || (BREAKDOWN_SELECTOR_WIDGETS.has(widget.id) ? 'client' : 'client'),
    ...(RETIRED_WIDGET_METADATA[widget.id] || {}),
  })),
  ...PHASE_ONE_WIDGET_DEFINITIONS.map((widget, index) => ({
    ...widget,
    defaultVisualization: widget.defaultVisualization || 'table',
    allowedChartTypes: widget.allowedChartTypes || ['table'],
    permissions: widget.permissions || ['viewer', 'user', 'admin', 'super_admin'],
    enabled: widget.enabled ?? true,
    deprecated: widget.deprecated ?? false,
    replacementWidgetIds: widget.replacementWidgetIds || [],
    displayOrder: widget.displayOrder ?? (400 + index),
    supportedBreakdowns: widget.supportedBreakdowns || (BREAKDOWN_SELECTOR_WIDGETS.has(widget.id) ? ['cumulative', 'daily', 'weekly', 'monthly'] : []),
    defaultBreakdown: widget.defaultBreakdown || DEFAULT_BREAKDOWN_BY_WIDGET[widget.id] || 'cumulative',
    breakdownMode: widget.breakdownMode || (BREAKDOWN_SELECTOR_WIDGETS.has(widget.id) ? 'client' : 'client'),
  })),
];

const numberFormatter = new Intl.NumberFormat('en-IN');

const CROSS_PAGE_WIDGET_IDS: ReadonlySet<DashboardWidgetId> = new Set<DashboardWidgetId>([
  'linkedin-campaigns', 'linkedin-monthly-spend', 'linkedin-follower-stats',
  'linkedin-lead-forms', 'linkedin-organic-posts',
  'ga4-users-stat', 'ga4-sessions-stat', 'ga4-engagement-stat',
  'gads-keywords', 'gads-stat-impressions', 'gads-stat-clicks', 'gads-stat-cost', 'gads-campaign-table',
  'email-campaigns', 'email-stat-openrate', 'email-stat-ctr', 'email-stat-sent', 'email-top-campaigns',
  'leads-stat',
  'ga4-users-total-stat', 'ga4-new-users-stat', 'ga4-sessions-total-stat', 'ga4-pageviews-total-stat',
  'ga4-pageviews-per-user-stat', 'ga4-avg-time-per-page-stat', 'ga4-percent-users-stat', 'ga4-pageviews-by-path',
  'ga4-fintrans-landing-page-views-by-country',
  'ga4-traffic-by-device', 'ga4-total-vs-organic-weekly', 'ga4-paid-search-campaign-performance',
  'ga4-paid-search-users-by-campaign', 'ga4-seo-weekly-stats', 'ga4-insights-aggregated', 'ga4-pages-screens', 'ga4-regions',
  'gsc-total-clicks-stat', 'gsc-total-impressions-stat', 'gsc-average-ctr-stat', 'gsc-average-position-stat',
  'gsc-weekly-performance-trend', 'gsc-queries-table', 'gsc-pages-table', 'gsc-countries-table', 'gsc-devices-table',
  'gsc-weekly-table',
  'email-total-campaigns-stat', 'email-delivery-rate-stat', 'email-bounce-rate-stat', 'email-unsubscribe-rate-stat',
  'email-subject-open-rate-chart',
  'linkedin-monthly-spend-trend', 'linkedin-campaign-details', 'linkedin-creative-details',
  'linkedin-organic-total-posts-stat', 'linkedin-organic-impressions-stat', 'linkedin-organic-clicks-stat',
  'linkedin-organic-engagements-stat', 'linkedin-organic-reactions-stat', 'linkedin-organic-comments-stat',
  'linkedin-organic-reposts-stat', 'linkedin-organic-avg-ctr-stat', 'linkedin-organic-avg-engagement-rate-stat',
  'linkedin-organic-followers-stat', 'linkedin-organic-impressions-vs-clicks', 'linkedin-organic-ctr-engagement',
  'linkedin-active-lead-forms-stat', 'linkedin-total-leads-captured-stat',
  'gads-impressions-stat', 'gads-clicks-stat', 'gads-total-cost-stat', 'gads-avg-ctr-stat', 'gads-avg-cpc-stat',
  'gads-conversions-stat', 'gads-conv-rate-stat', 'gads-cost-per-conv-stat', 'gads-ctr-trend',
  'gads-impressions-clicks-trend', 'gads-performance-by-device',
  'gads-campaign-performance-summary',
]);

const CHART_CAPABLE_WIDGET_IDS: ReadonlySet<DashboardWidgetId> = new Set<DashboardWidgetId>([
  'traffic-source', 'geo-visitors-country', 'geo-visitors-city', 'pageviews', 'email-campaigns',
  'bofu-monthly-trend', 'qualified-monthly-trend',
  'ga4-pageviews-by-path', 'ga4-fintrans-landing-page-views-by-country', 'ga4-traffic-by-device', 'ga4-total-vs-organic-weekly',
  'ga4-paid-search-users-by-campaign', 'gsc-weekly-performance-trend',
  'email-subject-open-rate-chart',
  'linkedin-organic-impressions-vs-clicks', 'linkedin-organic-ctr-engagement',
  'gads-ctr-trend', 'gads-impressions-clicks-trend', 'gads-performance-by-device',
]);

const DEFAULT_WIDGET_VISUALIZATION: Partial<Record<DashboardWidgetId, VisualizationConfig>> = {
  'geo-visitors-country': { chartType: 'bar', horizontal: false },
  'geo-visitors-city': { chartType: 'bar', horizontal: false },
  'email-campaigns': { chartType: 'bar', horizontal: false },
  'bofu-monthly-trend': { chartType: 'bar', horizontal: false },
  'qualified-monthly-trend': { chartType: 'bar', horizontal: false },
  'ga4-pageviews-by-path': { chartType: 'bar', horizontal: false },
  'ga4-fintrans-landing-page-views-by-country': { chartType: 'pie', horizontal: false },
  'ga4-traffic-by-device': { chartType: 'bar', horizontal: false },
  'ga4-total-vs-organic-weekly': { chartType: 'bar', horizontal: false },
  'ga4-paid-search-users-by-campaign': { chartType: 'bar', horizontal: true },
  'gsc-weekly-performance-trend': { chartType: 'line', horizontal: false },
  'email-subject-open-rate-chart': { chartType: 'bar', horizontal: false },
  'linkedin-organic-impressions-vs-clicks': { chartType: 'bar', horizontal: false },
  'linkedin-organic-ctr-engagement': { chartType: 'line', horizontal: false },
  'gads-ctr-trend': { chartType: 'line', horizontal: false },
  'gads-impressions-clicks-trend': { chartType: 'bar', horizontal: false },
  'gads-performance-by-device': { chartType: 'bar', horizontal: false },
};

const SINGLE_STAT_WIDGET_IDS: ReadonlySet<DashboardWidgetId> = new Set<DashboardWidgetId>([
  'ga4-users-total-stat', 'ga4-new-users-stat', 'ga4-sessions-total-stat', 'ga4-pageviews-total-stat',
  'ga4-pageviews-per-user-stat', 'ga4-avg-time-per-page-stat', 'ga4-percent-users-stat',
  'gsc-total-clicks-stat', 'gsc-total-impressions-stat', 'gsc-average-ctr-stat', 'gsc-average-position-stat',
  'email-total-campaigns-stat', 'email-delivery-rate-stat', 'email-bounce-rate-stat', 'email-unsubscribe-rate-stat',
  'linkedin-organic-total-posts-stat', 'linkedin-organic-impressions-stat', 'linkedin-organic-clicks-stat',
  'linkedin-organic-engagements-stat', 'linkedin-organic-reactions-stat', 'linkedin-organic-comments-stat',
  'linkedin-organic-reposts-stat', 'linkedin-organic-avg-ctr-stat', 'linkedin-organic-avg-engagement-rate-stat',
  'linkedin-organic-followers-stat', 'linkedin-active-lead-forms-stat', 'linkedin-total-leads-captured-stat',
  'gads-impressions-stat', 'gads-clicks-stat', 'gads-total-cost-stat', 'gads-avg-ctr-stat',
  'gads-avg-cpc-stat', 'gads-conversions-stat', 'gads-conv-rate-stat', 'gads-cost-per-conv-stat',
]);

const GALLERY_TABS: { key: GallerySource; label: string }[] = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'google-analytics', label: 'Google Analytics' },
  { key: 'search-console', label: 'Search Console' },
  { key: 'google-ads', label: 'Google Ads' },
  { key: 'email', label: 'Email' },
  { key: 'leads', label: 'Leads' },
];

const PIE_COLORS = ['#496786', '#C78244', '#7A9EBE', '#5D8C73', '#8B6CA8', '#E18B52', '#6E89A8', '#A77F5A'];

const buildDefaultWidgetLayouts = (): Record<DashboardWidgetId, DashboardWidgetLayout> => (
  DASHBOARD_WIDGETS.reduce((layouts, widget) => {
    layouts[widget.id] = {
      colSpan: widget.defaultColSpan,
      height: widget.defaultHeight,
      hidden: Boolean(widget.deprecated) || CROSS_PAGE_WIDGET_IDS.has(widget.id),
      visualizationConfig: DEFAULT_WIDGET_VISUALIZATION[widget.id]
        ? { ...DEFAULT_WIDGET_VISUALIZATION[widget.id]! }
        : undefined,
    };
    return layouts;
  }, {} as Record<DashboardWidgetId, DashboardWidgetLayout>)
);

const clampWidgetColSpan = (value: number): number => (
  Math.min(DASHBOARD_GRID_COLUMNS, Math.max(3, Math.round(value)))
);

const clampWidgetHeight = (value: number): number => (
  Math.min(DASHBOARD_MAX_WIDGET_HEIGHT, Math.max(DASHBOARD_MIN_WIDGET_HEIGHT, Math.round(value)))
);

const getDashboardLayoutStorageKey = (account: string): string => (
  `${DASHBOARD_LAYOUT_STORAGE_PREFIX}:${normalizeAccountKey(account)}`
);

const normalizeTrendMonthOption = (value: unknown, fallback: TrendMonthOption): TrendMonthOption => (
  TREND_MONTH_OPTIONS.includes(Number(value) as TrendMonthOption)
    ? Number(value) as TrendMonthOption
    : fallback
);

const normalizeStoredTrendMonths = (storedSettings: unknown): Record<TrendWidgetId, TrendMonthOption> => {
  const defaults = { ...DEFAULT_TREND_MONTHS };
  if (!storedSettings || typeof storedSettings !== 'object') {
    return defaults;
  }

  const record = storedSettings as Partial<Record<TrendWidgetId, unknown>>;
  return {
    'bofu-monthly-trend': normalizeTrendMonthOption(record['bofu-monthly-trend'], defaults['bofu-monthly-trend']),
    'qualified-monthly-trend': normalizeTrendMonthOption(record['qualified-monthly-trend'], defaults['qualified-monthly-trend']),
    'keyword-ranking-monthly': normalizeTrendMonthOption(record['keyword-ranking-monthly'], defaults['keyword-ranking-monthly']),
  };
};

const normalizeBreakdownMode = (value: unknown, fallback: BreakdownMode = 'cumulative'): BreakdownMode => (
  BREAKDOWN_OPTIONS.includes(String(value) as BreakdownMode)
    ? String(value) as BreakdownMode
    : fallback
);

const normalizeStoredBreakdowns = (storedSettings: unknown): Partial<Record<DashboardWidgetId, BreakdownMode>> => {
  if (!storedSettings || typeof storedSettings !== 'object') {
    return {};
  }

  const record = storedSettings as Record<string, unknown>;
  return Object.entries(record).reduce((acc, [widgetId, mode]) => {
    acc[widgetId as DashboardWidgetId] = normalizeBreakdownMode(
      mode,
      DEFAULT_BREAKDOWN_BY_WIDGET[widgetId as DashboardWidgetId] ?? 'cumulative'
    );
    return acc;
  }, {} as Partial<Record<DashboardWidgetId, BreakdownMode>>);
};

const normalizeWidgetOrder = (
  storedOrder: unknown,
  catalog: DashboardWidgetDefinition[] = DASHBOARD_WIDGETS
): DashboardWidgetId[] => {
  const catalogIds = catalog.map((widget) => widget.id);
  const catalogIdSet = new Set<DashboardWidgetId>(catalogIds);
  const activeCatalogIds = catalog
    .filter((widget) => widget.enabled !== false)
    .map((widget) => widget.id);
  const expandedOrder = Array.isArray(storedOrder)
    ? storedOrder.flatMap((entry) => {
      const widgetId = String(entry).trim() as DashboardWidgetId;
      const replacements = LEGACY_WIDGET_REPLACEMENTS[widgetId];
      return replacements && replacements.length > 0 ? replacements : [widgetId];
    })
    : [];
  const restoredOrder = Array.isArray(storedOrder)
    ? expandedOrder
      .filter((entry, index, rows) => catalogIdSet.has(entry) && rows.indexOf(entry) === index)
    : [];

  const missingIds = activeCatalogIds.filter((widgetId) => !restoredOrder.includes(widgetId));
  return [...restoredOrder, ...missingIds];
};

const normalizeStoredWidgetLayouts = (storedLayout: unknown): Record<DashboardWidgetId, DashboardWidgetLayout> => {
  const defaults = buildDefaultWidgetLayouts();
  if (!storedLayout || typeof storedLayout !== 'object') {
    return defaults;
  }

  const storedRecord = storedLayout as Partial<Record<DashboardWidgetId, Partial<DashboardWidgetLayout>>>;
  const effectiveStoredRecord: Partial<Record<DashboardWidgetId, Partial<DashboardWidgetLayout>>> = { ...storedRecord };
  const legacyGeoLayout = storedRecord['geo-visitors'];
  if (legacyGeoLayout) {
    if (!effectiveStoredRecord['geo-visitors-country']) {
      effectiveStoredRecord['geo-visitors-country'] = legacyGeoLayout;
    }
    if (!effectiveStoredRecord['geo-visitors-city']) {
      effectiveStoredRecord['geo-visitors-city'] = legacyGeoLayout;
    }
  }

  DASHBOARD_WIDGETS.forEach((widget) => {
    const storedWidget = effectiveStoredRecord[widget.id];
    if (!storedWidget) {
      return;
    }

    const storedViz = storedWidget.visualizationConfig;
    const normalizedViz = storedViz && typeof storedViz === 'object'
      ? {
        chartType: (['bar', 'line', 'pie', 'table'].includes(String((storedViz as VisualizationConfig).chartType))
          ? (storedViz as VisualizationConfig).chartType
          : (defaults[widget.id].visualizationConfig?.chartType ?? 'table')) as ChartType,
        horizontal: Boolean((storedViz as VisualizationConfig).horizontal),
      }
      : defaults[widget.id].visualizationConfig;

    defaults[widget.id] = {
      colSpan: clampWidgetColSpan(Number(storedWidget.colSpan ?? widget.defaultColSpan)),
      height: clampWidgetHeight(Number(storedWidget.height ?? widget.defaultHeight)),
      hidden: Boolean(widget.deprecated) || Boolean(storedWidget.hidden),
      visualizationConfig: normalizedViz,
    };
  });

  return defaults;
};

const normalizeWidgetRegistryItems = (items: unknown): DashboardWidgetDefinition[] => {
  if (!Array.isArray(items)) {
    return DASHBOARD_WIDGETS;
  }

  const catalogById = new Map<DashboardWidgetId, DashboardWidgetDefinition>(
    DASHBOARD_WIDGETS.map((widget) => [widget.id, widget])
  );

  const registryItems = items
    .map((item) => {
      const raw = item as WidgetRegistryApiItem;
      const widgetId = String(raw.widget_id || '').trim() as DashboardWidgetId;
      if (!widgetId || !catalogById.has(widgetId)) {
        return null;
      }
      const fallback = catalogById.get(widgetId)!;
      return {
        ...fallback,
        title: raw.title || fallback.title,
        description: raw.description || fallback.description,
        source: (raw.source as GallerySource) || fallback.source,
        dataProvider: raw.data_provider || fallback.dataProvider,
        defaultVisualization: (raw.default_visualization as ChartType) || fallback.defaultVisualization || 'table',
        allowedChartTypes: Array.isArray(raw.allowed_chart_types) && raw.allowed_chart_types.length > 0
          ? Array.from(new Set([
            ...raw.allowed_chart_types.filter((entry): entry is ChartType => ['bar', 'line', 'pie', 'table'].includes(entry)),
            ...(fallback.allowedChartTypes || []),
          ]))
          : (fallback.allowedChartTypes || ['table']),
        defaultColSpan: clampWidgetColSpan(Number(raw.default_size?.colSpan ?? fallback.defaultColSpan)),
        defaultHeight: clampWidgetHeight(Number(raw.default_size?.height ?? fallback.defaultHeight)),
        permissions: Array.isArray(raw.permissions) && raw.permissions.length > 0
          ? raw.permissions.filter((entry): entry is 'viewer' | 'user' | 'admin' | 'super_admin' => ['viewer', 'user', 'admin', 'super_admin'].includes(entry))
          : (fallback.permissions || ['viewer', 'user', 'admin', 'super_admin']),
        enabled: raw.enabled ?? fallback.enabled ?? true,
        deprecated: raw.deprecated ?? fallback.deprecated ?? false,
        replacementWidgetIds: Array.isArray(raw.replacement_widget_ids)
          ? raw.replacement_widget_ids.filter((entry): entry is DashboardWidgetId => catalogById.has(entry as DashboardWidgetId))
          : (fallback.replacementWidgetIds || []),
        displayOrder: Number(raw.display_order ?? fallback.displayOrder ?? 0),
        supportedBreakdowns: Array.isArray(raw.supported_breakdowns) && raw.supported_breakdowns.length > 0
          ? raw.supported_breakdowns.filter((entry): entry is BreakdownMode => BREAKDOWN_OPTIONS.includes(entry as BreakdownMode))
          : (fallback.supportedBreakdowns || []),
        defaultBreakdown: normalizeBreakdownMode(raw.default_breakdown, fallback.defaultBreakdown || 'cumulative'),
        breakdownMode: raw.breakdown_mode === 'server' || raw.breakdown_mode === 'client'
          ? raw.breakdown_mode
          : (fallback.breakdownMode || 'client'),
      } satisfies DashboardWidgetDefinition;
    })
    .filter((widget): widget is DashboardWidgetDefinition => widget !== null);

  const merged = new Map<DashboardWidgetId, DashboardWidgetDefinition>();
  DASHBOARD_WIDGETS.forEach((widget) => merged.set(widget.id, widget));
  registryItems.forEach((widget) => merged.set(widget.id, widget));

  return Array.from(merged.values()).sort((a, b) => {
    const orderDelta = (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
    if (orderDelta !== 0) {
      return orderDelta;
    }
    return a.title.localeCompare(b.title);
  });
};

const safeNumber = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

interface DashboardLinkedInDetailRow {
  campaignName: string;
  campaignType: string;
  creativeName?: string;
  impressions: number;
  clicks: number;
  spend: number;
  conversions: number;
}

const formatLinkedInCampaignType = (campaignType: string): string => {
  if (!campaignType || campaignType === 'Other') return 'Other Campaigns';
  if (campaignType === 'SponsoredInMail') return 'Sponsored Messaging';
  if (campaignType === 'SponsoredStatusUpdates_V2') return 'Sponsored Content';
  return campaignType
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
};

const aggregateLinkedInDetailRows = (
  metrics: Record<string, unknown>[],
  level: 'campaign' | 'creative'
): DashboardLinkedInDetailRow[] => {
  const grouped = new Map<string, DashboardLinkedInDetailRow>();

  metrics.forEach((metric) => {
    const campaignType = String(metric.campaign_type ?? metric.campaignType ?? 'Other');
    const campaignId = String(metric.campaign_id ?? metric.campaignId ?? '');
    const campaignName = String((metric.campaign_name ?? metric.campaignName ?? campaignId) || 'Unnamed Campaign');
    const creativeId = String(metric.creative_id ?? metric.creativeId ?? '');
    const creativeName = String((metric.creative_name ?? metric.creativeName ?? creativeId) || '');

    if (level === 'creative' && !creativeId && !creativeName) return;

    const entityKey = level === 'campaign'
      ? (campaignId || campaignName)
      : `${campaignId || campaignName}::${creativeId || creativeName}`;
    const key = `${campaignType}::${entityKey}`;
    const existing = grouped.get(key) ?? {
      campaignName,
      campaignType,
      ...(level === 'creative' ? { creativeName: creativeName || 'Unknown Creative' } : {}),
      impressions: 0,
      clicks: 0,
      spend: 0,
      conversions: 0,
    };

    existing.impressions += safeNumber(metric.impressions);
    existing.clicks += safeNumber(metric.clicks);
    existing.spend += safeNumber(metric.cost_in_local_currency ?? metric.costInLocalCurrency);
    existing.conversions += safeNumber(metric.conversions ?? metric.externalWebsiteConversions);
    grouped.set(key, existing);
  });

  return Array.from(grouped.values()).sort((a, b) => b.spend - a.spend);
};

const getGa4TimeseriesMetricValue = (row: Record<string, unknown>): number => (
  safeNumber(
    row.metric_value
    ?? row.value
    ?? row.users
    ?? row.activeUsers
    ?? row.pageViews
    ?? row.screenPageViews
  )
);

const normalizePercentValue = (value: unknown): number => {
  const numericValue = safeNumber(value);
  return numericValue > 1 ? numericValue : numericValue * 100;
};

interface PieSeriesRow {
  name: string;
  value: number;
}

const buildCompactPieSeries = (
  rows: PieSeriesRow[],
  maxSlices: number
): PieSeriesRow[] => {
  const cleaned = rows
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);

  if (cleaned.length <= maxSlices) {
    return cleaned;
  }

  const visibleCount = Math.max(1, maxSlices - 1);
  const visibleRows = cleaned.slice(0, visibleCount);
  const hiddenRows = cleaned.slice(visibleCount);
  const hiddenTotal = hiddenRows.reduce((sum, row) => sum + row.value, 0);

  if (hiddenTotal > 0) {
    visibleRows.push({
      name: `Others (${hiddenRows.length})`,
      value: hiddenTotal,
    });
  }

  return visibleRows;
};

const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDate = (value: string): Date => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return new Date();
  }
  return parsed;
};

const shiftDays = (date: Date, delta: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + delta);
  return next;
};

const formatMonthKey = (date: Date): string => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
);

const normalizeMonthKey = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^(\d{4})-(\d{2})/);
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return `${match[1]}-${match[2]}`;
};

const monthKeyFromDateValue = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value.trim()) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return formatMonthKey(parsed);
};

const toMonthLabel = (monthKey: string): string => {
  const normalized = normalizeMonthKey(monthKey);
  if (!normalized) return monthKey;
  const [year, month] = normalized.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const toMonthShortLabel = (monthKey: string): string => {
  const normalized = normalizeMonthKey(monthKey);
  if (!normalized) return monthKey;
  const [year, month] = normalized.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

interface MonthWindow {
  key: string;
  label: string;
  start: string;
  end: string;
}

const buildLastNMonthWindows = (count: number, anchorValue?: string | Date): MonthWindow[] => {
  const anchor = anchorValue instanceof Date
    ? new Date(anchorValue)
    : (anchorValue ? parseDate(anchorValue) : new Date());
  const now = Number.isNaN(anchor.getTime()) ? new Date() : anchor;
  const windows: MonthWindow[] = [];

  for (let offset = count - 1; offset >= 0; offset -= 1) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const end = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const monthKey = formatMonthKey(start);
    windows.push({
      key: monthKey,
      label: toMonthLabel(monthKey),
      start: formatDate(start),
      end: formatDate(end),
    });
  }

  return windows;
};

interface TimeBucketSeriesRow {
  name: string;
  bucketLabel: string;
  [seriesKey: string]: string | number;
}

interface SeriesBucketAggregation {
  chartRows: TimeBucketSeriesRow[];
  seriesKeys: string[];
  metricLabel: string;
}

const aggregateSeriesByBreakdown = (
  rows: Array<Record<string, unknown>>,
  breakdown: BreakdownMode,
  metricLabel: string,
  getDateValue: (row: Record<string, unknown>) => string | null,
  getSeriesKey: (row: Record<string, unknown>) => string,
  getMetricValue: (row: Record<string, unknown>) => number,
  maxSeries: number = 5
): SeriesBucketAggregation => {
  const totals = new Map<string, number>();
  const bucketSeries = new Map<string, Map<string, number>>();

  rows.forEach((row) => {
    const dateValue = getDateValue(row);
    if (!dateValue) return;
    const seriesKey = getSeriesKey(row);
    if (!seriesKey) return;
    const metricValue = getMetricValue(row);
    const bucketKey = getBreakdownBucketKey(dateValue, breakdown);
    totals.set(seriesKey, (totals.get(seriesKey) || 0) + metricValue);
    if (!bucketSeries.has(bucketKey)) {
      bucketSeries.set(bucketKey, new Map<string, number>());
    }
    const currentBucket = bucketSeries.get(bucketKey)!;
    currentBucket.set(seriesKey, (currentBucket.get(seriesKey) || 0) + metricValue);
  });

  const seriesKeys = Array.from(totals.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxSeries)
    .map(([seriesKey]) => seriesKey);

  const chartRows = Array.from(bucketSeries.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([bucketKey, seriesMap]) => {
      const row: TimeBucketSeriesRow = {
        name: formatBreakdownBucketLabel(bucketKey, breakdown),
        bucketLabel: formatBreakdownBucketLabel(bucketKey, breakdown),
      };
      seriesKeys.forEach((seriesKey) => {
        row[seriesKey] = seriesMap.get(seriesKey) || 0;
      });
      return row;
    });

  return {
    chartRows,
    seriesKeys,
    metricLabel,
  };
};

const aggregateScalarRowsByBreakdown = (
  rows: Array<Record<string, unknown>>,
  breakdown: BreakdownMode,
  getDateValue: (row: Record<string, unknown>) => string | null,
  metricBuilders: Record<string, (row: Record<string, unknown>) => number>
): TimeBucketSeriesRow[] => {
  const buckets = new Map<string, Record<string, number>>();

  rows.forEach((row) => {
    const dateValue = getDateValue(row);
    if (!dateValue) return;
    const bucketKey = getBreakdownBucketKey(dateValue, breakdown);
    if (!buckets.has(bucketKey)) {
      buckets.set(bucketKey, {});
    }
    const bucket = buckets.get(bucketKey)!;
    Object.entries(metricBuilders).forEach(([metricKey, builder]) => {
      bucket[metricKey] = (bucket[metricKey] || 0) + builder(row);
    });
  });

  return Array.from(buckets.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([bucketKey, metrics]) => ({
      name: formatBreakdownBucketLabel(bucketKey, breakdown),
      bucketLabel: formatBreakdownBucketLabel(bucketKey, breakdown),
      ...metrics,
    }));
};

const getInclusiveDays = (start: string, end: string): number => {
  const startDate = parseDate(start);
  const endDate = parseDate(end);
  const diff = endDate.getTime() - startDate.getTime();
  const rawDays = Math.floor(diff / (1000 * 60 * 60 * 24)) + 1;
  return rawDays > 0 ? rawDays : FALLBACK_RANGE_DAYS;
};

const buildPeriod = (start: string, end: string): string => `${start}_to_${end}`;

const getTrend = (current: number, previous: number): Trend => {
  if (previous === 0 && current === 0) {
    return { pct: 0, direction: 'flat' };
  }
  if (previous === 0 && current > 0) {
    return { pct: 100, direction: 'up' };
  }
  if (previous === 0 && current < 0) {
    return { pct: 100, direction: 'down' };
  }

  const delta = ((current - previous) / Math.abs(previous)) * 100;
  if (Math.abs(delta) < 0.01) {
    return { pct: 0, direction: 'flat' };
  }
  return { pct: Math.abs(delta), direction: delta > 0 ? 'up' : 'down' };
};

const divideOrNull = (numerator: number, denominator: number): number | null => {
  if (denominator <= 0) {
    return null;
  }
  return numerator / denominator;
};

const formatNumber = (value: number | null): string => {
  if (value === null || Number.isNaN(value)) {
    return 'N/A';
  }
  return numberFormatter.format(Math.round(value));
};

const formatCurrency = (value: number | null, currency: string): string => {
  if (value === null || Number.isNaN(value)) {
    return 'N/A';
  }
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatCurrencyTwoDecimals = (value: number, currency: string): string => (
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
);

const formatPercent = (value: number | null): string => {
  if (value === null || Number.isNaN(value)) {
    return 'N/A';
  }
  return `${value.toFixed(1)}%`;
};

const formatPercentTwoDecimals = (value: number): string => `${value.toFixed(2)}%`;

const formatTrendText = (trend: Trend): string => {
  if (trend.direction === 'flat') {
    return 'No change vs previous period';
  }
  return `${trend.direction === 'up' ? '+' : '-'}${trend.pct.toFixed(1)}% vs previous period`;
};

const statusLabel = (status: MetricStatus): string => {
  if (status === 'live') return 'Live';
  if (status === 'proxy') return 'Proxy';
  return 'Unavailable';
};

const getDiagnosticLabel = (key: string): string => ENDPOINT_FRIENDLY_LABELS[key] || key.replace(/-/g, ' ');

const toFriendlyDiagnosticMessage = (diagnostic: EndpointDiagnostic): string => {
  const label = diagnostic.userLabel || getDiagnosticLabel(diagnostic.key);
  if (diagnostic.status === 'unauthorized_or_mapping_missing') {
    return `${label} could not be loaded for this account mapping yet.`;
  }
  if (diagnostic.status === 'error') {
    return `${label} is temporarily unavailable.`;
  }
  if (diagnostic.status === 'empty') {
    return `${label} returned no data for this date range.`;
  }
  return `${label} loaded successfully.`;
};

const getDateRange = (preset: DatePreset, customStart: string, customEnd: string): RangeInfo => {
  const today = new Date();

  if (preset === 'custom') {
    const customStartDate = customStart ? parseDate(customStart) : shiftDays(today, -6);
    const customEndDate = customEnd ? parseDate(customEnd) : today;
    const safeStart = customStartDate <= customEndDate ? customStartDate : customEndDate;
    const safeEnd = customEndDate >= customStartDate ? customEndDate : customStartDate;
    const start = formatDate(safeStart);
    const end = formatDate(safeEnd);
    const days = getInclusiveDays(start, end);

    return {
      start,
      end,
      label: `${start} to ${end}`,
      days,
    };
  }

  const daysBack = preset === '30d' ? 29 : 6;
  const start = formatDate(shiftDays(today, -daysBack));
  const end = formatDate(today);
  const days = getInclusiveDays(start, end);

  return {
    start,
    end,
    label: preset === '30d' ? 'Last 30 days' : 'Last 1 week',
    days,
  };
};

const resolvePreviousRange = (currentRange: RangeInfo): RangeInfo => {
  const currentStart = parseDate(currentRange.start);
  const previousEndDate = shiftDays(currentStart, -1);
  const previousStartDate = shiftDays(previousEndDate, -(currentRange.days - 1));
  const start = formatDate(previousStartDate);
  const end = formatDate(previousEndDate);

  return {
    start,
    end,
    label: `${start} to ${end}`,
    days: currentRange.days,
  };
};

const spansMoreThanTwoMonths = (start: string, end: string): boolean => {
  const startDate = parseDate(start);
  const endDate = parseDate(end);
  const threshold = new Date(startDate);
  threshold.setMonth(threshold.getMonth() + 2);
  return endDate > threshold;
};

const getRangeAllowedBreakdowns = (preset: DatePreset, range: RangeInfo): BreakdownMode[] => {
  if (preset === '7d') {
    return ['cumulative', 'daily'];
  }
  if (preset === '30d') {
    return ['cumulative', 'daily', 'weekly'];
  }
  return spansMoreThanTwoMonths(range.start, range.end)
    ? ['cumulative', 'daily', 'weekly', 'monthly']
    : ['cumulative', 'daily', 'weekly'];
};

const startOfIsoWeek = (date: Date): Date => {
  const next = new Date(date);
  const day = next.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  next.setDate(next.getDate() + diff);
  next.setHours(0, 0, 0, 0);
  return next;
};

const getBreakdownBucketKey = (dateValue: string, breakdown: BreakdownMode): string => {
  const date = parseDate(dateValue);
  if (breakdown === 'monthly') {
    return formatMonthKey(date);
  }
  if (breakdown === 'weekly') {
    return formatDate(startOfIsoWeek(date));
  }
  return formatDate(date);
};

const formatBreakdownBucketLabel = (bucketKey: string, breakdown: BreakdownMode): string => {
  if (breakdown === 'monthly') {
    return toMonthShortLabel(bucketKey);
  }
  if (breakdown === 'weekly') {
    const weekStart = parseDate(bucketKey);
    const weekEnd = shiftDays(weekStart, 6);
    return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  }
  const parsed = parseDate(bucketKey);
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatCompactDateLabel = (rawDateValue: unknown): string => {
  const normalized = String(rawDateValue ?? '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return normalized || '-';
  }
  return formatBreakdownBucketLabel(getBreakdownBucketKey(normalized, 'daily'), 'daily');
};

const getDenseChartConfig = (
  bucketCount: number,
  seriesCount: number,
  isMaximized: boolean
) => {
  const densityScore = bucketCount * Math.max(seriesCount, 1);
  const tickTarget = isMaximized
    ? (bucketCount > 60 ? 12 : bucketCount > 36 ? 10 : bucketCount > 24 ? 8 : 6)
    : (bucketCount > 60 ? 5 : bucketCount > 36 ? 6 : bucketCount > 24 ? 5 : 4);
  const tickInterval = bucketCount <= tickTarget ? 0 : Math.max(Math.ceil(bucketCount / tickTarget) - 1, 0);
  const labelTarget = isMaximized
    ? (densityScore > 180 ? 4 : densityScore > 120 ? 6 : densityScore > 80 ? 8 : 10)
    : (densityScore > 180 ? 1 : densityScore > 120 ? 2 : densityScore > 80 ? 4 : 6);
  const axisFontSize = isMaximized ? 10 : 8;

  return {
    tickInterval,
    maxSeriesLabels: labelTarget,
    axisFontSize,
    lineOffsetY: isMaximized ? 10 : 13,
  };
};

const matchSourceToChannel = (rawSource: string): ChannelKey | null => {
  const source = rawSource.trim().toLowerCase();
  if (!source) {
    return null;
  }

  if (
    source.includes('linkedin') ||
    source.includes('li_fat_id') ||
    source.includes('inmail')
  ) {
    return 'linkedin';
  }

  if (
    source.includes('google') ||
    source.includes('gclid') ||
    source.includes('/cpc') ||
    source.includes('search')
  ) {
    return 'google';
  }

  if (
    source.includes('meta') ||
    source.includes('facebook') ||
    source.includes('instagram') ||
    source.includes('fb')
  ) {
    return 'meta';
  }

  if (
    source.includes('telemarketing') ||
    source.includes('tele-calling') ||
    source.includes('telesales') ||
    source.includes('cold call') ||
    source.includes('phone')
  ) {
    return 'telemarketing';
  }

  if (
    source.includes('email') ||
    source.includes('mailercloud') ||
    source.includes('newsletter')
  ) {
    return 'email';
  }

  return null;
};

type LeadFlowBucket = 'website_form_fill' | 'linkedin_form_fill' | 'email_replies' | 'telemarketing' | 'other';

const normalizeLeadFlowBucket = (lead: LeadRecord): LeadFlowBucket => {
  const firstTouch = (lead.first_touch_source || '').trim().toLowerCase();
  const sourceType = (lead.source?.type || '').trim().toLowerCase();

  if (
    firstTouch.includes('website form fill') ||
    firstTouch.includes('website contact form') ||
    firstTouch.includes('contact form fill')
  ) {
    return 'website_form_fill';
  }

  if (firstTouch.includes('linkedin form fill') || firstTouch.includes('linkedin lead form')) {
    return 'linkedin_form_fill';
  }

  if (
    firstTouch === 'email replies' ||
    firstTouch === 'email reply' ||
    firstTouch.includes('email replies')
  ) {
    return 'email_replies';
  }

  if (
    firstTouch.includes('telemarketing') ||
    firstTouch.includes('telemarketing follow-up') ||
    firstTouch.includes('tele-calling') ||
    firstTouch.includes('cold call')
  ) {
    return 'telemarketing';
  }

  if (sourceType === 'email') {
    return 'website_form_fill';
  }

  if (!firstTouch) {
    if (sourceType === 'linkedin') {
      return 'linkedin_form_fill';
    }
    if (sourceType === 'manual') {
      return 'telemarketing';
    }
  }

  return 'other';
};

const resolveLeadChannel = (lead: LeadRecord): ChannelKey => {
  const firstTouch = lead.first_touch_source || '';
  const firstTouchChannel = matchSourceToChannel(firstTouch);
  if (firstTouchChannel) {
    return firstTouchChannel;
  }

  const sourceType = (lead.source?.type || '').trim().toLowerCase();
  if (sourceType === 'linkedin') {
    return 'linkedin';
  }
  if (sourceType === 'email' || sourceType === 'mofu_qualified') {
    return 'email';
  }
  if (sourceType === 'manual') {
    return 'telemarketing';
  }

  const utmSource = lead.utm_data?.utm_source || '';
  const utmMedium = lead.utm_data?.utm_medium || '';
  const utmChannel = matchSourceToChannel(`${utmSource}/${utmMedium}`);
  if (utmChannel) {
    return utmChannel;
  }

  return 'other';
};

const buildLeadSnapshot = (leads: LeadRecord[]): LeadSnapshot => {
  const initialByChannel: Record<ChannelKey, ChannelFunnelStats> = {
    email: { leads: 0, mqls: 0, sqls: 0 },
    linkedin: { leads: 0, mqls: 0, sqls: 0 },
    google: { leads: 0, mqls: 0, sqls: 0 },
    telemarketing: { leads: 0, mqls: 0, sqls: 0 },
    meta: { leads: 0, mqls: 0, sqls: 0 },
    other: { leads: 0, mqls: 0, sqls: 0 },
  };

  let totalLeads = 0;
  let totalMqls = 0;
  let totalSqls = 0;

  leads.forEach((lead) => {
    const channel = resolveLeadChannel(lead);
    const stage = (lead.stage || '').trim().toUpperCase();
    const isMql = stage === 'BOFU';
    const isSql = lead.qualified === true;

    totalLeads += 1;
    if (isMql) totalMqls += 1;
    if (isSql) totalSqls += 1;

    initialByChannel[channel].leads += 1;
    if (isMql) initialByChannel[channel].mqls += 1;
    if (isSql) initialByChannel[channel].sqls += 1;
  });

  return {
    totalLeads,
    totalMqls,
    totalSqls,
    byChannel: initialByChannel,
  };
};

const fetchAllLeadsForRange = async (account: string, start: string, end: string): Promise<LeadRecord[]> => {
  const aggregatedLeads: LeadRecord[] = [];
  let page = 1;
  let pages = 1;

  while (page <= pages && page <= MAX_LEADS_PAGES) {
    const response = await apiService.getLeads({
      account,
      start_date: `${start}T00:00:00`,
      end_date: `${end}T23:59:59`,
      page,
      page_size: LEADS_PAGE_SIZE,
      sort_by: 'date',
      sort_order: 'desc',
    });

    const pageLeads: LeadRecord[] = Array.isArray(response?.leads) ? response.leads : [];
    aggregatedLeads.push(...pageLeads);

    pages = typeof response?.pages === 'number' && response.pages > 0 ? response.pages : 1;
    page += 1;
  }

  if (page > MAX_LEADS_PAGES) {
    logger.warn('[Dashboard] Lead fetch stopped at max pagination cap');
  }

  return aggregatedLeads;
};

const fetchAllLeadsByFilter = async (
  account: string,
  filters: {
    stage?: string;
    qualified?: boolean;
  }
): Promise<LeadRecord[]> => {
  const aggregatedLeads: LeadRecord[] = [];
  let page = 1;
  let pages = 1;

  while (page <= pages && page <= MAX_LEADS_PAGES) {
    const response = await apiService.getLeads({
      account,
      stage: filters.stage,
      qualified: filters.qualified,
      page,
      page_size: LEADS_PAGE_SIZE,
      sort_by: 'date',
      sort_order: 'desc',
    });

    const pageLeads: LeadRecord[] = Array.isArray(response?.leads) ? response.leads : [];
    aggregatedLeads.push(...pageLeads);

    pages = typeof response?.pages === 'number' && response.pages > 0 ? response.pages : 1;
    page += 1;
  }

  if (page > MAX_LEADS_PAGES) {
    logger.warn('[Dashboard] Lead fetch stopped at max pagination cap');
  }

  return aggregatedLeads;
};

const Dashboard: React.FC<{ isActive?: boolean }> = ({ isActive = true }) => {
  const {
    accounts,
    selectedAccount,
    setSelectedAccount,
    loading: rbacLoading,
    userProfile,
    hasPageAccess,
  } = useRBAC();
  const [draftFilters, setDraftFilters] = useState<DashboardFilterState>({
    account: null,
    datePreset: '7d',
    customStartDate: formatDate(shiftDays(new Date(), -6)),
    customEndDate: formatDate(new Date()),
  });
  const [appliedFilters, setAppliedFilters] = useState<DashboardFilterState | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [leadsPage, setLeadsPage] = useState(0);
  const [leadsRowsPerPage, setLeadsRowsPerPage] = useState<number>(5);
  const [tablePagination, setTablePagination] = useState<Record<string, { page: number; rowsPerPage: number }>>({});
  const [keywordTrendMatchType, setKeywordTrendMatchType] = useState<KeywordMatchTypeFilter>('ALL');
  const [keywordTrendRankBy, setKeywordTrendRankBy] = useState<KeywordRankingMetric>('impressions');
  const [trendMonthSettings, setTrendMonthSettings] = useState<Record<TrendWidgetId, TrendMonthOption>>(
    { ...DEFAULT_TREND_MONTHS }
  );
  const [breakdownSelections, setBreakdownSelections] = useState<Partial<Record<DashboardWidgetId, BreakdownMode>>>({});
  const [endpointDiagnostics, setEndpointDiagnostics] = useState<Record<string, EndpointDiagnostic>>({});
  const [widgetLayouts, setWidgetLayouts] = useState<Record<DashboardWidgetId, DashboardWidgetLayout>>(
    () => buildDefaultWidgetLayouts()
  );
  const [layoutStorageAccount, setLayoutStorageAccount] = useState<string | null>(null);
  const [maximizedWidgetId, setMaximizedWidgetId] = useState<DashboardWidgetId | null>(null);
  const [resizeSession, setResizeSession] = useState<ResizeSession | null>(null);
  const [showWidgetGallery, setShowWidgetGallery] = useState(false);
  const [galleryTab, setGalleryTab] = useState<GallerySource>('dashboard');
  const [gallerySearch, setGallerySearch] = useState('');
  const [gallerySelected, setGallerySelected] = useState<Set<DashboardWidgetId>>(new Set());
  const [crossPageData, setCrossPageData] = useState<Record<string, unknown>>({});
  const [crossPageLoading, setCrossPageLoading] = useState<Record<string, boolean>>({});
  const [widgetRegistry, setWidgetRegistry] = useState<DashboardWidgetDefinition[]>(DASHBOARD_WIDGETS);
  const [editingChartWidgetId, setEditingChartWidgetId] = useState<DashboardWidgetId | null>(null);
  const [widgetOrder, setWidgetOrder] = useState<DashboardWidgetId[]>(
    () => normalizeWidgetOrder(undefined, DASHBOARD_WIDGETS)
  );
  const [emailTopCampaignSortBy, setEmailTopCampaignSortBy] = useState<EmailTopCampaignSortBy>('delivered');
  const [emailTopCampaignFilter, setEmailTopCampaignFilter] = useState('');
  const [draggedWidgetId, setDraggedWidgetId] = useState<DashboardWidgetId | null>(null);
  const [dragOverWidgetId, setDragOverWidgetId] = useState<DashboardWidgetId | null>(null);
  const [insightsEdits, setInsightsEdits] = useState<{
    worked: string[];
    attention: string[];
    priorities: string[];
  } | null>(null);
  const [isEditingInsights, setIsEditingInsights] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState<{
    layouts: Record<DashboardWidgetId, DashboardWidgetLayout>;
    order: DashboardWidgetId[];
  } | null>(null);
  const [draftVizConfig, setDraftVizConfig] = useState<VisualizationConfig>({ chartType: 'bar', horizontal: false });
  const [refreshTick, setRefreshTick] = useState(0);
  const [syncState, setSyncState] = useState<DashboardSyncState>('idle');
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [layoutNotice, setLayoutNotice] = useState<{
    open: boolean;
    message: string;
    removedWidgetId: DashboardWidgetId | null;
  }>({ open: false, message: '', removedWidgetId: null });
  const loadIdRef = useRef(0);
  const layoutSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backgroundSyncRunRef = useRef(0);
  const hasAppliedInitialFiltersRef = useRef(false);
  const manualRefreshRequestedRef = useRef(false);
  const selectedClientRef = useRef<ClientRecord | null>(null);
  const visibleWidgetsRef = useRef<DashboardWidgetDefinition[]>([]);
  const loadCrossPageWidgetDataRef = useRef<((widgetId: DashboardWidgetId) => Promise<void>) | null>(null);

  const activeDatePreset = appliedFilters?.datePreset ?? draftFilters.datePreset;
  const activeCustomStartDate = appliedFilters?.customStartDate ?? draftFilters.customStartDate;
  const activeCustomEndDate = appliedFilters?.customEndDate ?? draftFilters.customEndDate;
  const activeRange = useMemo(
    () => getDateRange(
      activeDatePreset,
      activeCustomStartDate,
      activeCustomEndDate
    ),
    [activeDatePreset, activeCustomStartDate, activeCustomEndDate]
  );
  const canonicalSelectedAccount = useMemo(
    () => (appliedFilters?.account ? resolveCanonicalAccount(appliedFilters.account, accounts) : null),
    [accounts, appliedFilters]
  );
  const dashboardRole = userProfile?.role || 'viewer';
  const canViewDashboardPage = hasPageAccess('dashboard');
  const canCustomizeDashboard = ['super_admin', 'admin', 'user'].includes(dashboardRole);

  useEffect(() => {
    let isMounted = true;
    apiService.getDashboardWidgetRegistry(undefined, true)
      .then((response) => {
        if (!isMounted) {
          return;
        }
        const registryItems = normalizeWidgetRegistryItems((response as { items?: unknown })?.items);
        setWidgetRegistry(registryItems.length > 0 ? registryItems : DASHBOARD_WIDGETS);
      })
      .catch((registryError) => {
        logger.warn('[Dashboard] Widget registry unavailable, using fallback catalog', registryError);
        if (isMounted) {
          setWidgetRegistry(DASHBOARD_WIDGETS);
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setWidgetOrder((currentOrder) => normalizeWidgetOrder(currentOrder, widgetRegistry));
    setSavedSnapshot((currentSnapshot) => currentSnapshot
      ? {
        ...currentSnapshot,
        order: normalizeWidgetOrder(currentSnapshot.order, widgetRegistry),
      }
      : currentSnapshot);
  }, [widgetRegistry]);

  useEffect(() => {
    if (!isActive) {
      return;
    }
    if (accounts.length === 0) {
      if (rbacLoading) {
        return;
      }
      if (selectedAccount) {
        setSelectedAccount(null);
      }
      setDraftFilters((current) => ({ ...current, account: null }));
      setAppliedFilters(null);
      hasAppliedInitialFiltersRef.current = false;
      return;
    }

    const preferredAccount = draftFilters.account
      ? resolveCanonicalAccount(draftFilters.account, accounts)
      : resolveCanonicalAccount(selectedAccount, accounts);
    const nextAccount = preferredAccount && accounts.includes(preferredAccount)
      ? preferredAccount
      : accounts[0];
    if (selectedAccount !== nextAccount) {
      setSelectedAccount(nextAccount);
    }

    setDraftFilters((current) => (
      current.account === nextAccount
        ? current
        : { ...current, account: nextAccount }
    ));

    setAppliedFilters((current) => {
      if (current?.account) {
        const resolvedApplied = resolveCanonicalAccount(current.account, accounts);
        if (resolvedApplied && accounts.includes(resolvedApplied)) {
          return resolvedApplied === current.account
            ? current
            : { ...current, account: resolvedApplied };
        }
        return { ...current, account: nextAccount };
      }

      if (hasAppliedInitialFiltersRef.current) {
        return current;
      }

      hasAppliedInitialFiltersRef.current = true;
      return {
        account: nextAccount,
        datePreset: draftFilters.datePreset,
        customStartDate: draftFilters.customStartDate,
        customEndDate: draftFilters.customEndDate,
      };
    });
  }, [accounts, draftFilters.account, draftFilters.customEndDate, draftFilters.customStartDate, draftFilters.datePreset, isActive, rbacLoading, selectedAccount, setSelectedAccount]);

  useEffect(() => {
    if (!appliedFilters?.account) {
      setWidgetLayouts(buildDefaultWidgetLayouts());
      setWidgetOrder(normalizeWidgetOrder(undefined, widgetRegistry));
      setTrendMonthSettings({ ...DEFAULT_TREND_MONTHS });
      setBreakdownSelections({});
      setMaximizedWidgetId(null);
      setLayoutStorageAccount(null);
      return;
    }
    const effectiveAccount = resolveCanonicalAccount(appliedFilters.account, accounts);

    const loadLayout = async () => {
      try {
        const serverLayout = await apiService.getDashboardLayout(effectiveAccount);
        if (serverLayout?.layouts) {
          setWidgetLayouts(normalizeStoredWidgetLayouts(serverLayout.layouts));
          setTrendMonthSettings(normalizeStoredTrendMonths(serverLayout.settings?.trendMonths));
          setBreakdownSelections(normalizeStoredBreakdowns(serverLayout.settings?.breakdownSelections));
          setWidgetOrder(normalizeWidgetOrder(serverLayout.settings?.widgetOrder, widgetRegistry));
          setMaximizedWidgetId(null);
          setLayoutStorageAccount(effectiveAccount);
          return;
        }
      } catch {
        // 404 = no saved layout yet; any other error = fall back to localStorage
      }

      try {
        const storedLayout = window.localStorage.getItem(getDashboardLayoutStorageKey(effectiveAccount));
        if (!storedLayout) {
          setWidgetLayouts(buildDefaultWidgetLayouts());
          setWidgetOrder(normalizeWidgetOrder(undefined, widgetRegistry));
          setTrendMonthSettings({ ...DEFAULT_TREND_MONTHS });
        } else {
          const parsedStored = JSON.parse(storedLayout) as {
            layouts?: unknown;
            trendMonths?: unknown;
            breakdownSelections?: unknown;
            widgetOrder?: unknown;
          } | unknown;
          const hasContainerShape = Boolean(
            parsedStored &&
            typeof parsedStored === 'object' &&
            (
              'layouts' in (parsedStored as Record<string, unknown>)
              || 'trendMonths' in (parsedStored as Record<string, unknown>)
              || 'breakdownSelections' in (parsedStored as Record<string, unknown>)
              || 'widgetOrder' in (parsedStored as Record<string, unknown>)
            )
          );
          const legacyLayouts = hasContainerShape
            ? (parsedStored as { layouts?: unknown }).layouts
            : parsedStored;
          const savedTrendMonths = hasContainerShape
            ? (parsedStored as { trendMonths?: unknown }).trendMonths
            : undefined;
          const savedBreakdowns = hasContainerShape
            ? (parsedStored as { breakdownSelections?: unknown }).breakdownSelections
            : undefined;
          const savedWidgetOrder = hasContainerShape
            ? (parsedStored as { widgetOrder?: unknown }).widgetOrder
            : undefined;

          setWidgetLayouts(normalizeStoredWidgetLayouts(legacyLayouts));
          setTrendMonthSettings(normalizeStoredTrendMonths(savedTrendMonths));
          setBreakdownSelections(normalizeStoredBreakdowns(savedBreakdowns));
          setWidgetOrder(normalizeWidgetOrder(savedWidgetOrder, widgetRegistry));
        }
      } catch (storageError) {
        logger.warn('[Dashboard] Unable to restore saved widget layout', storageError);
        setWidgetLayouts(buildDefaultWidgetLayouts());
        setWidgetOrder(normalizeWidgetOrder(undefined, widgetRegistry));
        setTrendMonthSettings({ ...DEFAULT_TREND_MONTHS });
        setBreakdownSelections({});
      }
      setMaximizedWidgetId(null);
      setLayoutStorageAccount(effectiveAccount);
    };

    loadLayout();
  }, [accounts, appliedFilters?.account, widgetRegistry]);

  useEffect(() => {
    const effectiveAccount = canonicalSelectedAccount || appliedFilters?.account;
    if (!effectiveAccount || layoutStorageAccount !== effectiveAccount || resizeSession) {
      return;
    }

    const settingsPayload = {
      trendMonths: trendMonthSettings,
      breakdownSelections,
      widgetOrder,
    };

    try {
      window.localStorage.setItem(
        getDashboardLayoutStorageKey(effectiveAccount),
        JSON.stringify({ layouts: widgetLayouts, ...settingsPayload })
      );
    } catch (storageError) {
      logger.warn('[Dashboard] Unable to persist widget layout to localStorage', storageError);
    }

    if (layoutSaveTimerRef.current) {
      clearTimeout(layoutSaveTimerRef.current);
    }
    layoutSaveTimerRef.current = setTimeout(() => {
      apiService.saveDashboardLayout(effectiveAccount, widgetLayouts, settingsPayload).catch((err) => {
        logger.warn('[Dashboard] Unable to persist widget layout to server', err);
      });
    }, 1000);
  }, [appliedFilters?.account, breakdownSelections, canonicalSelectedAccount, layoutStorageAccount, resizeSession, widgetLayouts, trendMonthSettings, widgetOrder]);

  // Helper: get widget definition by id
  const getWidgetDefinition = useCallback((widgetId: DashboardWidgetId) => (
    widgetRegistry.find((widget) => widget.id === widgetId)
  ), [widgetRegistry]);

  // Returns available breakdown modes for a widget based on supported modes and current range/preset
  const getAvailableBreakdowns = useCallback((widgetId: DashboardWidgetId): BreakdownMode[] => {
    if (!BREAKDOWN_SELECTOR_WIDGETS.has(widgetId) || WEEKLY_ONLY_WIDGETS.has(widgetId)) {
      return [];
    }
    const widget = getWidgetDefinition(widgetId);
    const supported = widget?.supportedBreakdowns && widget.supportedBreakdowns.length > 0
      ? widget.supportedBreakdowns
      : (BREAKDOWN_SELECTOR_WIDGETS.has(widgetId) ? ['cumulative', 'daily', 'weekly', 'monthly'] : ['cumulative']);
    const rangeAllowed = getRangeAllowedBreakdowns(activeDatePreset, activeRange);
    return supported.filter((mode) => rangeAllowed.includes(mode));
  }, [activeDatePreset, activeRange, getWidgetDefinition]);

  const getSelectedBreakdown = useCallback((widgetId: DashboardWidgetId): BreakdownMode => {
    const available = getAvailableBreakdowns(widgetId);
    if (available.length === 0) {
      return 'cumulative';
    }
    const saved = breakdownSelections[widgetId];
    return saved && available.includes(saved) ? saved : 'cumulative';
  }, [breakdownSelections, getAvailableBreakdowns]);

  const setWidgetBreakdown = (widgetId: DashboardWidgetId, breakdown: BreakdownMode) => {
    setBreakdownSelections((prev) => ({
      ...prev,
      [widgetId]: breakdown,
    }));
  };

  useEffect(() => {
    setBreakdownSelections((current) => {
      let changed = false;
      const next: Partial<Record<DashboardWidgetId, BreakdownMode>> = { ...current };
      widgetRegistry.forEach((widget) => {
        const available = getAvailableBreakdowns(widget.id);
        if (available.length === 0) {
          if (next[widget.id]) {
            delete next[widget.id];
            changed = true;
          }
          return;
        }
        const currentValue = next[widget.id];
        if (!currentValue || !available.includes(currentValue)) {
          next[widget.id] = 'cumulative';
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [activeDatePreset, activeRange.end, activeRange.start, getAvailableBreakdowns, widgetRegistry]);

  useEffect(() => {
    if (!resizeSession) {
      return undefined;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const deltaX = event.clientX - resizeSession.startX;
      const deltaY = event.clientY - resizeSession.startY;

      setWidgetLayouts((currentLayouts) => {
        const currentWidgetLayout = currentLayouts[resizeSession.id];
        if (!currentWidgetLayout) {
          return currentLayouts;
        }

        const horizontalDelta = Math.round(deltaX / DASHBOARD_COLUMN_PIXEL_STEP);
        const verticalDelta = Math.round(deltaY);
        let nextColSpan = resizeSession.startColSpan;
        let nextHeight = resizeSession.startHeight;

        if (resizeSession.direction === 'e') {
          nextColSpan = resizeSession.startColSpan + horizontalDelta;
        } else if (resizeSession.direction === 'w') {
          nextColSpan = resizeSession.startColSpan - horizontalDelta;
        } else if (resizeSession.direction === 's') {
          nextHeight = resizeSession.startHeight + verticalDelta;
        } else if (resizeSession.direction === 'n') {
          nextHeight = resizeSession.startHeight - verticalDelta;
        }

        return {
          ...currentLayouts,
          [resizeSession.id]: {
            ...currentWidgetLayout,
            colSpan: clampWidgetColSpan(nextColSpan),
            height: clampWidgetHeight(nextHeight),
          },
        };
      });
    };

    const handlePointerUp = () => setResizeSession(null);

    document.body.classList.add('dashboard-widget-resizing');
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      document.body.classList.remove('dashboard-widget-resizing');
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [resizeSession]);

  const visibleWidgets = useMemo(
    () => widgetOrder
      .filter((id) => !widgetLayouts[id]?.hidden)
      .map((id) => widgetRegistry.find((w) => w.id === id))
      .filter((w): w is DashboardWidgetDefinition => w !== undefined && w.enabled !== false),
    [widgetOrder, widgetLayouts, widgetRegistry]
  );
  visibleWidgetsRef.current = visibleWidgets;

  const loadDashboardData = useCallback(
    async (manualRefresh: boolean) => {
      if (!appliedFilters?.account || !canonicalSelectedAccount) {
        return;
      }
      const effectiveAccount = canonicalSelectedAccount;

      loadIdRef.current += 1;
      const myLoadId = loadIdRef.current;
      logger.warn(`[Dashboard][RaceGuard] Load #${myLoadId} started for account="${appliedFilters.account}" (canonical="${effectiveAccount}")`);

      const previousRange = resolvePreviousRange(activeRange);
      const currentPeriod = buildPeriod(activeRange.start, activeRange.end);
      const previousPeriod = buildPeriod(previousRange.start, previousRange.end);
      const widgetsForLoad = visibleWidgetsRef.current;
      const visibleWidgetIds = new Set(widgetsForLoad.map((widget) => widget.id));
      const hasVisibleWidget = (...widgetIds: DashboardWidgetId[]) => widgetIds.some((widgetId) => visibleWidgetIds.has(widgetId));
      const hasVisibleSource = (...sources: DashboardWidgetDefinition['source'][]) => widgetsForLoad.some((widget) => sources.includes(widget.source));
      const hasVisibleSharedGoogleAdsWidget = SHARED_GOOGLE_ADS_WIDGET_IDS.some((widgetId) => visibleWidgetIds.has(widgetId));

      const needsLeadData = hasVisibleSource('leads')
        || hasVisibleWidget('kpi-summary', 'cost-summary', 'channel-funnel', 'website-lead-flow', 'leads-table');
      const needsClientConfig = hasVisibleWidget('kpi-summary', 'cost-summary', 'channel-funnel', 'website-lead-flow', 'ads-performance');
      const needsGA4Summary = hasVisibleWidget(
        'website-lead-flow',
        'ga4-users-total-stat',
        'ga4-new-users-stat',
        'ga4-sessions-total-stat',
        'ga4-pageviews-total-stat',
        'ga4-pageviews-per-user-stat',
        'ga4-avg-time-per-page-stat',
        'ga4-percent-users-stat',
      );
      const needsGA4Country = hasVisibleWidget('geo-visitors-country', 'website-lead-flow', 'ga4-regions');
      const needsGA4City = hasVisibleWidget('geo-visitors-city', 'website-lead-flow', 'ga4-regions');
      const needsGA4SourceMedium = false;
      const needsGA4ByPage = hasVisibleWidget('pageviews', 'ga4-pageviews-by-path', 'ga4-pages-screens');
      const needsGA4LandingPageCountryViews = hasVisibleWidget('ga4-fintrans-landing-page-views-by-country');
      const needsGA4ByDevice = hasVisibleWidget('ga4-traffic-by-device');
      const needsGA4WeeklyUserAcquisition = hasVisibleWidget('ga4-total-vs-organic-weekly');
      const needsPaidSearchCampaigns = hasVisibleWidget(
        'ga4-paid-search-campaign-performance',
        'ga4-paid-search-users-by-campaign'
      );
      const needsGA4Weekly = hasVisibleWidget('ga4-seo-weekly-stats');
      const needsGA4FirstUserChannel = hasVisibleWidget('traffic-source', 'website-lead-flow');

      const needsSearchConsoleSummary = hasVisibleWidget(
        'gsc-total-clicks-stat',
        'gsc-total-impressions-stat',
        'gsc-average-ctr-stat',
        'gsc-average-position-stat',
      );
      const needsSearchConsoleQuery = hasVisibleWidget('keywords', 'gsc-queries-table');
      const needsSearchConsolePage = hasVisibleWidget('gsc-pages-table');
      const needsSearchConsoleCountry = hasVisibleWidget('gsc-countries-table');
      const needsSearchConsoleDevice = hasVisibleWidget('gsc-devices-table');
      const needsSearchConsoleWeekly = hasVisibleWidget('gsc-weekly-performance-trend', 'gsc-weekly-table');

      const needsEmailPerformance = hasVisibleSource('email') || hasVisibleWidget('email-followers');
      const needsLinkedInAggregated = hasVisibleWidget('linkedin-campaigns', 'ads-performance');
      const needsLinkedInDetails = hasVisibleWidget('linkedin-campaign-details', 'linkedin-creative-details');
      const needsLinkedInOrganic = hasVisibleWidget(
        'social-posts',
        'linkedin-organic-posts',
        'linkedin-organic-total-posts-stat',
        'linkedin-organic-impressions-stat',
        'linkedin-organic-clicks-stat',
        'linkedin-organic-engagements-stat',
        'linkedin-organic-reactions-stat',
        'linkedin-organic-comments-stat',
        'linkedin-organic-reposts-stat',
        'linkedin-organic-avg-ctr-stat',
        'linkedin-organic-avg-engagement-rate-stat',
        'linkedin-organic-impressions-vs-clicks',
        'linkedin-organic-ctr-engagement',
      );
      const needsLinkedInLeadForms = hasVisibleWidget('linkedin-lead-forms', 'linkedin-active-lead-forms-stat');
      const needsLinkedInLeadResponses = hasVisibleWidget('linkedin-total-leads-captured-stat');
      const needsLinkedInAccounts = hasVisibleWidget('email-followers', 'linkedin-follower-stats', 'linkedin-organic-followers-stat');

      const needsGoogleAdsSummary = hasVisibleSource('google-ads') || hasVisibleSharedGoogleAdsWidget;
      const needsGoogleAdsDevice = hasVisibleWidget('gads-performance-by-device');

      logger.debug('[Dashboard] Google Ads request gating', {
        account: effectiveAccount,
        explicitSourceVisible: hasVisibleSource('google-ads'),
        sharedSummaryVisible: hasVisibleSharedGoogleAdsWidget,
        deviceWidgetVisible: needsGoogleAdsDevice,
        needsGoogleAdsSummary,
      });

      const requestDefinitions: Array<{
        key: string | null;
        enabled: boolean;
        request: () => Promise<unknown>;
      }> = [
        { key: 'leads-current', enabled: needsLeadData, request: () => fetchAllLeadsForRange(effectiveAccount, activeRange.start, activeRange.end) },
        { key: 'leads-previous', enabled: needsLeadData, request: () => fetchAllLeadsForRange(effectiveAccount, previousRange.start, previousRange.end) },
        { key: null, enabled: needsClientConfig, request: () => apiService.getClient(effectiveAccount) },
        { key: 'ga4-summary', enabled: needsGA4Summary, request: () => apiService.getGA4SummaryTotals(effectiveAccount, activeRange.start, activeRange.end) },
        { key: 'ga4-country', enabled: needsGA4Country, request: () => apiService.getGA4MetricsByCountry(effectiveAccount, activeRange.start, activeRange.end) },
        { key: 'ga4-city', enabled: needsGA4City, request: () => apiService.getGA4MetricsByCity(effectiveAccount, activeRange.start, activeRange.end) },
        { key: 'ga4-source-medium', enabled: needsGA4SourceMedium, request: () => apiService.getGA4MetricsBySourceMedium(effectiveAccount, activeRange.start, activeRange.end) },
        { key: 'ga4-page', enabled: needsGA4ByPage, request: () => apiService.getGA4MetricsByPage(effectiveAccount, activeRange.start, activeRange.end) },
        { key: 'ga4-landing-page-country-views', enabled: needsGA4LandingPageCountryViews, request: () => apiService.getGA4LandingPageViewsByCountry(effectiveAccount, activeRange.start, activeRange.end) },
        { key: 'ga4-device', enabled: needsGA4ByDevice, request: () => apiService.getGA4MetricsByDevice(effectiveAccount, activeRange.start, activeRange.end) },
        { key: 'ga4-weekly-user-acquisition', enabled: needsGA4WeeklyUserAcquisition, request: () => apiService.getGA4WeeklyUserAcquisition(effectiveAccount, activeRange.end) },
        { key: 'ga4-paid-search-campaigns', enabled: needsPaidSearchCampaigns, request: () => apiService.getPaidSearchCampaigns(effectiveAccount, activeRange.start, activeRange.end) },
        { key: 'ga4-weekly', enabled: needsGA4Weekly, request: () => apiService.getGA4WeeklyStats(effectiveAccount, activeRange.start, activeRange.end) },
        { key: 'ga4-first-channel', enabled: needsGA4FirstUserChannel, request: () => apiService.getGA4MetricsByFirstUserChannel(effectiveAccount, activeRange.start, activeRange.end) },
        { key: 'search-console-summary', enabled: needsSearchConsoleSummary, request: () => apiService.getSearchConsoleSummary(effectiveAccount, activeRange.start, activeRange.end) },
        { key: 'search-console-query', enabled: needsSearchConsoleQuery, request: () => apiService.getSearchConsoleByQuery(effectiveAccount, activeRange.start, activeRange.end, 25) },
        { key: 'search-console-page', enabled: needsSearchConsolePage, request: () => apiService.getSearchConsoleByPage(effectiveAccount, activeRange.start, activeRange.end, 50) },
        { key: 'search-console-country', enabled: needsSearchConsoleCountry, request: () => apiService.getSearchConsoleByCountry(effectiveAccount, activeRange.start, activeRange.end, 50) },
        { key: 'search-console-device', enabled: needsSearchConsoleDevice, request: () => apiService.getSearchConsoleByDevice(effectiveAccount, activeRange.start, activeRange.end) },
        { key: 'search-console-weekly', enabled: needsSearchConsoleWeekly, request: () => apiService.getSearchConsoleWeekly(effectiveAccount, activeRange.start, activeRange.end) },
        { key: 'email-current', enabled: needsEmailPerformance, request: () => apiService.getEmailMetricsPerformance(effectiveAccount, currentPeriod) },
        { key: 'email-previous', enabled: needsEmailPerformance, request: () => apiService.getEmailMetricsPerformance(effectiveAccount, previousPeriod) },
        { key: 'linkedin-current', enabled: needsLinkedInAggregated, request: () => apiService.getStoredLinkedInCampaignMetricsAggregated({ account: effectiveAccount, start_date: activeRange.start, end_date: activeRange.end }, manualRefresh) },
        { key: 'linkedin-previous', enabled: needsLinkedInAggregated, request: () => apiService.getStoredLinkedInCampaignMetricsAggregated({ account: effectiveAccount, start_date: previousRange.start, end_date: previousRange.end }, manualRefresh) },
        { key: 'linkedin-campaign-details', enabled: needsLinkedInDetails, request: () => apiService.getStoredLinkedInCampaignMetricsAggregated({ account: effectiveAccount, start_date: activeRange.start, end_date: activeRange.end }, manualRefresh) },
        // Pull a wider stored-organic window for dashboard widgets so the
        // dashboard's top-post charts and tables stay aligned with the
        // LinkedIn Campaigns page instead of rendering from a tiny first page.
        { key: 'linkedin-organic', enabled: needsLinkedInOrganic, request: () => apiService.getStoredLinkedInOrganicPosts({ account: effectiveAccount, start_date: activeRange.start, end_date: activeRange.end, page: 1, page_size: 100, forceRefresh: manualRefresh }) },
        { key: 'linkedin-lead-forms', enabled: needsLinkedInLeadForms, request: () => apiService.getStoredLinkedInLeadForms({ account: effectiveAccount, start_date: activeRange.start, end_date: activeRange.end, forceRefresh: manualRefresh }) },
        { key: 'linkedin-lead-responses', enabled: needsLinkedInLeadResponses, request: () => apiService.getStoredLinkedInLeadResponses({ account: effectiveAccount, start_date: activeRange.start, end_date: activeRange.end, forceRefresh: manualRefresh }) },
        { key: 'google-ads-current', enabled: needsGoogleAdsSummary, request: () => apiService.getGoogleAdsMetrics(effectiveAccount, activeRange.start, activeRange.end) },
        { key: 'google-ads-previous', enabled: needsGoogleAdsSummary, request: () => apiService.getGoogleAdsMetrics(effectiveAccount, previousRange.start, previousRange.end) },
        { key: 'google-ads-device', enabled: needsGoogleAdsDevice, request: () => apiService.getGoogleAdsDeviceBreakdown(effectiveAccount, activeRange.start, activeRange.end) },
        { key: 'linkedin-accounts', enabled: needsLinkedInAccounts, request: () => apiService.getLinkedInAccounts() },
      ];

      const publishCompletedResponse = (key: string | null, value: unknown): boolean => {
        if (!key || myLoadId !== loadIdRef.current) {
          return false;
        }

        const response = value as Record<string, unknown>;
        const progressiveData: Record<string, unknown> = {};
        switch (key) {
          case 'ga4-summary': progressiveData['ga4-summary-raw'] = value; break;
          case 'ga4-country': progressiveData['ga4-country-raw'] = value; break;
          case 'ga4-city': progressiveData['ga4-city-raw'] = value; break;
          case 'ga4-page': progressiveData['ga4-page-raw'] = value; break;
          case 'ga4-landing-page-country-views': progressiveData['ga4-fintrans-landing-page-views-by-country'] = value; break;
          case 'ga4-device': progressiveData['ga4-device-raw'] = value; break;
          case 'ga4-weekly-user-acquisition': progressiveData['ga4-total-vs-organic-weekly'] = value; break;
          case 'ga4-paid-search-campaigns':
            progressiveData['ga4-paid-search-campaign-performance'] = value;
            progressiveData['ga4-paid-search-users-by-campaign'] = value;
            break;
          case 'ga4-weekly': progressiveData['ga4-weekly-raw'] = value; break;
          case 'search-console-summary': progressiveData['gsc-summary-raw'] = value; break;
          case 'search-console-query': progressiveData['gsc-query-raw'] = value; break;
          case 'search-console-page': progressiveData['gsc-page-raw'] = value; break;
          case 'search-console-country': progressiveData['gsc-country-raw'] = value; break;
          case 'search-console-device': progressiveData['gsc-device-raw'] = value; break;
          case 'search-console-weekly': progressiveData['gsc-weekly-raw'] = value; break;
          case 'email-current':
            progressiveData['email-campaigns'] = value;
            progressiveData['email-current-raw'] = value;
            break;
          case 'linkedin-current': {
            const metrics = Array.isArray(response?.campaign_metrics) ? response.campaign_metrics : [];
            progressiveData['linkedin-campaigns'] = metrics;
            progressiveData['linkedin-monthly-spend'] = metrics;
            progressiveData['linkedin-monthly-spend-trend'] = metrics;
            break;
          }
          case 'linkedin-campaign-details':
            progressiveData['linkedin-campaign-details-raw'] = value;
            progressiveData['linkedin-creative-details-raw'] = value;
            break;
          case 'linkedin-organic': progressiveData['linkedin-organic-posts'] = value; break;
          case 'linkedin-lead-forms': progressiveData['linkedin-lead-forms'] = value; break;
          case 'linkedin-lead-responses': progressiveData['linkedin-lead-responses-raw'] = value; break;
          case 'google-ads-current': {
            const metrics = Array.isArray(response?.metrics) ? response.metrics as GoogleAdsMetric[] : [];
            const campaignSummary = Array.isArray(response?.campaign_summary)
              ? response.campaign_summary as GoogleAdsCampaignSummary[]
              : buildGoogleAdsCampaignSummaryFallback(metrics);
            progressiveData['google-ads-metrics-raw'] = value;
            progressiveData['gads-campaign-table'] = metrics;
            progressiveData['gads-campaign-performance-summary'] = campaignSummary;
            break;
          }
          case 'google-ads-device': progressiveData['google-ads-device-raw'] = value; break;
          default: break;
        }

        if (Object.keys(progressiveData).length > 0) {
          setCrossPageData((current) => ({ ...current, ...progressiveData }));
          return true;
        }
        return false;
      };

      try {
        if (manualRefresh) {
          setRefreshing(true);
          setSyncState('loading_saved');
          setSyncMessage('Showing saved data while the latest updates are being checked.');
        } else {
          setLoading(true);
        }

        setError(null);

        setEndpointDiagnostics(Object.fromEntries(
          requestDefinitions
            .filter((definition) => definition.enabled && definition.key)
            .map((definition) => {
              const key = definition.key as string;
              return [key, {
                key,
                status: 'loading' as EndpointStatus,
                message: `Loading ${getDiagnosticLabel(key)} for ${effectiveAccount}.`,
                userLabel: getDiagnosticLabel(key),
              }];
            })
        ));

        // Render the dashboard frame immediately. Source-specific responses are
        // published as they resolve, while cross-source KPIs are finalized below.
        setDashboardData((current) => (
          current?.clientName === effectiveAccount
            ? current
            : createEmptyDashboardData(effectiveAccount)
        ));

        const settled = await Promise.allSettled(
          requestDefinitions.map((definition) => (
            definition.enabled
              ? definition.request().then((value) => {
                const wasPublished = publishCompletedResponse(definition.key, value);
                if (definition.key && wasPublished && myLoadId === loadIdRef.current) {
                  const isEmptyArray = Array.isArray(value) && value.length === 0;
                  const isEmptyObject = value !== null
                    && typeof value === 'object'
                    && !Array.isArray(value)
                    && Object.keys(value as Record<string, unknown>).length === 0;
                  const key = definition.key;
                  // Keep the loader through the render that publishes the response.
                  // This prevents a transient empty state between loading and content.
                  window.requestAnimationFrame(() => {
                    if (myLoadId !== loadIdRef.current) {
                      return;
                    }
                    setEndpointDiagnostics((current) => ({
                      ...current,
                      [key]: {
                        key,
                        status: (isEmptyArray || isEmptyObject) ? 'empty' : 'loaded',
                        message: (isEmptyArray || isEmptyObject)
                          ? `No data returned for ${effectiveAccount}.`
                          : `Loaded for ${effectiveAccount}.`,
                        userLabel: getDiagnosticLabel(key),
                      },
                    }));
                  });
                }
                return value;
              }).catch((reason) => {
                if (definition.key && myLoadId === loadIdRef.current) {
                  const key = definition.key;
                  const requestError = reason as { response?: { status?: number; data?: { detail?: string } }; message?: string };
                  const errorMessage = String(requestError?.response?.data?.detail || requestError?.message || 'Request failed');
                  const unauthorized = requestError?.response?.status === 401
                    || requestError?.response?.status === 403
                    || errorMessage.toLowerCase().includes('permission')
                    || errorMessage.toLowerCase().includes('access denied')
                    || errorMessage.toLowerCase().includes('mapping');
                  setEndpointDiagnostics((current) => ({
                    ...current,
                    [key]: {
                      key,
                      status: unauthorized ? 'unauthorized_or_mapping_missing' : 'error',
                      message: errorMessage,
                      userLabel: getDiagnosticLabel(key),
                    },
                  }));
                }
                throw reason;
              })
              : Promise.resolve(null)
          ))
        );

        const settledByKey = new Map<string, PromiseSettledResult<unknown>>();
        requestDefinitions.forEach((definition, index) => {
          if (definition.key) {
            settledByKey.set(definition.key, settled[index]);
          }
        });

        const getSettledValue = <T,>(key: string, fallback: T): T => {
          const result = settledByKey.get(key);
          return result?.status === 'fulfilled' && result.value
            ? result.value as T
            : fallback;
        };

        const diagnosticSources = requestDefinitions.map((definition) => (
          definition.enabled ? definition.key : null
        ));
        const nextDiagnostics: Record<string, EndpointDiagnostic> = {};
        settled.forEach((result, index) => {
          const key = diagnosticSources[index];
          if (!key) {
            return;
          }
          if (result.status === 'fulfilled') {
            const value = result.value as unknown;
            const isEmptyArray = Array.isArray(value) && value.length === 0;
            const isEmptyObject = value !== null && typeof value === 'object' && !Array.isArray(value) && Object.keys(value as Record<string, unknown>).length === 0;
            const userLabel = getDiagnosticLabel(key);
            nextDiagnostics[key] = {
              key,
              status: (isEmptyArray || isEmptyObject) ? 'empty' : 'loaded',
              message: (isEmptyArray || isEmptyObject)
                ? `No data returned for ${effectiveAccount}.`
                : `Loaded for ${effectiveAccount}.`,
              userLabel,
              userMessage: (isEmptyArray || isEmptyObject)
                ? `${userLabel} returned no data for the selected range.`
                : `${userLabel} loaded.`,
            };
            return;
          }

          const reason = result.reason as { response?: { status?: number; data?: { detail?: string } }; message?: string } | undefined;
          const errorMessage = String(reason?.response?.data?.detail || reason?.message || 'Request failed');
          const unauthorized = reason?.response?.status === 401
            || reason?.response?.status === 403
            || errorMessage.toLowerCase().includes('permission')
            || errorMessage.toLowerCase().includes('access denied')
            || errorMessage.toLowerCase().includes('mapping');

          nextDiagnostics[key] = {
            key,
            status: unauthorized ? 'unauthorized_or_mapping_missing' : 'error',
            message: errorMessage,
            userLabel: getDiagnosticLabel(key),
          };
        });
        const currentLeads = getSettledValue<Record<string, unknown>[]>('leads-current', []);
        const previousLeads = getSettledValue<Record<string, unknown>[]>('leads-previous', []);
        const selectedClient: ClientRecord | null =
          settled[2]?.status === 'fulfilled' && settled[2].value
            ? settled[2].value as ClientRecord
            : null;
        selectedClientRef.current = selectedClient;

        logger.warn(`[Dashboard][Spend] account="${effectiveAccount}" matched=${!!selectedClient} budgets=${JSON.stringify(selectedClient?.campaign_budgets ?? null)}`);

        const ga4SummaryResponse = getSettledValue<Record<string, unknown>>('ga4-summary', {});
        const ga4ByCountryResponse = getSettledValue<Record<string, unknown>>('ga4-country', {});
        const ga4ByCityResponse = getSettledValue<Record<string, unknown>>('ga4-city', {});
        const ga4ByPageResponse = getSettledValue<Record<string, unknown>>('ga4-page', {});
        const ga4LandingPageCountryResponse = getSettledValue<Record<string, unknown>>('ga4-landing-page-country-views', {});
        const ga4ByDeviceResponse = getSettledValue<Record<string, unknown>>('ga4-device', {});
        const ga4WeeklyUserAcquisitionResponse = getSettledValue<Record<string, unknown>>('ga4-weekly-user-acquisition', {});
        const paidSearchCampaignResponse = getSettledValue<PaidSearchCampaignResponse | Record<string, never>>('ga4-paid-search-campaigns', {});
        const ga4WeeklyResponse = getSettledValue<Record<string, unknown>>('ga4-weekly', {});
        const ga4ByFirstChannelResponse = getSettledValue<Record<string, unknown>>('ga4-first-channel', {});
        const gscSummaryResponse = getSettledValue<Record<string, unknown>>('search-console-summary', {});
        const gscQueryResponse = getSettledValue<Record<string, unknown>>('search-console-query', {});
        const gscPageResponse = getSettledValue<Record<string, unknown>>('search-console-page', {});
        const gscCountryResponse = getSettledValue<Record<string, unknown>>('search-console-country', {});
        const gscDeviceResponse = getSettledValue<Record<string, unknown>>('search-console-device', {});
        const gscWeeklyResponse = getSettledValue<Record<string, unknown>>('search-console-weekly', {});
        const emailResponseCurrent = getSettledValue<Record<string, unknown>>('email-current', {});
        const emailResponsePrevious = getSettledValue<Record<string, unknown>>('email-previous', {});
        const linkedInCurrentResponse = getSettledValue<Record<string, unknown>>('linkedin-current', {});
        const linkedInPreviousResponse = getSettledValue<Record<string, unknown>>('linkedin-previous', {});
        const linkedInDetailedResponse = getSettledValue<Record<string, unknown>>('linkedin-campaign-details', {});
        const linkedInOrganicResponse = getSettledValue<Record<string, unknown>>('linkedin-organic', {});
        const linkedInLeadFormsResponse = getSettledValue<Record<string, unknown>>('linkedin-lead-forms', {});
        const linkedInLeadResponsesResponse = getSettledValue<Record<string, unknown>>('linkedin-lead-responses', {});
        const emptyGoogleAdsResponse: GoogleAdsMetricsResponse = {
          account: effectiveAccount,
          period: `${activeRange.start} to ${activeRange.end}`,
          total_records: 0,
          metrics: [],
        };
        const googleAdsResponse = getSettledValue<GoogleAdsMetricsResponse>('google-ads-current', emptyGoogleAdsResponse);
        const googleAdsPreviousResponse = getSettledValue<GoogleAdsMetricsResponse>('google-ads-previous', {
          ...emptyGoogleAdsResponse,
          period: `${previousRange.start} to ${previousRange.end}`,
        });
        const googleAdsDeviceResponse = getSettledValue<Record<string, unknown>>('google-ads-device', {});

        const linkedInAccountsRaw: unknown = getSettledValue<unknown>('linkedin-accounts', null);
        const linkedInAccountsList: LinkedInAccountInfo[] = Array.isArray(linkedInAccountsRaw)
          ? linkedInAccountsRaw as LinkedInAccountInfo[]
          : Array.isArray((linkedInAccountsRaw as Record<string, unknown> | null)?.accounts)
            ? ((linkedInAccountsRaw as Record<string, unknown>).accounts as LinkedInAccountInfo[])
            : [];
        const mappedLinkedInAccount = linkedInAccountsList.find((item) => normalizeAccountKey(item.account) === normalizeAccountKey(effectiveAccount));
        const followerOrgId = mappedLinkedInAccount?.organization_id || mappedLinkedInAccount?.account_id;
        const [linkedInFollowerCurrentResponse, linkedInFollowerPreviousResponse] = followerOrgId
          ? await Promise.allSettled([
            apiService.getStoredLinkedInFollowerStatistics({
              account: effectiveAccount,
              organization_id: followerOrgId,
              start_date: activeRange.start,
              end_date: activeRange.end,
            }),
            apiService.getStoredLinkedInFollowerStatistics({
              account: effectiveAccount,
              organization_id: followerOrgId,
              start_date: previousRange.start,
              end_date: previousRange.end,
            }),
          ])
          : [
            { status: 'rejected', reason: new Error('LinkedIn follower organization mapping unavailable') } as PromiseRejectedResult,
            { status: 'rejected', reason: new Error('LinkedIn follower organization mapping unavailable') } as PromiseRejectedResult,
          ];

        const budgets = selectedClient?.campaign_budgets || {};
        const currency = (budgets.currency || 'INR').toUpperCase();

        const currentLeadSnapshot = buildLeadSnapshot(currentLeads);
        const previousLeadSnapshot = buildLeadSnapshot(previousLeads);

        const linkedInCurrentMetrics: Record<string, unknown>[] = Array.isArray(linkedInCurrentResponse.campaign_metrics)
          ? linkedInCurrentResponse.campaign_metrics as Record<string, unknown>[]
          : [];
        const linkedInPreviousMetrics: Record<string, unknown>[] = Array.isArray(linkedInPreviousResponse.campaign_metrics)
          ? linkedInPreviousResponse.campaign_metrics as Record<string, unknown>[]
          : [];

        const linkedInSpendCurrent = linkedInCurrentMetrics.reduce(
          (sum, item) => sum + safeNumber(item.cost_in_local_currency ?? item.costInLocalCurrency),
          0
        );
        const linkedInSpendPrevious = linkedInPreviousMetrics.reduce(
          (sum, item) => sum + safeNumber(item.cost_in_local_currency ?? item.costInLocalCurrency),
          0
        );
        const linkedInImpressions = linkedInCurrentMetrics.reduce((sum, item) => sum + safeNumber(item.impressions), 0);
        const linkedInClicks = linkedInCurrentMetrics.reduce((sum, item) => sum + safeNumber(item.clicks), 0);
        const linkedInConversions = linkedInCurrentMetrics.reduce(
          (sum, item) => sum + safeNumber(item.conversions ?? item.externalWebsiteConversions ?? item.leads),
          0
        );
        const linkedInCtr = divideOrNull(linkedInClicks * 100, linkedInImpressions);
        const linkedInConversionRate = divideOrNull(linkedInConversions * 100, linkedInClicks);
        const linkedInCac = divideOrNull(linkedInSpendCurrent, linkedInConversions);

        const googleMetricsRows: GoogleAdsMetric[] = Array.isArray(googleAdsResponse.metrics)
          ? googleAdsResponse.metrics
          : [];
        const googlePreviousMetricsRows: GoogleAdsMetric[] = Array.isArray(googleAdsPreviousResponse.metrics)
          ? googleAdsPreviousResponse.metrics
          : [];
        const googleCampaignSummaryRows = Array.isArray(googleAdsResponse.campaign_summary)
          ? googleAdsResponse.campaign_summary
          : buildGoogleAdsCampaignSummaryFallback(googleMetricsRows);
        const googleSpendCurrent = googleMetricsRows.reduce((sum, item) => sum + safeNumber(item.cost), 0);
        const googleSpendPrevious = googlePreviousMetricsRows.reduce((sum, item) => sum + safeNumber(item.cost), 0);
        const googleImpressions = googleMetricsRows.reduce((sum, item) => sum + safeNumber(item.impressions), 0);
        const googleClicks = googleMetricsRows.reduce((sum, item) => sum + safeNumber(item.clicks), 0);
        const googleConversions = googleMetricsRows.reduce((sum, item) => sum + safeNumber(item.conversions), 0);
        const googleCtr = divideOrNull(googleClicks * 100, googleImpressions);
        const googleConversionRate = divideOrNull(googleConversions * 100, googleClicks);
        const googleCac = divideOrNull(googleSpendCurrent, googleConversions);

        const linkedInCurrentResult = settledByKey.get('linkedin-current');
        const googleCurrentResult = settledByKey.get('google-ads-current');
        const hasLinkedInLiveData = linkedInCurrentResult?.status === 'fulfilled' && Boolean(linkedInCurrentResult.value);
        const hasGoogleLiveData = googleCurrentResult?.status === 'fulfilled' && Boolean(googleCurrentResult.value);

        const budgetScale = activeRange.days / 30;
        const proxyEmailSpend = safeNumber(budgets.email_campaigns) * budgetScale;
        const proxyTelemarketingSpend = safeNumber(budgets.telemarketing) * budgetScale;
        const proxyLinkedInSpend = safeNumber(budgets.linkedin) * budgetScale;
        const proxyGoogleSpend = safeNumber(budgets.google) * budgetScale;
        const proxyMetaSpend = safeNumber(budgets.meta) * budgetScale;

        const spendByChannel: Record<ChannelKey, ChannelSpendState> = {
          email: proxyEmailSpend > 0
            ? { amount: proxyEmailSpend, status: 'proxy', note: 'Budget-proxy spend (scaled by selected period)' }
            : { amount: null, status: 'unavailable', note: 'No live spend feed configured' },
          telemarketing: proxyTelemarketingSpend > 0
            ? { amount: proxyTelemarketingSpend, status: 'proxy', note: 'Budget-proxy spend (scaled by selected period)' }
            : { amount: null, status: 'unavailable', note: 'No live spend feed configured' },
          linkedin: hasLinkedInLiveData
            ? { amount: linkedInSpendCurrent, status: 'live', note: 'LinkedIn live campaign spend' }
            : proxyLinkedInSpend > 0
              ? { amount: proxyLinkedInSpend, status: 'proxy', note: 'Budget-proxy spend (live data unavailable)' }
              : { amount: null, status: 'unavailable', note: 'No spend data available' },
          google: hasGoogleLiveData
            ? { amount: googleSpendCurrent, status: 'live', note: 'Google Ads live campaign spend' }
            : proxyGoogleSpend > 0
              ? { amount: proxyGoogleSpend, status: 'proxy', note: 'Budget-proxy spend (live data unavailable)' }
              : { amount: null, status: 'unavailable', note: 'No spend data available' },
          meta: proxyMetaSpend > 0
            ? { amount: proxyMetaSpend, status: 'proxy', note: 'Budget-proxy spend (Meta live feed not integrated)' }
            : { amount: null, status: 'unavailable', note: 'Meta live feed not integrated yet' },
          other: { amount: null, status: 'unavailable', note: 'No spend model defined' },
        };

        const previousSpendByChannel: Record<ChannelKey, number> = {
          email: proxyEmailSpend,
          telemarketing: proxyTelemarketingSpend,
          linkedin: hasLinkedInLiveData ? linkedInSpendPrevious : proxyLinkedInSpend,
          google: hasGoogleLiveData ? googleSpendPrevious : proxyGoogleSpend,
          meta: proxyMetaSpend,
          other: 0,
        };

        const channelRows: ChannelMetricsRow[] = PRIMARY_CHANNEL_KEYS.map((channel) => {
          const funnelStats = currentLeadSnapshot.byChannel[channel];
          const spendState = spendByChannel[channel];
          const cpl = spendState.amount === null ? null : divideOrNull(spendState.amount, funnelStats.leads);
          const cpmql = spendState.amount === null ? null : divideOrNull(spendState.amount, funnelStats.mqls);
          const cpsql = spendState.amount === null ? null : divideOrNull(spendState.amount, funnelStats.sqls);

          return {
            channel,
            spend: spendState.amount,
            spendStatus: spendState.status,
            spendNote: spendState.note,
            leads: funnelStats.leads,
            mqls: funnelStats.mqls,
            sqls: funnelStats.sqls,
            cpl,
            cpmql,
            cpsql,
          };
        });

        if (currentLeadSnapshot.byChannel.other.leads > 0) {
          channelRows.push({
            channel: 'other',
            spend: null,
            spendStatus: 'unavailable',
            spendNote: 'No spend model defined',
            leads: currentLeadSnapshot.byChannel.other.leads,
            mqls: currentLeadSnapshot.byChannel.other.mqls,
            sqls: currentLeadSnapshot.byChannel.other.sqls,
            cpl: null,
            cpmql: null,
            cpsql: null,
          });
        }

        const totalSpend = channelRows.reduce((sum, row) => sum + (row.spend || 0), 0);
        const previousTotalSpend = PRIMARY_CHANNEL_KEYS.reduce((sum, channel) => sum + previousSpendByChannel[channel], 0);
        const cplCurrent = divideOrNull(totalSpend, currentLeadSnapshot.totalLeads);
        const cplPrevious = divideOrNull(previousTotalSpend, previousLeadSnapshot.totalLeads);
        const cpmqlCurrent = divideOrNull(totalSpend, currentLeadSnapshot.totalMqls);
        const cpmqlPrevious = divideOrNull(previousTotalSpend, previousLeadSnapshot.totalMqls);
        const cpsqlCurrent = divideOrNull(totalSpend, currentLeadSnapshot.totalSqls);
        const cpsqlPrevious = divideOrNull(previousTotalSpend, previousLeadSnapshot.totalSqls);

        const ga4SummaryTotals: Record<string, unknown> = (ga4SummaryResponse.totals as Record<string, unknown>) || ga4SummaryResponse;

        const countryRows = (Array.isArray(ga4ByCountryResponse.data) ? ga4ByCountryResponse.data : [])
          .map((row) => ({
            label: String((row as Record<string, unknown>)._id || 'Unknown'),
            users: safeNumber((row as Record<string, unknown>).activeUsers),
          }))
          .sort((a, b) => b.users - a.users)
          .slice(0, 10);

        const cityRows = (Array.isArray(ga4ByCityResponse.data) ? ga4ByCityResponse.data : [])
          .map((row) => ({
            label: String((row as Record<string, unknown>)._id || 'Unknown'),
            users: safeNumber((row as Record<string, unknown>).activeUsers),
          }))
          .sort((a, b) => b.users - a.users)
          .slice(0, 10);

        const channelUserMap = new Map<string, number>();
        (Array.isArray(ga4ByFirstChannelResponse.data) ? ga4ByFirstChannelResponse.data : []).forEach((row) => {
          const rowData = row as Record<string, unknown>;
          const source = String(
            rowData.firstUserDefaultChannelGroup
              || rowData.channel
              || rowData._id
              || 'Unknown'
          ).trim() || 'Unknown';
          const users = safeNumber(rowData.totalUsers ?? rowData.activeUsers ?? rowData.users);
          channelUserMap.set(source, (channelUserMap.get(source) || 0) + users);
        });
        const sourceRows = Array.from(channelUserMap.entries())
          .map(([source, users]) => ({ source, users }))
          .sort((a, b) => b.users - a.users)
          .slice(0, 10);

        const organicVisitors = Array.from(channelUserMap.entries())
          .filter(([source]) => source.trim().toLowerCase() === 'organic search')
          .reduce((sum, [, users]) => sum + users, 0);

        const totalWebsiteVisitors = safeNumber(ga4SummaryTotals.totalUsers ?? ga4SummaryTotals.sessions);
        const websiteConversionRate = divideOrNull(currentLeadSnapshot.totalLeads * 100, totalWebsiteVisitors) || 0;

        const pageviewRows = (Array.isArray(ga4ByPageResponse.data) ? ga4ByPageResponse.data : [])
          .map((row) => ({
            pagePath: String((row as Record<string, unknown>)._id || 'Unknown'),
            pageViews: safeNumber((row as Record<string, unknown>).screenPageViews),
          }))
          .sort((a, b) => b.pageViews - a.pageViews)
          .slice(0, 10);

        const keywordRows = (Array.isArray(gscQueryResponse.data) ? gscQueryResponse.data : [])
          .map((row) => {
            const rowData = row as Record<string, unknown>;
            const rawCtr = safeNumber(rowData.ctr);
            const ctrPct = rawCtr > 1 ? rawCtr : rawCtr * 100;
            return {
              query: String(rowData.query || 'N/A'),
              clicks: safeNumber(rowData.clicks),
              impressions: safeNumber(rowData.impressions),
              ctr: ctrPct,
              position: safeNumber(rowData.position),
            };
          })
          .sort((a, b) => a.position - b.position)
          .slice(0, 15);

        const emailMetricsCurrent = (emailResponseCurrent.metrics as Record<string, unknown>) || {};
        const emailMetricsPrevious = (emailResponsePrevious.metrics as Record<string, unknown>) || {};

        const emailSentCurrent = safeNumber(emailMetricsCurrent.total_sent);
        const emailDeliveredCurrent = safeNumber(emailMetricsCurrent.total_delivered);
        const emailOpensCurrent = safeNumber(emailMetricsCurrent.total_opens);
        const emailClicksCurrent = safeNumber(emailMetricsCurrent.total_clicks);
        const emailDeliveryRateCurrent = safeNumber(
          emailMetricsCurrent.delivery_rate ?? divideOrNull(emailDeliveredCurrent * 100, emailSentCurrent)
        );
        const emailOpenRateCurrent = safeNumber(
          emailMetricsCurrent.avg_open_rate ?? divideOrNull(emailOpensCurrent * 100, emailDeliveredCurrent)
        );
        const emailCtrCurrent = safeNumber(
          emailMetricsCurrent.avg_click_rate ?? divideOrNull(emailClicksCurrent * 100, emailDeliveredCurrent)
        );

        const emailSentPrevious = safeNumber(emailMetricsPrevious.total_sent);
        const emailDeliveredPrevious = safeNumber(emailMetricsPrevious.total_delivered);
        const emailOpensPrevious = safeNumber(emailMetricsPrevious.total_opens);
        const emailClicksPrevious = safeNumber(emailMetricsPrevious.total_clicks);
        const emailDeliveryRatePrevious = safeNumber(
          emailMetricsPrevious.delivery_rate ?? divideOrNull(emailDeliveredPrevious * 100, emailSentPrevious)
        );
        const emailOpenRatePrevious = safeNumber(
          emailMetricsPrevious.avg_open_rate ?? divideOrNull(emailOpensPrevious * 100, emailDeliveredPrevious)
        );
        const emailCtrPrevious = safeNumber(
          emailMetricsPrevious.avg_click_rate ?? divideOrNull(emailClicksPrevious * 100, emailDeliveredPrevious)
        );

        const socialPosts: SocialPostRow[] = (Array.isArray(linkedInOrganicResponse.organic_posts)
          ? linkedInOrganicResponse.organic_posts
          : []
        )
          .map((post) => {
            const postData = post as Record<string, unknown>;
            const engagementValue = safeNumber(postData.engagement_rate ?? postData.engagement);
            const normalizedEngagement = engagementValue > 1 ? engagementValue : engagementValue * 100;
            const ctrValue = safeNumber(postData.ctr);
            const normalizedCtr = ctrValue > 1 ? ctrValue : ctrValue * 100;
            const rawLabel = String(postData.commentary || postData.post_urn || 'LinkedIn Post');
            return {
              channel: 'LinkedIn',
              label: rawLabel.length > 56 ? `${rawLabel.slice(0, 55)}...` : rawLabel,
              impressions: safeNumber(postData.impression_count),
              engagementRate: normalizedEngagement,
              ctr: normalizedCtr,
            };
          });

        const linkedInFollowerPayload = linkedInFollowerCurrentResponse.status === 'fulfilled'
          && linkedInFollowerCurrentResponse.value
          && typeof linkedInFollowerCurrentResponse.value === 'object'
          ? linkedInFollowerCurrentResponse.value as Record<string, unknown>
          : null;
        const linkedInFollowerCurrentMetrics = linkedInFollowerPayload?.current
          && typeof linkedInFollowerPayload.current === 'object'
          ? linkedInFollowerPayload.current as Record<string, unknown>
          : null;

        let followerRows: FollowerRow[] = [
          {
            channel: 'LinkedIn',
            totalFollowers: null,
            netGrowth: null,
            status: 'unavailable',
            note: 'Follower growth data unavailable for this account',
          },
          {
            channel: 'Meta',
            totalFollowers: null,
            netGrowth: null,
            status: 'unavailable',
            note: 'Meta follower integration pending',
          },
        ];

        if (linkedInFollowerCurrentMetrics) {
          const currentFollowers = safeNumber(
            linkedInFollowerCurrentMetrics.totalFollowers ?? linkedInFollowerCurrentMetrics.total_followers
          );
          const currentNetGrowth = safeNumber(
            linkedInFollowerCurrentMetrics.netGain ?? linkedInFollowerCurrentMetrics.net_gain
          );

          followerRows = [
            {
              channel: 'LinkedIn',
              totalFollowers: currentFollowers,
              netGrowth: currentNetGrowth,
              status: 'live',
              note: 'LinkedIn follower statistics',
            },
            {
              channel: 'Meta',
              totalFollowers: null,
              netGrowth: null,
              status: 'unavailable',
              note: 'Meta follower integration pending',
            },
          ];
        }

        const adsPerformance: AdsRow[] = [
          {
            channel: 'LinkedIn',
            impressions: linkedInImpressions,
            clicks: linkedInClicks,
            ctr: linkedInCtr,
            conversions: linkedInConversions,
            conversionRate: linkedInConversionRate,
            cac: linkedInCac,
            status: hasLinkedInLiveData ? 'live' : 'unavailable',
            note: hasLinkedInLiveData ? 'Live LinkedIn ads data' : 'LinkedIn ads data unavailable',
          },
          {
            channel: 'Google',
            impressions: googleImpressions,
            clicks: googleClicks,
            ctr: googleCtr,
            conversions: googleConversions,
            conversionRate: googleConversionRate,
            cac: googleCac,
            status: hasGoogleLiveData ? 'live' : 'unavailable',
            note: hasGoogleLiveData ? 'Live Google Ads data' : 'Google Ads data unavailable',
          },
          {
            channel: 'Meta',
            impressions: null,
            clicks: null,
            ctr: null,
            conversions: null,
            conversionRate: null,
            cac: null,
            status: proxyMetaSpend > 0 ? 'proxy' : 'unavailable',
            note: proxyMetaSpend > 0 ? 'Budget proxy available (live ads feed pending)' : 'Meta ads live integration pending',
          },
        ];

        const websiteFormFills = currentLeads.filter((lead) => normalizeLeadFlowBucket(lead) === 'website_form_fill').length;
        const telemarketingFollowUp = currentLeads.filter((lead) => normalizeLeadFlowBucket(lead) === 'telemarketing').length;
        const emailReplyLeads = currentLeads.filter((lead) => normalizeLeadFlowBucket(lead) === 'email_replies').length;

        const leadFlow: LeadFlowRow[] = [
          { label: 'Website form fills', value: websiteFormFills },
          { label: 'Telemarketing follow-up', value: telemarketingFollowUp },
          { label: 'Email replies', value: emailReplyLeads },
        ];

        const worked: string[] = [];
        const attention: string[] = [];
        const priorities: string[] = [];

        if (googleCac !== null && linkedInCac !== null && googleCac < linkedInCac) {
          worked.push('Google Ads delivered a lower CAC than LinkedIn in the selected period.');
        }
        if (emailOpenRateCurrent >= 20) {
          worked.push(`Email open rate held healthy at ${emailOpenRateCurrent.toFixed(1)}%.`);
        }
        if (websiteConversionRate >= 2) {
          worked.push(`Website lead conversion stayed strong at ${websiteConversionRate.toFixed(1)}%.`);
        }

        if (linkedInCac !== null && googleCac !== null && linkedInCac > googleCac * 1.25) {
          attention.push('LinkedIn CAC is materially higher than Google CAC and needs tightening.');
        }
        if (emailCtrCurrent < 2) {
          attention.push('Email CTR is low; review CTA hierarchy and campaign targeting.');
        }
        if (proxyMetaSpend > 0) {
          attention.push('Meta spend is currently proxy-only; live ad performance cannot yet be validated.');
        }

        priorities.push('Prioritize channels with lower CAC and stronger conversion efficiency.');
        priorities.push('Refine underperforming ad creatives and landing pages for higher conversion rate.');
        priorities.push('Expand high-intent campaigns and repurpose top-performing content themes.');

        const summaryMessage = `This dashboard is read-only and tracks ${CHANNEL_LABELS.email}, ${CHANNEL_LABELS.linkedin}, ${CHANNEL_LABELS.google}, ${CHANNEL_LABELS.telemarketing}, and ${CHANNEL_LABELS.meta} metrics for ${activeRange.label}.`;

        const builtData: DashboardData = {
          clientName: selectedClient?.client_name || effectiveAccount,
          industry: selectedClient?.industry || 'Not specified',
          currency,
          totalSpend,
          totalSpendTrend: getTrend(totalSpend, previousTotalSpend),
          leads: currentLeadSnapshot.totalLeads,
          leadsTrend: getTrend(currentLeadSnapshot.totalLeads, previousLeadSnapshot.totalLeads),
          mqls: currentLeadSnapshot.totalMqls,
          mqlsTrend: getTrend(currentLeadSnapshot.totalMqls, previousLeadSnapshot.totalMqls),
          sqls: currentLeadSnapshot.totalSqls,
          sqlsTrend: getTrend(currentLeadSnapshot.totalSqls, previousLeadSnapshot.totalSqls),
          cpl: cplCurrent,
          cplTrend: getTrend(cplCurrent || 0, cplPrevious || 0),
          cpmql: cpmqlCurrent,
          cpmqlTrend: getTrend(cpmqlCurrent || 0, cpmqlPrevious || 0),
          cpsql: cpsqlCurrent,
          cpsqlTrend: getTrend(cpsqlCurrent || 0, cpsqlPrevious || 0),
          websiteVisitors: totalWebsiteVisitors,
          organicVisitors,
          websiteConversionRate,
          channelRows,
          leadsTableRows: currentLeads,
          countryRows,
          cityRows,
          sourceRows,
          pageviewRows,
          keywordRows,
          emailSummary: {
            sent: emailSentCurrent,
            delivered: emailDeliveredCurrent,
            deliveryRate: emailDeliveryRateCurrent,
            opens: emailOpensCurrent,
            openRate: emailOpenRateCurrent,
            clicks: emailClicksCurrent,
            ctr: emailCtrCurrent,
          },
          socialPosts,
          followers: followerRows,
          adsPerformance,
          leadFlow,
          worked: worked.length > 0 ? worked : ['Baseline reporting is active and ready for period-over-period analysis.'],
          attention: attention.length > 0 ? attention : ['No major risk signal detected in the selected period.'],
          priorities,
          summaryMessage,
        };

        if (emailSentPrevious === 0 && emailDeliveredPrevious === 0 && emailOpensPrevious === 0 && emailClicksPrevious === 0) {
          logger.debug('[Dashboard] Previous email period has limited data context');
        }
        if (emailDeliveryRatePrevious === 0 && emailOpenRatePrevious === 0 && emailCtrPrevious === 0) {
          logger.debug('[Dashboard] Previous email rate context is limited');
        }

        if (myLoadId !== loadIdRef.current) {
          logger.warn(`[Dashboard][RaceGuard] Load #${myLoadId} discarded — superseded by load #${loadIdRef.current} for account="${effectiveAccount}"`);
          return;
        }
        logger.warn(`[Dashboard][RaceGuard] Load #${myLoadId} committed for account="${effectiveAccount}"`);

        setCrossPageData((prev) => ({
          ...prev,
          'ga4-summary-raw': ga4SummaryResponse,
          'ga4-page-raw': ga4ByPageResponse,
          'ga4-fintrans-landing-page-views-by-country': ga4LandingPageCountryResponse,
          'ga4-country-raw': ga4ByCountryResponse,
          'ga4-city-raw': ga4ByCityResponse,
          'ga4-device-raw': ga4ByDeviceResponse,
          'ga4-total-vs-organic-weekly': ga4WeeklyUserAcquisitionResponse,
          'ga4-paid-search-campaign-performance': paidSearchCampaignResponse,
          'ga4-paid-search-users-by-campaign': paidSearchCampaignResponse,
          'ga4-weekly-raw': ga4WeeklyResponse,
          'gsc-summary-raw': gscSummaryResponse,
          'gsc-query-raw': gscQueryResponse,
          'gsc-page-raw': gscPageResponse,
          'gsc-country-raw': gscCountryResponse,
          'gsc-device-raw': gscDeviceResponse,
          'gsc-weekly-raw': gscWeeklyResponse,
          'linkedin-campaigns': linkedInCurrentMetrics,
          'linkedin-monthly-spend': linkedInCurrentMetrics,
          'linkedin-monthly-spend-trend': linkedInCurrentMetrics,
          'linkedin-campaign-details-raw': linkedInDetailedResponse,
          'linkedin-creative-details-raw': linkedInDetailedResponse,
          'linkedin-organic-posts': linkedInOrganicResponse,
          'linkedin-lead-forms': linkedInLeadFormsResponse,
          'linkedin-lead-responses-raw': linkedInLeadResponsesResponse,
          'linkedin-follower-stats-raw': linkedInFollowerCurrentResponse.status === 'fulfilled' ? linkedInFollowerCurrentResponse.value : null,
          'linkedin-follower-stats-previous-raw': linkedInFollowerPreviousResponse.status === 'fulfilled' ? linkedInFollowerPreviousResponse.value : null,
          'email-campaigns': emailResponseCurrent,
          'email-current-raw': emailResponseCurrent,
          'gads-stat-impressions': { impressions: googleImpressions, clicks: googleClicks, cost: googleSpendCurrent },
          'gads-stat-clicks': { impressions: googleImpressions, clicks: googleClicks, ctr: googleCtr },
          'gads-stat-cost': { cost: googleSpendCurrent },
          'gads-campaign-table': googleMetricsRows,
          'gads-campaign-performance-summary': googleCampaignSummaryRows,
          'google-ads-metrics-raw': googleAdsResponse,
          'google-ads-device-raw': googleAdsDeviceResponse,
        }));
        setDashboardData(builtData);
        // Requests that feed derived dashboard values are not render-ready until
        // builtData and the final cross-page response map have been committed.
        window.requestAnimationFrame(() => {
          if (myLoadId === loadIdRef.current) {
            setEndpointDiagnostics(nextDiagnostics);
          }
        });
        setLastSyncedAt(new Date().toLocaleString());
        setLeadsPage(0);
      } catch (loadError) {
        logger.error('[Dashboard] Failed to load dashboard data', loadError);
        setError("We couldn't load dashboard metrics for this account. Please try refreshing.");
      } finally {
        if (manualRefresh) {
          setRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    },
    [activeRange, appliedFilters?.account, canonicalSelectedAccount]
  );

  useEffect(() => {
    if (!appliedFilters?.account || !canonicalSelectedAccount || layoutStorageAccount !== canonicalSelectedAccount) {
      return;
    }
    const manualRefresh = manualRefreshRequestedRef.current;
    manualRefreshRequestedRef.current = false;
    loadDashboardData(manualRefresh);
  }, [appliedFilters?.account, activeRange.end, activeRange.start, canonicalSelectedAccount, layoutStorageAccount, refreshTick, loadDashboardData]);

  useEffect(() => {
    setCrossPageData({});
    setCrossPageLoading({});
    setKeywordTrendMatchType('ALL');
    setKeywordTrendRankBy('impressions');
    setTablePagination((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      return {};
    });
  }, [appliedFilters?.account, activeRange.start, activeRange.end]);

  useEffect(() => {
    setCrossPageData((prev) => ({
      ...prev,
      'keyword-ranking-monthly': undefined,
      'bofu-monthly-trend': undefined,
      'qualified-monthly-trend': undefined,
    }));
  }, [
    trendMonthSettings['keyword-ranking-monthly'],
    trendMonthSettings['bofu-monthly-trend'],
    trendMonthSettings['qualified-monthly-trend'],
  ]);

  const pagedLeads = useMemo(() => {
    if (!dashboardData) return [];
    const startIndex = leadsPage * leadsRowsPerPage;
    const endIndex = startIndex + leadsRowsPerPage;
    return dashboardData.leadsTableRows.slice(startIndex, endIndex);
  }, [dashboardData, leadsPage, leadsRowsPerPage]);

  const totalLeadsPages = useMemo(() => {
    if (!dashboardData) return 1;
    return Math.max(1, Math.ceil(dashboardData.leadsTableRows.length / leadsRowsPerPage));
  }, [dashboardData, leadsRowsPerPage]);
  const endpointWarnings = useMemo(
    () => Object.values(endpointDiagnostics).filter(
      (diag) => diag.status === 'error' || diag.status === 'unauthorized_or_mapping_missing'
    ),
    [endpointDiagnostics]
  );
  const endpointWarningText = useMemo(() => {
    if (endpointWarnings.length === 0) {
      return null;
    }
    const friendlyList = endpointWarnings.slice(0, 3).map((diag) => diag.userLabel || getDiagnosticLabel(diag.key));
    const moreCount = endpointWarnings.length - friendlyList.length;
    const labelText = `${friendlyList.join(', ')}${moreCount > 0 ? ` and ${moreCount} more` : ''}`;
    const firstGuidance = toFriendlyDiagnosticMessage(endpointWarnings[0]);
    return {
      title: 'Some dashboard sections are partially unavailable for this account.',
      detail: `${labelText}. ${firstGuidance} Other available sections are still visible. You can refresh, try a broader date range, or contact your admin if this continues.`,
    };
  }, [endpointWarnings]);

  const isDiagnosticUnavailable = useCallback((...keys: string[]) => (
    keys.some((key) => {
      const status = endpointDiagnostics[key]?.status;
      return status === 'error' || status === 'unauthorized_or_mapping_missing';
    })
  ), [endpointDiagnostics]);

  const isWebsiteDataUnavailable = isDiagnosticUnavailable(
    'ga4-summary',
    'ga4-country',
    'ga4-city',
    'ga4-first-channel',
  );
  const isLeadDataUnavailable = isDiagnosticUnavailable('leads-current', 'leads-previous');
  const isSocialDataUnavailable = isDiagnosticUnavailable('linkedin-organic');

  const applyDraftFilters = useCallback((manualRefresh: boolean) => {
    const nextAccount = draftFilters.account
      ? resolveCanonicalAccount(draftFilters.account, accounts) ?? draftFilters.account
      : null;
    if (!nextAccount) {
      return null;
    }

    const nextApplied: DashboardFilterState = {
      account: nextAccount,
      datePreset: draftFilters.datePreset,
      customStartDate: draftFilters.customStartDate,
      customEndDate: draftFilters.customEndDate,
    };

    setAppliedFilters(nextApplied);
    if (manualRefresh) {
      // Only set the flag — do NOT call setRefreshTick here.
      // runBackgroundSync will call setRefreshTick after the backend sync
      // completes. This ensures the post-sync data reload reads
      // manualRefreshRequestedRef=true and bypasses the frontend cache,
      // preventing stale data from being shown after a manual refresh.
      manualRefreshRequestedRef.current = true;
    }
    return nextApplied;
  }, [accounts, draftFilters]);

  const runBackgroundSync = useCallback(async (filters: DashboardFilterState) => {
    if (!filters.account) {
      return;
    }

    const canonicalAccount = resolveCanonicalAccount(filters.account, accounts) ?? filters.account;
    const range = getDateRange(filters.datePreset, filters.customStartDate, filters.customEndDate);
    const visibleWidgetIds = new Set(visibleWidgets.map((widget) => widget.id));
    const visibleSources = new Set(visibleWidgets.map((widget) => widget.source));
    const currentRun = backgroundSyncRunRef.current + 1;
    backgroundSyncRunRef.current = currentRun;
    const googleAdsVisible = visibleSources.has('google-ads');
    const googleAdsSharedDependency = SHARED_GOOGLE_ADS_WIDGET_IDS.some((widgetId) => visibleWidgetIds.has(widgetId));
    const selectedClientForAccount = selectedClientRef.current
      && normalizeAccountKey(selectedClientRef.current.account) === normalizeAccountKey(canonicalAccount)
        ? selectedClientRef.current
        : null;
    const googleAdsMappingKnown = selectedClientForAccount !== null;
    const googleAdsHasActiveMapping = hasActiveChannelMapping(selectedClientForAccount, 'google_ads');

    const requestedSources = new Set<string>();
    if (
      visibleSources.has('google-analytics')
      || ['website-lead-flow', 'traffic-source', 'geo-visitors-country', 'geo-visitors-city', 'pageviews'].some((widgetId) => visibleWidgetIds.has(widgetId as DashboardWidgetId))
    ) {
      requestedSources.add('ga4');
    }
    if (googleAdsVisible || googleAdsSharedDependency) {
      if (googleAdsMappingKnown && !googleAdsHasActiveMapping) {
        logger.debug('[Dashboard] Skipping google_ads background sync: no active mapping for selected client', {
          account: canonicalAccount,
          explicitWidgetVisible: googleAdsVisible,
          sharedSummaryVisible: googleAdsSharedDependency,
        });
      } else {
        logger.debug('[Dashboard] Requesting google_ads background sync', {
          account: canonicalAccount,
          reason: googleAdsVisible ? 'explicit_widget' : 'shared_summary_widget',
        });
        requestedSources.add('google_ads');
      }
    } else {
      logger.debug('[Dashboard] Skipping google_ads background sync: no visible widget requires it', {
        account: canonicalAccount,
      });
    }
    if (
      visibleSources.has('linkedin')
      || ['social-posts', 'linkedin-organic-posts', 'email-followers', 'ads-performance'].some((widgetId) => visibleWidgetIds.has(widgetId as DashboardWidgetId))
    ) {
      requestedSources.add('linkedin');
    }
    if (
      visibleSources.has('leads')
      || ['kpi-summary', 'cost-summary', 'channel-funnel', 'website-lead-flow', 'leads-table'].some((widgetId) => visibleWidgetIds.has(widgetId as DashboardWidgetId))
    ) {
      requestedSources.add('leads');
    }

    if (requestedSources.size === 0) {
      setSyncState('idle');
      setSyncMessage(null);
      return;
    }

    setSyncState('syncing');
    setSyncMessage('Showing saved data while the latest updates are being fetched in the background.');
    // Disable the Refresh button for the entire duration of the sync so the
    // user cannot trigger a second concurrent refresh.
    setRefreshing(true);

    const job = await apiService.startDashboardRefreshJob({
      account: canonicalAccount,
      start_date: range.start,
      end_date: range.end,
      sources: Array.from(requestedSources),
    });

    const pollIntervalMs = 3500;
    const maxPollAttempts = 20;
    let pollAttempts = 0;

    while (backgroundSyncRunRef.current === currentRun) {
      await new Promise((resolve) => window.setTimeout(resolve, pollIntervalMs));
      pollAttempts += 1;
      const status = await apiService.getDashboardRefreshJob(job.job_id);

      if (backgroundSyncRunRef.current !== currentRun) {
        return;
      }

      const runningCount = Object.values(status.source_statuses || {}).filter((entry) => (
        typeof entry === 'object' && entry !== null && (entry as { status?: string }).status === 'running'
      )).length;

      if (status.status === 'queued' || status.status === 'running') {
        if (pollAttempts >= maxPollAttempts) {
          setSyncState('syncing');
          setSyncMessage('Latest updates are still running in the background. Saved dashboard data remains available, and you can refresh again shortly.');
          // Re-enable the Refresh button so the user can try again.
          setRefreshing(false);
          return;
        }
        setSyncState('syncing');
        setSyncMessage(
          runningCount > 0
            ? `Showing saved data while ${runningCount} source update${runningCount === 1 ? '' : 's'} ${runningCount === 1 ? 'continues' : 'continue'} in the background.`
            : 'Showing saved data while the latest updates are being fetched in the background.'
        );
        continue;
      }

      apiService.clearDashboardAccountCache(canonicalAccount);
      setSyncState(status.status === 'complete' ? 'complete' : 'partial_error');
      setSyncMessage(
        status.status === 'complete'
          ? 'Latest source updates were fetched successfully. Refreshing widgets now.'
          : 'Some sources could not be updated live just now. Saved data is still shown where available.'
      );
      setRefreshTick((current) => current + 1);
      return;
    }
  }, [accounts, visibleWidgets]);

  const handleWidgetRemove = (widgetId: DashboardWidgetId) => {
    if (!canCustomizeDashboard) {
      return;
    }
    setWidgetLayouts((currentLayouts) => ({
      ...currentLayouts,
      [widgetId]: {
        ...currentLayouts[widgetId],
        hidden: true,
      },
    }));
    if (maximizedWidgetId === widgetId) {
      setMaximizedWidgetId(null);
    }
    const widgetTitle = getWidgetDefinition(widgetId)?.title || 'Widget';
    setLayoutNotice({
      open: true,
      message: `${widgetTitle} removed from the dashboard.`,
      removedWidgetId: widgetId,
    });
  };

  const handleWidgetRestore = (widgetId: DashboardWidgetId) => {
    if (!canCustomizeDashboard) {
      return;
    }
    setWidgetLayouts((currentLayouts) => ({
      ...currentLayouts,
      [widgetId]: { ...currentLayouts[widgetId], hidden: false },
    }));
    setWidgetOrder((prev) =>
      prev.includes(widgetId) ? prev : [...prev, widgetId]
    );
    void loadCrossPageWidgetDataRef.current?.(widgetId);
  };

  const handleSaveLayout = () => {
    if (!canCustomizeDashboard) {
      return;
    }
    setSavedSnapshot({ layouts: { ...widgetLayouts }, order: [...widgetOrder] });
  };

  const handleLayoutReset = () => {
    if (!canCustomizeDashboard) {
      return;
    }
    if (savedSnapshot) {
      setWidgetLayouts(savedSnapshot.layouts);
      setWidgetOrder(normalizeWidgetOrder(savedSnapshot.order, widgetRegistry));
    } else {
      setWidgetLayouts(buildDefaultWidgetLayouts());
      setWidgetOrder(normalizeWidgetOrder(undefined, widgetRegistry));
    }
    setMaximizedWidgetId(null);
  };

  const handleWidgetDrop = (targetId: DashboardWidgetId) => {
    if (!canCustomizeDashboard) {
      setDraggedWidgetId(null);
      setDragOverWidgetId(null);
      return;
    }
    if (!draggedWidgetId || draggedWidgetId === targetId) {
      setDraggedWidgetId(null);
      setDragOverWidgetId(null);
      return;
    }
    setWidgetOrder((prev) => {
      const next = prev.filter((id) => id !== draggedWidgetId);
      const targetIdx = next.indexOf(targetId);
      next.splice(targetIdx, 0, draggedWidgetId);
      return next;
    });
    setDraggedWidgetId(null);
    setDragOverWidgetId(null);
  };

  const handleResizeStart = (
    event: React.PointerEvent<HTMLButtonElement>,
    widgetId: DashboardWidgetId,
    direction: ResizeDirection
  ) => {
    if (!canCustomizeDashboard) {
      return;
    }
    const currentWidgetLayout = widgetLayouts[widgetId];
    if (!currentWidgetLayout) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setResizeSession({
      id: widgetId,
      direction,
      startX: event.clientX,
      startY: event.clientY,
      startColSpan: currentWidgetLayout.colSpan,
      startHeight: currentWidgetLayout.height,
    });
  };

  const loadCrossPageWidgetData = useCallback(async (widgetId: DashboardWidgetId) => {
    if (!appliedFilters?.account || !canonicalSelectedAccount || !activeRange) {
      return;
    }
    const effectiveAccount = canonicalSelectedAccount;
    const isLeadTrendWidget = widgetId === 'bofu-monthly-trend' || widgetId === 'qualified-monthly-trend';
    const widgetDefinition = widgetRegistry.find((widget) => widget.id === widgetId);
    if (!isLeadTrendWidget && (!widgetDefinition || widgetDefinition.enabled === false)) {
      logger.debug('[Dashboard] Skipping cross-page widget fetch for unavailable or deprecated widget', {
        account: effectiveAccount,
        widgetId,
        enabled: widgetDefinition?.enabled ?? null,
      });
      return;
    }
    if (isLeadTrendWidget) {
      const bothLoaded = crossPageData['bofu-monthly-trend'] !== undefined
        && crossPageData['qualified-monthly-trend'] !== undefined;
      if (bothLoaded || crossPageLoading['bofu-monthly-trend'] || crossPageLoading['qualified-monthly-trend']) {
        return;
      }
    }
    const timeseriesWidgetKeys: Partial<Record<DashboardWidgetId, string[]>> = {
      'traffic-source': ['ga4-source-timeseries-raw'],
      'geo-visitors-country': ['ga4-country-timeseries-raw'],
      'geo-visitors-city': ['ga4-city-timeseries-raw'],
      'pageviews': ['ga4-page-timeseries-raw'],
      'ga4-pageviews-by-path': ['ga4-page-timeseries-raw'],
      'ga4-traffic-by-device': ['ga4-device-timeseries-raw'],
      'gads-performance-by-device': ['google-ads-device-timeseries-raw'],
    };
    const requiredTimeseriesKeys = timeseriesWidgetKeys[widgetId] || [];
    const hasAllTimeseriesKeys = requiredTimeseriesKeys.length > 0
      && requiredTimeseriesKeys.every((key) => crossPageData[key] !== undefined);
    if (requiredTimeseriesKeys.length > 0 && hasAllTimeseriesKeys) {
      return;
    }
    // Widgets that derive from dashboardData need no API call — they'll render once dashboardData is loaded.
    const derivedFromDashboard: ReadonlySet<DashboardWidgetId> = new Set([
      'linkedin-campaigns', 'linkedin-monthly-spend', 'linkedin-follower-stats', 'linkedin-organic-posts',
      'ga4-users-stat', 'ga4-sessions-stat', 'ga4-engagement-stat',
      'gads-stat-impressions', 'gads-stat-clicks', 'gads-stat-cost', 'gads-campaign-table',
      'email-stat-openrate', 'email-stat-ctr', 'email-stat-sent', 'email-top-campaigns',
      'leads-stat',
      'ga4-users-total-stat', 'ga4-new-users-stat', 'ga4-sessions-total-stat', 'ga4-pageviews-total-stat',
      'ga4-pageviews-per-user-stat', 'ga4-avg-time-per-page-stat', 'ga4-percent-users-stat',
      'ga4-seo-weekly-stats', 'ga4-insights-aggregated', 'ga4-pages-screens', 'ga4-regions',
      'gsc-total-clicks-stat', 'gsc-total-impressions-stat', 'gsc-average-ctr-stat', 'gsc-average-position-stat',
      'gsc-weekly-performance-trend', 'gsc-queries-table', 'gsc-pages-table', 'gsc-countries-table', 'gsc-devices-table',
      'gsc-weekly-table',
      'email-campaigns', 'email-total-campaigns-stat', 'email-delivery-rate-stat', 'email-bounce-rate-stat',
      'email-unsubscribe-rate-stat', 'email-subject-open-rate-chart',
      'linkedin-monthly-spend-trend', 'linkedin-campaign-details', 'linkedin-creative-details',
      'linkedin-organic-total-posts-stat', 'linkedin-organic-impressions-stat', 'linkedin-organic-clicks-stat',
      'linkedin-organic-engagements-stat', 'linkedin-organic-reactions-stat', 'linkedin-organic-comments-stat',
      'linkedin-organic-reposts-stat', 'linkedin-organic-avg-ctr-stat', 'linkedin-organic-avg-engagement-rate-stat',
      'linkedin-organic-followers-stat', 'linkedin-organic-impressions-vs-clicks', 'linkedin-organic-ctr-engagement',
      'linkedin-active-lead-forms-stat', 'linkedin-total-leads-captured-stat',
      'gads-impressions-stat', 'gads-clicks-stat', 'gads-total-cost-stat', 'gads-avg-ctr-stat', 'gads-avg-cpc-stat',
      'gads-conversions-stat', 'gads-conv-rate-stat', 'gads-cost-per-conv-stat', 'gads-ctr-trend',
      'gads-impressions-clicks-trend',
      'gads-campaign-performance-summary',
    ] as DashboardWidgetId[]);
    if (requiredTimeseriesKeys.length === 0 && derivedFromDashboard.has(widgetId)) {
      return;
    }
    if (isLeadTrendWidget) {
      setCrossPageLoading((prev) => ({
        ...prev,
        'bofu-monthly-trend': true,
        'qualified-monthly-trend': true,
      }));
    } else {
      setCrossPageLoading((prev) => ({ ...prev, [widgetId]: true }));
    }
    try {
      if (widgetId === 'traffic-source') {
        const result = await apiService.getGA4DimensionTimeseries(effectiveAccount, 'first_user_channel', activeRange.start, activeRange.end, 6);
        setCrossPageData((prev) => ({ ...prev, 'ga4-source-timeseries-raw': result }));
      } else if (widgetId === 'ga4-total-vs-organic-weekly') {
        const result = await apiService.getGA4WeeklyUserAcquisition(effectiveAccount, activeRange.end);
        setCrossPageData((prev) => ({ ...prev, 'ga4-total-vs-organic-weekly': result }));
      } else if (widgetId === 'ga4-fintrans-landing-page-views-by-country') {
        const result = await apiService.getGA4LandingPageViewsByCountry(
          effectiveAccount,
          activeRange.start,
          activeRange.end
        );
        setCrossPageData((prev) => ({ ...prev, 'ga4-fintrans-landing-page-views-by-country': result }));
      } else if (widgetId === 'ga4-paid-search-campaign-performance' || widgetId === 'ga4-paid-search-users-by-campaign') {
        const result = await apiService.getPaidSearchCampaigns(effectiveAccount, activeRange.start, activeRange.end);
        setCrossPageData((prev) => ({
          ...prev,
          'ga4-paid-search-campaign-performance': result,
          'ga4-paid-search-users-by-campaign': result,
        }));
      } else if (widgetId === 'geo-visitors-country') {
        const result = await apiService.getGA4DimensionTimeseries(effectiveAccount, 'country', activeRange.start, activeRange.end, 6);
        setCrossPageData((prev) => ({ ...prev, 'ga4-country-timeseries-raw': result }));
      } else if (widgetId === 'geo-visitors-city') {
        const result = await apiService.getGA4DimensionTimeseries(effectiveAccount, 'city', activeRange.start, activeRange.end, 6);
        setCrossPageData((prev) => ({ ...prev, 'ga4-city-timeseries-raw': result }));
      } else if (widgetId === 'pageviews' || widgetId === 'ga4-pageviews-by-path') {
        const result = await apiService.getGA4DimensionTimeseries(effectiveAccount, 'page', activeRange.start, activeRange.end, 6);
        setCrossPageData((prev) => ({ ...prev, 'ga4-page-timeseries-raw': result }));
      } else if (widgetId === 'ga4-traffic-by-device') {
        const result = await apiService.getGA4DimensionTimeseries(effectiveAccount, 'device', activeRange.start, activeRange.end, 6);
        setCrossPageData((prev) => ({ ...prev, 'ga4-device-timeseries-raw': result }));
      } else if (widgetId === 'gads-performance-by-device') {
        const result = await apiService.getGoogleAdsDeviceBreakdownTimeseries(effectiveAccount, activeRange.start, activeRange.end, 6);
        setCrossPageData((prev) => ({ ...prev, 'google-ads-device-timeseries-raw': result }));
      } else if (widgetId === 'gads-keywords') {
        const result = await apiService.getGoogleAdsKeywords(effectiveAccount, activeRange.start, activeRange.end, 25);
        setCrossPageData((prev) => ({ ...prev, 'gads-keywords': result }));
        setEndpointDiagnostics((prev) => ({
          ...prev,
          'gads-keywords': {
            key: 'gads-keywords',
            status: Array.isArray((result as Record<string, unknown>)?.data) && ((result as Record<string, unknown>).data as unknown[]).length > 0 ? 'loaded' : 'empty',
            message: `Google Ads keyword data ${Array.isArray((result as Record<string, unknown>)?.data) && ((result as Record<string, unknown>).data as unknown[]).length > 0 ? 'loaded' : 'not available'} for ${effectiveAccount}.`,
            userLabel: getDiagnosticLabel('gads-keywords'),
          },
        }));
      } else if (widgetId === 'email-campaigns') {
        const period = buildPeriod(activeRange.start, activeRange.end);
        const result = await apiService.getEmailMetricsPerformance(effectiveAccount, period, 1, 20);
        setCrossPageData((prev) => ({ ...prev, 'email-campaigns': result }));
        setEndpointDiagnostics((prev) => ({
          ...prev,
          'email-campaigns': {
            key: 'email-campaigns',
            status: safeNumber((result as Record<string, unknown>)?.metrics ? 1 : 0) > 0 ? 'loaded' : 'empty',
            message: `Email campaign data requested for ${effectiveAccount}.`,
            userLabel: getDiagnosticLabel('email-campaigns'),
          },
        }));
      } else if (widgetId === 'linkedin-lead-forms') {
        const result = await apiService.getStoredLinkedInLeadForms({
          account: effectiveAccount, start_date: activeRange.start, end_date: activeRange.end,
        });
        setCrossPageData((prev) => ({ ...prev, 'linkedin-lead-forms': result }));
        setEndpointDiagnostics((prev) => ({
          ...prev,
          'linkedin-lead-forms': {
            key: 'linkedin-lead-forms',
            status: Array.isArray(result) && result.length > 0 ? 'loaded' : 'empty',
            message: `LinkedIn lead form data ${Array.isArray(result) && result.length > 0 ? 'loaded' : 'not available'} for ${effectiveAccount}.`,
            userLabel: getDiagnosticLabel('linkedin-lead-forms'),
          },
        }));
      } else if (widgetId === 'bofu-monthly-trend' || widgetId === 'qualified-monthly-trend') {
        const monthWindowCount = Math.max(
          trendMonthSettings['bofu-monthly-trend'],
          trendMonthSettings['qualified-monthly-trend']
        );
        const monthWindows = buildLastNMonthWindows(monthWindowCount, activeRange.end);
        const monthKeys = new Set(monthWindows.map((window) => window.key));

        const [bofuLeads, qualifiedLeads] = await Promise.all([
          fetchAllLeadsByFilter(effectiveAccount, { stage: 'BOFU' }),
          fetchAllLeadsByFilter(effectiveAccount, { qualified: true }),
        ]);

        const bofuCountMap = new Map<string, number>();
        const qualifiedCountMap = new Map<string, number>();
        monthWindows.forEach((window) => {
          bofuCountMap.set(window.key, 0);
          qualifiedCountMap.set(window.key, 0);
        });

        bofuLeads.forEach((lead) => {
          const monthKey = normalizeMonthKey(lead.bofu_month)
            || monthKeyFromDateValue(lead.date);
          if (!monthKey || !monthKeys.has(monthKey)) return;
          bofuCountMap.set(monthKey, (bofuCountMap.get(monthKey) || 0) + 1);
        });

        qualifiedLeads.forEach((lead) => {
          const monthKey = normalizeMonthKey(lead.qualified_month)
            || monthKeyFromDateValue(lead.qualified_date)
            || monthKeyFromDateValue(lead.date);
          if (!monthKey || !monthKeys.has(monthKey)) return;
          qualifiedCountMap.set(monthKey, (qualifiedCountMap.get(monthKey) || 0) + 1);
        });

        const bofuTrendRows: MonthlyTrendRow[] = monthWindows.map((window) => ({
          monthKey: window.key,
          monthLabel: window.label,
          value: bofuCountMap.get(window.key) || 0,
        }));
        const qualifiedTrendRows: MonthlyTrendRow[] = monthWindows.map((window) => ({
          monthKey: window.key,
          monthLabel: window.label,
          value: qualifiedCountMap.get(window.key) || 0,
        }));

        setCrossPageData((prev) => ({
          ...prev,
          'bofu-monthly-trend': bofuTrendRows,
          'qualified-monthly-trend': qualifiedTrendRows,
        }));
        setEndpointDiagnostics((prev) => ({
          ...prev,
          'bofu-monthly-trend': {
            key: 'bofu-monthly-trend',
            status: bofuTrendRows.some((row) => row.value > 0) ? 'loaded' : 'empty',
            message: `BOFU trend ${bofuTrendRows.some((row) => row.value > 0) ? 'loaded' : 'has no records'} for ${effectiveAccount}.`,
            userLabel: getDiagnosticLabel('bofu-monthly-trend'),
          },
          'qualified-monthly-trend': {
            key: 'qualified-monthly-trend',
            status: qualifiedTrendRows.some((row) => row.value > 0) ? 'loaded' : 'empty',
            message: `Qualified trend ${qualifiedTrendRows.some((row) => row.value > 0) ? 'loaded' : 'has no records'} for ${effectiveAccount}.`,
            userLabel: getDiagnosticLabel('qualified-monthly-trend'),
          },
        }));
      } else if (widgetId === 'keyword-ranking-monthly') {
        const monthWindows = buildLastNMonthWindows(trendMonthSettings['keyword-ranking-monthly'], activeRange.end);
        const monthlyKeywordResponses = await Promise.all(
          monthWindows.map(async (window) => {
            const response = await apiService.getGoogleAdsKeywords(
              effectiveAccount,
              window.start,
              window.end,
              KEYWORD_MONTHLY_LIMIT
            );
            const rows = Array.isArray((response as Record<string, unknown>)?.data)
              ? ((response as Record<string, unknown>).data as Record<string, unknown>[])
              : [];
            return { window, rows };
          })
        );

        const aggregatedRows: MonthlyKeywordTrendRow[] = [];
        monthlyKeywordResponses.forEach(({ window, rows }) => {
          const byKeyword = new Map<string, MonthlyKeywordTrendRow>();
          rows.forEach((row) => {
            const keyword = String(row.keyword_text ?? row.keyword ?? '').trim();
            if (!keyword) return;
            const matchType = String(row.match_type ?? 'UNKNOWN').toUpperCase();
            const key = `${keyword}__${matchType}`;
            const impressions = safeNumber(row.impressions);
            const clicks = safeNumber(row.clicks);
            const conversions = safeNumber(row.conversions);

            const existing = byKeyword.get(key) || {
              monthKey: window.key,
              monthLabel: window.label,
              keyword,
              matchType,
              impressions: 0,
              clicks: 0,
              ctr: 0,
              conversions: 0,
            };

            existing.impressions += impressions;
            existing.clicks += clicks;
            existing.conversions += conversions;
            byKeyword.set(key, existing);
          });

          byKeyword.forEach((row) => {
            row.ctr = row.impressions > 0 ? (row.clicks / row.impressions) * 100 : 0;
            aggregatedRows.push(row);
          });
        });

        setCrossPageData((prev) => ({ ...prev, 'keyword-ranking-monthly': aggregatedRows }));
        setEndpointDiagnostics((prev) => ({
          ...prev,
          'keyword-ranking-monthly': {
            key: 'keyword-ranking-monthly',
            status: aggregatedRows.length > 0 ? 'loaded' : 'empty',
            message: `Keyword ranking trend ${aggregatedRows.length > 0 ? 'loaded' : 'has no records'} for ${effectiveAccount}.`,
            userLabel: getDiagnosticLabel('keyword-ranking-monthly'),
          },
        }));
      }
    } catch (err) {
      logger.error(`[Dashboard] Failed to load cross-page widget data for ${widgetId}`, err);
      const error = err as { response?: { status?: number; data?: { detail?: string } }; message?: string };
      const rawMessage = String(error?.response?.data?.detail || error?.message || 'Request failed');
      const unauthorized = error?.response?.status === 401
        || error?.response?.status === 403
        || rawMessage.toLowerCase().includes('permission')
        || rawMessage.toLowerCase().includes('access denied')
        || rawMessage.toLowerCase().includes('mapping');
      setEndpointDiagnostics((prev) => ({
        ...prev,
        [widgetId]: {
          key: widgetId,
          status: unauthorized ? 'unauthorized_or_mapping_missing' : 'error',
          message: rawMessage,
          userLabel: getDiagnosticLabel(widgetId),
        },
      }));
    } finally {
      if (isLeadTrendWidget) {
        setCrossPageLoading((prev) => ({
          ...prev,
          'bofu-monthly-trend': false,
          'qualified-monthly-trend': false,
        }));
      } else {
        setCrossPageLoading((prev) => ({ ...prev, [widgetId]: false }));
      }
    }
  }, [appliedFilters?.account, canonicalSelectedAccount, activeRange, crossPageData, crossPageLoading, trendMonthSettings, widgetRegistry]);
  loadCrossPageWidgetDataRef.current = loadCrossPageWidgetData;

  // Auto-load cross-page widget data when a widget is visible but its data hasn't
  // been fetched yet — happens when layout is restored from localStorage/server.
  useEffect(() => {
    if (
      !appliedFilters?.account
      || !canonicalSelectedAccount
      || !activeRange
      || layoutStorageAccount !== canonicalSelectedAccount
    ) return;
    const NEEDS_API_CALL: DashboardWidgetId[] = [
      'gads-keywords',
      'email-campaigns',
      'linkedin-lead-forms',
      'keyword-ranking-monthly',
    ];
    NEEDS_API_CALL.forEach((widgetId) => {
      if (
        !widgetLayouts[widgetId]?.hidden &&
        crossPageData[widgetId] === undefined &&
        !crossPageLoading[widgetId]
      ) {
        loadCrossPageWidgetData(widgetId);
      }
    });

    const leadTrendVisible = !widgetLayouts['bofu-monthly-trend']?.hidden || !widgetLayouts['qualified-monthly-trend']?.hidden;
    const leadTrendMissing = crossPageData['bofu-monthly-trend'] === undefined || crossPageData['qualified-monthly-trend'] === undefined;
    const leadTrendLoading = Boolean(crossPageLoading['bofu-monthly-trend'] || crossPageLoading['qualified-monthly-trend']);
    if (leadTrendVisible && leadTrendMissing && !leadTrendLoading) {
      loadCrossPageWidgetData('bofu-monthly-trend');
    }

    Array.from(BREAKDOWN_TIMESERIES_WIDGETS).forEach((widgetId) => {
      if (widgetLayouts[widgetId]?.hidden) {
        return;
      }

      const selectedBreakdown = getSelectedBreakdown(widgetId);
      if (selectedBreakdown === 'cumulative') {
        return;
      }

      if (!crossPageLoading[widgetId]) {
        loadCrossPageWidgetData(widgetId);
      }
    });
  }, [
    appliedFilters?.account,
    canonicalSelectedAccount,
    activeRange,
    layoutStorageAccount,
    widgetLayouts,
    crossPageData,
    crossPageLoading,
    loadCrossPageWidgetData,
    breakdownSelections,
    getSelectedBreakdown,
  ]);

  const getTableState = (widgetId: string) => ({
    page: tablePagination[widgetId]?.page ?? 0,
    rowsPerPage: tablePagination[widgetId]?.rowsPerPage ?? 5,
  });

  const setTablePage = (widgetId: string, page: number) =>
    setTablePagination((prev) => ({ ...prev, [widgetId]: { ...getTableState(widgetId), page } }));

  const setTableRowsPerPage = (widgetId: string, rowsPerPage: number) =>
    setTablePagination((prev) => ({ ...prev, [widgetId]: { page: 0, rowsPerPage } }));

  const setTrendMonths = (widgetId: TrendWidgetId, months: TrendMonthOption) => {
    setTrendMonthSettings((prev) => ({
      ...prev,
      [widgetId]: normalizeTrendMonthOption(months, DEFAULT_TREND_MONTHS[widgetId]),
    }));
  };

  const pageRows = <T,>(widgetId: string, rows: T[]): T[] => {
    const { page, rowsPerPage } = getTableState(widgetId);
    const totalPages = Math.max(1, Math.ceil(rows.length / rowsPerPage));
    const safePage = Math.min(page, totalPages - 1);
    return rows.slice(safePage * rowsPerPage, (safePage + 1) * rowsPerPage);
  };

  const renderTableFooter = (widgetId: string, totalRows: number, extraClassName?: string) => {
    const { page, rowsPerPage } = getTableState(widgetId);
    const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
    const safePage = Math.min(page, totalPages - 1);
    const from = totalRows === 0 ? 0 : safePage * rowsPerPage + 1;
    const to = Math.min((safePage + 1) * rowsPerPage, totalRows);
    const footerClassName = [
      'client-dashboard__table-footer',
      extraClassName || '',
    ].join(' ').trim();
    return (
      <div className={footerClassName}>
        <div className="client-dashboard__table-footer-left">
          <span className="client-dashboard__table-footer-label">Rows per page:</span>
          <select
            className="client-dashboard__table-rows-select"
            value={rowsPerPage}
            onChange={(e) => setTableRowsPerPage(widgetId, Number(e.target.value))}
          >
            {TABLE_PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="client-dashboard__table-footer-right">
          <span className="client-dashboard__table-footer-count">{from}–{to} of {totalRows}</span>
          <div className="client-dashboard__table-nav-group">
            <button
              type="button"
              className="client-dashboard__table-nav-btn"
              aria-label="Previous page"
              disabled={safePage === 0}
              onClick={() => setTablePage(widgetId, safePage - 1)}
            >
              &#8249;
            </button>
            <button
              type="button"
              className="client-dashboard__table-nav-btn"
              aria-label="Next page"
              disabled={safePage >= totalPages - 1}
              onClick={() => setTablePage(widgetId, safePage + 1)}
            >
              &#8250;
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderMaximizedPortal = () => {
    if (!maximizedWidgetId) {
      return null;
    }
    const widget = widgetRegistry.find((w) => w.id === maximizedWidgetId);
    if (!widget) {
      return null;
    }
    return createPortal(
      <>
        <button
          type="button"
          className="client-dashboard__widget-backdrop"
          aria-label="Close maximized widget"
          onClick={() => setMaximizedWidgetId(null)}
        />
        <section className="client-dashboard__widget is-maximized client-dashboard__maximized-context">
          <div className="client-dashboard__widget-header">
            <div className="client-dashboard__widget-title">{widget.title}</div>
            <div className="client-dashboard__widget-actions">
              {getAvailableBreakdowns(widget.id).length > 0 && (
                <select
                  className="client-dashboard__widget-control-select"
                  aria-label={`${widget.title} breakdown`}
                  value={getSelectedBreakdown(widget.id)}
                  onChange={(event) => setWidgetBreakdown(widget.id, event.target.value as BreakdownMode)}
                >
                  {getAvailableBreakdowns(widget.id).map((mode) => (
                    <option key={`${widget.id}-max-${mode}`} value={mode}>{BREAKDOWN_LABELS[mode]}</option>
                  ))}
                </select>
              )}
              <button
                type="button"
                className="client-dashboard__icon-button"
                title="Minimize widget"
                aria-label="Minimize widget"
                onClick={() => setMaximizedWidgetId(null)}
              >
                <CloseFullscreenIcon fontSize="small" />
              </button>
            </div>
          </div>
          <div className="client-dashboard__widget-body">
            {renderDashboardWidgetContent(maximizedWidgetId)}
          </div>
        </section>
      </>,
      document.body
    );
  };

  const closeGallery = () => {
    setShowWidgetGallery(false);
    setGallerySearch('');
    setGallerySelected(new Set());
  };

  const toggleGallerySelect = (widgetId: DashboardWidgetId) => {
    if (!canCustomizeDashboard) {
      return;
    }
    setGallerySelected((prev) => {
      const next = new Set(prev);
      if (next.has(widgetId)) { next.delete(widgetId); } else { next.add(widgetId); }
      return next;
    });
  };

  const addSelectedWidgets = () => {
    if (!canCustomizeDashboard) {
      return;
    }
    const selectedWidgetIds = Array.from(gallerySelected);
    selectedWidgetIds.forEach((widgetId) => handleWidgetRestore(widgetId));
    if (selectedWidgetIds.length > 0) {
      const message = selectedWidgetIds.length === 1
        ? `${getWidgetDefinition(selectedWidgetIds[0])?.title || 'Widget'} added to the dashboard.`
        : `${selectedWidgetIds.length} widgets added to the dashboard.`;
      setLayoutNotice({ open: true, message, removedWidgetId: null });
    }
    closeGallery();
  };

  const renderWidgetGallery = () => {
    if (!showWidgetGallery || !canCustomizeDashboard) {
      return null;
    }
    const searchLower = gallerySearch.trim().toLowerCase();
    const isSearching = searchLower !== '';

    // When searching: show all hidden widgets matching the query across all sources.
    // When not searching: show hidden widgets for the active tab only.
    const displayWidgets = widgetRegistry.filter((w) => {
      const isHidden = widgetLayouts[w.id]?.hidden;
      if (!isHidden) return false;
      if (w.enabled === false || w.deprecated) return false;
      if (isSearching) {
        return w.title.toLowerCase().includes(searchLower) ||
          w.description.toLowerCase().includes(searchLower) ||
          w.source.toLowerCase().includes(searchLower);
      }
      return w.source === galleryTab;
    });

    return createPortal(
      <>
        <button
          type="button"
          className="client-dashboard__widget-backdrop"
          aria-label="Close widget gallery"
          onClick={closeGallery}
        />
        <div className="client-dashboard__gallery-modal" role="dialog" aria-label="Widget gallery">
          <div className="client-dashboard__gallery-header">
            <span className="client-dashboard__gallery-title">Add Widget</span>
            <button type="button" className="client-dashboard__icon-button" aria-label="Close gallery" onClick={closeGallery}>
              <CloseIcon fontSize="small" />
            </button>
          </div>

          <div className="client-dashboard__gallery-search-row">
            <SearchIcon fontSize="small" className="client-dashboard__gallery-search-icon" />
            <input
              type="search"
              className="client-dashboard__gallery-search"
              placeholder="Search by name or source (e.g. 'sent', 'linkedin', 'clicks')…"
              value={gallerySearch}
              onChange={(e) => setGallerySearch(e.target.value)}
              autoFocus
            />
          </div>

          {!isSearching && (
            <div className="client-dashboard__gallery-tabs">
              {GALLERY_TABS.map((tab) => {
                const count = widgetRegistry.filter((w) => w.source === tab.key && widgetLayouts[w.id]?.hidden && w.enabled !== false && !w.deprecated).length;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    className={`client-dashboard__gallery-tab ${galleryTab === tab.key ? 'active' : ''}`}
                    onClick={() => setGalleryTab(tab.key)}
                  >
                    {tab.label}{count > 0 ? ` (${count})` : ''}
                  </button>
                );
              })}
            </div>
          )}

          <div className="client-dashboard__gallery-body">
            {displayWidgets.length === 0 ? (
              <div className="client-dashboard__gallery-empty">
                {isSearching
                  ? `No available widgets match "${gallerySearch}".`
                  : 'All widgets from this source are already active on your dashboard.'}
              </div>
            ) : (
              <div className="client-dashboard__gallery-grid">
                {displayWidgets.map((widget) => {
                  const isSelected = gallerySelected.has(widget.id);
                  return (
                    <label
                      key={widget.id}
                      className={`client-dashboard__gallery-card${isSelected ? ' is-selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        className="client-dashboard__gallery-checkbox"
                        checked={isSelected}
                        onChange={() => toggleGallerySelect(widget.id)}
                      />
                      <div className={`client-dashboard__gallery-badge client-dashboard__gallery-badge--${widget.source}`}>
                        {GALLERY_TABS.find((t) => t.key === widget.source)?.label ?? widget.source}
                      </div>
                      <div className="client-dashboard__gallery-card-title">{widget.title}</div>
                      <div className="client-dashboard__gallery-card-desc">{widget.description}</div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="client-dashboard__gallery-footer">
            <span className="client-dashboard__gallery-count">
              {gallerySelected.size > 0 ? `${gallerySelected.size} selected` : `${displayWidgets.length} available`}
            </span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" className="client-dashboard__reset-layout" onClick={closeGallery}>
                Cancel
              </button>
              {gallerySelected.size > 0 ? (
                <button type="button" className="client-dashboard__gallery-add-btn" onClick={addSelectedWidgets}>
                  Add {gallerySelected.size} widget{gallerySelected.size !== 1 ? 's' : ''}
                </button>
              ) : (
                <button
                  type="button"
                  className="client-dashboard__gallery-add-btn"
                  disabled={displayWidgets.length === 0}
                  onClick={() => {
                    displayWidgets.forEach((w) => toggleGallerySelect(w.id));
                  }}
                >
                  Select all
                </button>
              )}
            </div>
          </div>
        </div>
      </>,
      document.body
    );
  };

  const openChartEditor = (widgetId: DashboardWidgetId) => {
    if (!canCustomizeDashboard) {
      return;
    }
    const current = widgetLayouts[widgetId]?.visualizationConfig;
    setDraftVizConfig(current ?? { chartType: 'table', horizontal: false });
    setEditingChartWidgetId(widgetId);
  };

  const applyChartConfig = () => {
    if (!canCustomizeDashboard) return;
    if (!editingChartWidgetId) return;
    setWidgetLayouts((prev) => ({
      ...prev,
      [editingChartWidgetId]: {
        ...prev[editingChartWidgetId],
        visualizationConfig: { ...draftVizConfig },
      },
    }));
    setEditingChartWidgetId(null);
  };

  const renderChartEditorPanel = () => {
    if (!editingChartWidgetId) return null;
    const widget = widgetRegistry.find((w) => w.id === editingChartWidgetId);
    if (!widget) return null;

    const CHART_TYPE_OPTIONS: { value: ChartType; label: string }[] = [
      { value: 'table', label: 'Table (default)' },
      { value: 'bar', label: 'Bar Chart' },
      { value: 'line', label: 'Line Chart' },
      { value: 'pie', label: 'Pie Chart' },
    ];
    const supportedChartTypes = new Set(widget.allowedChartTypes || ['table']);
    const visibleChartTypeOptions = CHART_TYPE_OPTIONS.filter((opt) => supportedChartTypes.has(opt.value));

    return createPortal(
      <>
        <button
          type="button"
          className="client-dashboard__widget-backdrop"
          aria-label="Close chart editor"
          onClick={() => setEditingChartWidgetId(null)}
        />
        <div className="client-dashboard__chart-editor" role="dialog" aria-label="Chart editor">
          <div className="client-dashboard__chart-editor-header">
            <span className="client-dashboard__chart-editor-title">Chart Settings — {widget.title}</span>
            <button
              type="button"
              className="client-dashboard__icon-button"
              aria-label="Close"
              onClick={() => setEditingChartWidgetId(null)}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
          <div className="client-dashboard__chart-editor-body">
            <fieldset className="client-dashboard__chart-editor-group">
              <legend>Visualization type</legend>
              {visibleChartTypeOptions.map((opt) => (
                <label key={opt.value} className="client-dashboard__chart-editor-option">
                  <input
                    type="radio"
                    name="chartType"
                    value={opt.value}
                    checked={draftVizConfig.chartType === opt.value}
                    onChange={() => setDraftVizConfig((prev) => ({ ...prev, chartType: opt.value }))}
                  />
                  {opt.label}
                </label>
              ))}
            </fieldset>
            {(draftVizConfig.chartType === 'bar') && (
              <fieldset className="client-dashboard__chart-editor-group">
                <legend>Orientation</legend>
                <label className="client-dashboard__chart-editor-option">
                  <input
                    type="radio"
                    name="horizontal"
                    checked={!draftVizConfig.horizontal}
                    onChange={() => setDraftVizConfig((prev) => ({ ...prev, horizontal: false }))}
                  />
                  Vertical
                </label>
                <label className="client-dashboard__chart-editor-option">
                  <input
                    type="radio"
                    name="horizontal"
                    checked={draftVizConfig.horizontal}
                    onChange={() => setDraftVizConfig((prev) => ({ ...prev, horizontal: true }))}
                  />
                  Horizontal
                </label>
              </fieldset>
            )}
          </div>
          <div className="client-dashboard__chart-editor-footer">
            <button
              type="button"
              className="client-dashboard__reset-layout"
              onClick={() => setEditingChartWidgetId(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="wizrdom-button small"
              onClick={applyChartConfig}
            >
              Apply
            </button>
          </div>
        </div>
      </>,
      document.body
    );
  };

  const renderWidgetFrame = (
    widget: DashboardWidgetDefinition,
    content: React.ReactNode
  ) => {
    const layout = widgetLayouts[widget.id] || buildDefaultWidgetLayouts()[widget.id];
    const isStatWidget = SINGLE_STAT_WIDGET_IDS.has(widget.id);

    const isDragging = draggedWidgetId === widget.id;
    const isDragOver = dragOverWidgetId === widget.id && draggedWidgetId !== widget.id;
    const availableBreakdowns = getAvailableBreakdowns(widget.id);

    return (
      <section
        key={widget.id}
        draggable={canCustomizeDashboard}
        className={`client-dashboard__widget${isStatWidget ? ' client-dashboard__widget--stat' : ''}${isDragging ? ' is-dragging' : ''}${isDragOver ? ' is-drag-over' : ''}`}
        style={{
          gridColumn: `span ${layout.colSpan}`,
          minHeight: layout.height,
        }}
        onDragStart={() => {
          if (!canCustomizeDashboard) return;
          setDraggedWidgetId(widget.id);
        }}
        onDragEnd={() => { setDraggedWidgetId(null); setDragOverWidgetId(null); }}
        onDragOver={(e) => {
          if (!canCustomizeDashboard) return;
          e.preventDefault();
          setDragOverWidgetId(widget.id);
        }}
        onDragLeave={() => setDragOverWidgetId(null)}
        onDrop={(e) => {
          if (!canCustomizeDashboard) return;
          e.preventDefault();
          handleWidgetDrop(widget.id);
        }}
      >
        <div className="client-dashboard__widget-header">
          <div className="client-dashboard__widget-title">{widget.title}</div>
          <div className="client-dashboard__widget-actions">
            {availableBreakdowns.length > 0 && (
              <select
                className="client-dashboard__widget-control-select"
                aria-label={`${widget.title} breakdown`}
                value={getSelectedBreakdown(widget.id)}
                onChange={(event) => setWidgetBreakdown(widget.id, event.target.value as BreakdownMode)}
              >
                {availableBreakdowns.map((mode) => (
                  <option key={`${widget.id}-${mode}`} value={mode}>{BREAKDOWN_LABELS[mode]}</option>
                ))}
              </select>
            )}
            {canCustomizeDashboard && CHART_CAPABLE_WIDGET_IDS.has(widget.id) && (
              <button
                type="button"
                className="client-dashboard__icon-button"
                title="Change chart type"
                aria-label="Change chart type"
                onClick={() => openChartEditor(widget.id)}
              >
                <BarChartIcon fontSize="small" />
              </button>
            )}
            <button
              type="button"
              className="client-dashboard__icon-button"
              title="Maximize widget"
              aria-label="Maximize widget"
              onClick={() => setMaximizedWidgetId(widget.id)}
            >
              <CropSquareIcon fontSize="small" />
            </button>
            {canCustomizeDashboard && (
              <button
                type="button"
                className="client-dashboard__icon-button"
                title="Remove widget"
                aria-label="Remove widget"
                onClick={() => handleWidgetRemove(widget.id)}
              >
                <CloseIcon fontSize="small" />
              </button>
            )}
          </div>
        </div>
        <div className="client-dashboard__widget-body">
          {content}
        </div>
        {canCustomizeDashboard && (['n', 's', 'e', 'w'] as ResizeDirection[]).map((direction) => (
          <button
            key={direction}
            type="button"
            draggable={false}
            className={`client-dashboard__resize-handle ${direction}`}
            aria-label={`Resize widget ${direction}`}
            onDragStart={(e) => e.stopPropagation()}
            onPointerDown={(event) => {
              event.stopPropagation();
              handleResizeStart(event, widget.id, direction);
            }}
          />
        ))}
      </section>
    );
  };

  const getWidgetLoadingKeys = (widgetId: DashboardWidgetId): string[] => {
    if (['geo-visitors-country'].includes(widgetId)) return ['ga4-country'];
    if (['geo-visitors-city'].includes(widgetId)) return ['ga4-city'];
    if (['pageviews', 'ga4-pageviews-by-path', 'ga4-pages-screens'].includes(widgetId)) return ['ga4-page'];
    if (widgetId === 'ga4-fintrans-landing-page-views-by-country') return ['ga4-landing-page-country-views'];
    if (['traffic-source'].includes(widgetId)) return ['ga4-first-channel'];
    if (['ga4-traffic-by-device'].includes(widgetId)) return ['ga4-device'];
    if (widgetId === 'ga4-total-vs-organic-weekly') return ['ga4-weekly-user-acquisition'];
    if (widgetId === 'ga4-paid-search-campaign-performance' || widgetId === 'ga4-paid-search-users-by-campaign') {
      return ['ga4-paid-search-campaigns'];
    }
    if (['ga4-seo-weekly-stats'].includes(widgetId)) return ['ga4-weekly'];
    if (['ga4-regions'].includes(widgetId)) return ['ga4-country', 'ga4-city'];
    if ([
      'ga4-users-stat', 'ga4-sessions-stat', 'ga4-engagement-stat',
      'ga4-users-total-stat', 'ga4-new-users-stat', 'ga4-sessions-total-stat',
      'ga4-pageviews-total-stat', 'ga4-pageviews-per-user-stat', 'ga4-avg-time-per-page-stat',
      'ga4-percent-users-stat', 'ga4-insights-aggregated',
    ].includes(widgetId)) return ['ga4-summary'];

    if (['keywords', 'gsc-queries-table'].includes(widgetId)) return ['search-console-query'];
    if (widgetId === 'gsc-pages-table') return ['search-console-page'];
    if (widgetId === 'gsc-countries-table') return ['search-console-country'];
    if (widgetId === 'gsc-devices-table') return ['search-console-device'];
    if (['gsc-weekly-performance-trend', 'gsc-weekly-table'].includes(widgetId)) return ['search-console-weekly'];
    if ([
      'gsc-total-clicks-stat', 'gsc-total-impressions-stat',
      'gsc-average-ctr-stat', 'gsc-average-position-stat',
    ].includes(widgetId)) return ['search-console-summary'];

    if (widgetId === 'gads-performance-by-device') return ['google-ads-device'];
    if (widgetId.startsWith('gads-') || widgetId === 'keyword-ranking-monthly') return ['google-ads-current'];

    if (widgetId === 'linkedin-lead-forms' || widgetId === 'linkedin-active-lead-forms-stat') return ['linkedin-lead-forms'];
    if (widgetId === 'linkedin-total-leads-captured-stat') return ['linkedin-lead-responses'];
    if (widgetId === 'linkedin-campaign-details' || widgetId === 'linkedin-creative-details') return ['linkedin-campaign-details'];
    if (widgetId.includes('linkedin-organic') || widgetId === 'social-posts') return ['linkedin-organic'];
    if (widgetId === 'linkedin-follower-stats' || widgetId === 'email-followers') return ['linkedin-accounts'];
    if (widgetId.startsWith('linkedin-')) return ['linkedin-current'];

    if (widgetId.startsWith('email-')) return ['email-current'];
    if (widgetId === 'leads-table' || widgetId === 'leads-stat') return ['leads-current'];

    return [];
  };

  const renderDashboardWidgetContent = (widgetId: DashboardWidgetId) => {
    if (!dashboardData) {
      return null;
    }
    const loadingKeys = getWidgetLoadingKeys(widgetId);
    if (loadingKeys.some((key) => endpointDiagnostics[key]?.status === 'loading')) {
      const widget = getWidgetDefinition(widgetId);
      return <LoadingSpinner message={`Loading ${widget?.title || 'widget data'}...`} />;
    }
    const isMaximizedView = maximizedWidgetId === widgetId;
    const widgetColSpan = widgetLayouts[widgetId]?.colSpan ?? 2;
    const estimatedChartWidth = isMaximizedView
      ? 1120
      : widgetColSpan >= 4
        ? 980
        : widgetColSpan === 3
          ? 760
          : widgetColSpan === 2
            ? 560
            : 340;
    const isCompactChart = !isMaximizedView && widgetColSpan <= 2;
    const pieLabelDensity = isMaximizedView
      ? { maxLabels: 12, minPercentage: 0.03, insideThreshold: 0.1, outerRadius: '78%' as const }
      : { maxLabels: 8, minPercentage: 0.08, insideThreshold: 0.13, outerRadius: '72%' as const };
    const createDashboardBarLabels = ({
      rowCount,
      seriesCount = 1,
      formatValue,
      orientation = 'vertical',
      minValue = 0,
      stacked = false,
      showAllLabels = false,
    }: {
      rowCount: number;
      seriesCount?: number;
      formatValue: (value: number, index?: number) => string;
      orientation?: 'vertical' | 'horizontal';
      minValue?: number;
      stacked?: boolean;
      showAllLabels?: boolean;
    }) => {
      const density = getDenseChartConfig(rowCount, seriesCount, isMaximizedView);
      return createBarLabelRenderer({
        dataLength: rowCount * Math.max(seriesCount, 1),
        formatValue,
        stacked,
        orientation,
        maxLabels: showAllLabels ? rowCount * Math.max(seriesCount, 1) : density.maxSeriesLabels,
        minValue,
        containerWidth: estimatedChartWidth,
        minLabelSpacing: orientation === 'horizontal'
          ? (isMaximizedView ? 88 : 108)
          : (isCompactChart ? 124 : isMaximizedView ? 74 : 96),
        denseThreshold: isMaximizedView ? 18 : 10,
        minBarWidthForLabel: showAllLabels
          ? 0
          : orientation === 'horizontal'
            ? (isMaximizedView ? 42 : 52)
            : (seriesCount > 1 ? (isMaximizedView ? 10 : 14) : (isMaximizedView ? 8 : 10)),
        minBarHeightForLabel: showAllLabels
          ? 0
          : orientation === 'horizontal'
            ? (isCompactChart ? 14 : 12)
            : (isCompactChart ? 26 : 18),
        minInsideBarWidthForLabel: isMaximizedView ? 68 : 84,
        fontSize: isCompactChart ? 9 : 10,
      });
    };
    const createDashboardLineLabels = ({
      rowCount,
      seriesCount = 1,
      formatValue,
      minValue = 0,
      offsetY,
    }: {
      rowCount: number;
      seriesCount?: number;
      formatValue: (value: number, index?: number) => string;
      minValue?: number;
      offsetY?: number;
    }) => {
      const density = getDenseChartConfig(rowCount, seriesCount, isMaximizedView);
      return createLineLabelRenderer({
        dataLength: rowCount * Math.max(seriesCount, 1),
        formatValue,
        maxLabels: density.maxSeriesLabels,
        minValue,
        containerWidth: estimatedChartWidth,
        minLabelSpacing: isCompactChart ? 124 : isMaximizedView ? 76 : 96,
        denseThreshold: isMaximizedView ? 18 : 10,
        offsetY: offsetY ?? density.lineOffsetY,
        fontSize: isCompactChart ? 9 : 10,
      });
    };
    const createAlwaysVisibleBarLabels = ({
      formatValue,
      orientation = 'vertical',
    }: {
      formatValue: (value: number, index?: number) => string;
      orientation?: 'vertical' | 'horizontal';
    }) => {
      const fontSize = isCompactChart ? 9 : 10;

      return (props: any) => {
        const { x = 0, y = 0, width = 0, height = 0, value, index = 0 } = props;
        if (value === null || value === undefined || Math.abs(Number(value)) <= 0) {
          return null;
        }

        const labelValue = formatValue(Number(value), index);
        const textX = orientation === 'horizontal'
          ? x + width + 6
          : x + width / 2;
        const textY = orientation === 'horizontal'
          ? y + height / 2
          : Math.max(12, y - 6);

        return (
          <text
            x={textX}
            y={textY}
            fill="#14385d"
            stroke="#f8fafc"
            strokeWidth={3}
            paintOrder="stroke"
            fontSize={fontSize}
            fontWeight={700}
            textAnchor={orientation === 'horizontal' ? 'start' : 'middle'}
            dominantBaseline={orientation === 'horizontal' ? 'middle' : 'auto'}
          >
            {labelValue}
          </text>
        );
      };
    };
    const ga4SummaryRaw = crossPageData['ga4-summary-raw'] as Record<string, unknown> | undefined;
    const ga4SummaryTotalsRaw = ((ga4SummaryRaw?.totals as Record<string, unknown>) || ga4SummaryRaw || {});
    const ga4PageRowsRaw = Array.isArray((crossPageData['ga4-page-raw'] as Record<string, unknown> | undefined)?.data)
      ? ((crossPageData['ga4-page-raw'] as Record<string, unknown>).data as Record<string, unknown>[])
      : [];
    const ga4LandingPageCountryResponse = crossPageData['ga4-fintrans-landing-page-views-by-country'] as Record<string, unknown> | undefined;
    const ga4LandingPageCountryRows = Array.isArray(ga4LandingPageCountryResponse?.data)
      ? ga4LandingPageCountryResponse.data as Record<string, unknown>[]
      : [];
    const ga4CountryRowsRaw = Array.isArray((crossPageData['ga4-country-raw'] as Record<string, unknown> | undefined)?.data)
      ? ((crossPageData['ga4-country-raw'] as Record<string, unknown>).data as Record<string, unknown>[])
      : [];
    const ga4CityRowsRaw = Array.isArray((crossPageData['ga4-city-raw'] as Record<string, unknown> | undefined)?.data)
      ? ((crossPageData['ga4-city-raw'] as Record<string, unknown>).data as Record<string, unknown>[])
      : [];
    const ga4DeviceRowsRaw = Array.isArray((crossPageData['ga4-device-raw'] as Record<string, unknown> | undefined)?.data)
      ? ((crossPageData['ga4-device-raw'] as Record<string, unknown>).data as Record<string, unknown>[])
      : [];
    const ga4WeeklyRowsRaw = Array.isArray((crossPageData['ga4-weekly-raw'] as Record<string, unknown> | undefined)?.data)
      ? ((crossPageData['ga4-weekly-raw'] as Record<string, unknown>).data as Record<string, unknown>[])
      : [];
    const ga4WeeklyUserAcquisitionRows = Array.isArray((crossPageData['ga4-total-vs-organic-weekly'] as Record<string, unknown> | undefined)?.data)
      ? ((crossPageData['ga4-total-vs-organic-weekly'] as Record<string, unknown>).data as Record<string, unknown>[])
      : [];
    const paidSearchCampaignResponse = (
      crossPageData['ga4-paid-search-campaign-performance']
      || crossPageData['ga4-paid-search-users-by-campaign']
    ) as PaidSearchCampaignResponse | undefined;
    const paidSearchCampaignRows = Array.isArray(paidSearchCampaignResponse?.data)
      ? paidSearchCampaignResponse.data
      : [];
    const paidSearchUnattributed = paidSearchCampaignResponse?.unattributed ?? null;
    const gscSummaryRaw = crossPageData['gsc-summary-raw'] as Record<string, unknown> | undefined;
    const gscSummaryMetricsRaw = (
      (gscSummaryRaw?.totals as Record<string, unknown>) ||
      (gscSummaryRaw?.summary as Record<string, unknown>) ||
      gscSummaryRaw ||
      {}
    );
    const gscSummaryMetrics = {
      clicks: safeNumber(gscSummaryMetricsRaw.clicks ?? gscSummaryMetricsRaw.totalClicks),
      impressions: safeNumber(gscSummaryMetricsRaw.impressions ?? gscSummaryMetricsRaw.totalImpressions),
      ctr: normalizePercentValue(gscSummaryMetricsRaw.ctr ?? gscSummaryMetricsRaw.avgCtr),
      position: safeNumber(gscSummaryMetricsRaw.position ?? gscSummaryMetricsRaw.avgPosition),
    };
    const gscQueryRows = Array.isArray((crossPageData['gsc-query-raw'] as Record<string, unknown> | undefined)?.data)
      ? ((crossPageData['gsc-query-raw'] as Record<string, unknown>).data as Record<string, unknown>[])
      : [];
    const gscPageRows = Array.isArray((crossPageData['gsc-page-raw'] as Record<string, unknown> | undefined)?.data)
      ? ((crossPageData['gsc-page-raw'] as Record<string, unknown>).data as Record<string, unknown>[])
      : [];
    const gscCountryRows = Array.isArray((crossPageData['gsc-country-raw'] as Record<string, unknown> | undefined)?.data)
      ? ((crossPageData['gsc-country-raw'] as Record<string, unknown>).data as Record<string, unknown>[])
      : [];
    const gscDeviceRows = Array.isArray((crossPageData['gsc-device-raw'] as Record<string, unknown> | undefined)?.data)
      ? ((crossPageData['gsc-device-raw'] as Record<string, unknown>).data as Record<string, unknown>[])
      : [];
    const gscWeeklyRows = Array.isArray((crossPageData['gsc-weekly-raw'] as Record<string, unknown> | undefined)?.data)
      ? ((crossPageData['gsc-weekly-raw'] as Record<string, unknown>).data as Record<string, unknown>[])
      : [];
    const emailCurrentRaw = crossPageData['email-current-raw'] as Record<string, unknown> | undefined;
    const emailSummaryMetrics = ((emailCurrentRaw?.metrics as Record<string, unknown>) || emailCurrentRaw || {});
    const emailCampaignRows = Array.isArray((crossPageData['email-campaigns'] as Record<string, unknown> | undefined)?.campaigns)
      ? ((crossPageData['email-campaigns'] as Record<string, unknown>).campaigns as Record<string, unknown>[])
      : Array.isArray((crossPageData['email-campaigns'] as Record<string, unknown> | undefined)?.data)
        ? ((crossPageData['email-campaigns'] as Record<string, unknown>).data as Record<string, unknown>[])
        : [];
    const linkedInDetailedMetrics = Array.isArray((crossPageData['linkedin-campaign-details-raw'] as Record<string, unknown> | undefined)?.campaign_metrics)
      ? ((crossPageData['linkedin-campaign-details-raw'] as Record<string, unknown>).campaign_metrics as Record<string, unknown>[])
      : [];
    const linkedInOrganicRaw = crossPageData['linkedin-organic-posts'] as Record<string, unknown> | undefined;
    const linkedInOrganicPosts = Array.isArray(linkedInOrganicRaw?.organic_posts)
      ? linkedInOrganicRaw.organic_posts as Record<string, unknown>[]
      : Array.isArray(linkedInOrganicRaw?.posts)
        ? linkedInOrganicRaw.posts as Record<string, unknown>[]
      : Array.isArray(linkedInOrganicRaw)
        ? linkedInOrganicRaw as unknown as Record<string, unknown>[]
        : [];
    // Server-side aggregate over ALL posts matching the query (no pagination limit).
    // Use this for stat cards so totals are accurate even when total_records > page_size.
    const linkedInOrganicSummary = ((linkedInOrganicRaw?.summary as Record<string, unknown> | undefined)
      ?.current) as Record<string, unknown> | undefined;
    const linkedInFollowerRaw = crossPageData['linkedin-follower-stats-raw'] as Record<string, unknown> | null | undefined;
    const linkedInFollowerCurrent = (((linkedInFollowerRaw?.current as Record<string, unknown>) || linkedInFollowerRaw || {}) as Record<string, unknown>);
    const linkedInLeadFormsRaw = crossPageData['linkedin-lead-forms'] as Record<string, unknown> | undefined;
    const linkedInLeadFormRows = Array.isArray(linkedInLeadFormsRaw?.lead_forms)
      ? linkedInLeadFormsRaw.lead_forms as Record<string, unknown>[]
      : Array.isArray(linkedInLeadFormsRaw)
        ? linkedInLeadFormsRaw as unknown as Record<string, unknown>[]
        : [];
    const linkedInLeadResponsesRaw = crossPageData['linkedin-lead-responses-raw'] as Record<string, unknown> | undefined;
    const linkedInLeadResponseRows = Array.isArray(linkedInLeadResponsesRaw?.lead_form_responses)
      ? linkedInLeadResponsesRaw.lead_form_responses as Record<string, unknown>[]
      : Array.isArray(linkedInLeadResponsesRaw?.lead_responses)
        ? linkedInLeadResponsesRaw.lead_responses as Record<string, unknown>[]
        : Array.isArray(linkedInLeadResponsesRaw?.responses)
          ? linkedInLeadResponsesRaw.responses as Record<string, unknown>[]
          : Array.isArray(linkedInLeadResponsesRaw)
            ? linkedInLeadResponsesRaw as unknown as Record<string, unknown>[]
            : [];
    const googleAdsMetricsRaw = Array.isArray((crossPageData['google-ads-metrics-raw'] as Record<string, unknown> | undefined)?.metrics)
      ? ((crossPageData['google-ads-metrics-raw'] as Record<string, unknown>).metrics as Record<string, unknown>[])
      : [];
    const googleAdsDeviceRows = Array.isArray((crossPageData['google-ads-device-raw'] as Record<string, unknown> | undefined)?.data)
      ? ((crossPageData['google-ads-device-raw'] as Record<string, unknown>).data as Record<string, unknown>[])
      : [];
    const ga4PageTimeseriesRowsRaw = Array.isArray((crossPageData['ga4-page-timeseries-raw'] as Record<string, unknown> | undefined)?.data)
      ? ((crossPageData['ga4-page-timeseries-raw'] as Record<string, unknown>).data as Record<string, unknown>[])
      : [];
    const ga4SourceTimeseriesRowsRaw = Array.isArray((crossPageData['ga4-source-timeseries-raw'] as Record<string, unknown> | undefined)?.data)
      ? ((crossPageData['ga4-source-timeseries-raw'] as Record<string, unknown>).data as Record<string, unknown>[])
      : [];
    const ga4CountryTimeseriesRowsRaw = Array.isArray((crossPageData['ga4-country-timeseries-raw'] as Record<string, unknown> | undefined)?.data)
      ? ((crossPageData['ga4-country-timeseries-raw'] as Record<string, unknown>).data as Record<string, unknown>[])
      : [];
    const ga4CityTimeseriesRowsRaw = Array.isArray((crossPageData['ga4-city-timeseries-raw'] as Record<string, unknown> | undefined)?.data)
      ? ((crossPageData['ga4-city-timeseries-raw'] as Record<string, unknown>).data as Record<string, unknown>[])
      : [];
    const ga4DeviceTimeseriesRowsRaw = Array.isArray((crossPageData['ga4-device-timeseries-raw'] as Record<string, unknown> | undefined)?.data)
      ? ((crossPageData['ga4-device-timeseries-raw'] as Record<string, unknown>).data as Record<string, unknown>[])
      : [];
    const googleAdsDeviceTimeseriesRowsRaw = Array.isArray((crossPageData['google-ads-device-timeseries-raw'] as Record<string, unknown> | undefined)?.data)
      ? ((crossPageData['google-ads-device-timeseries-raw'] as Record<string, unknown>).data as Record<string, unknown>[])
      : [];
    const renderSingleStatCard = (_label: string, value: string, subtext?: string | null) => (
      <div className="client-dashboard__stat-widget">
        <div className="client-dashboard__stat-value">{value}</div>
        {subtext ? <div className="client-dashboard__stat-subtext">{subtext}</div> : null}
      </div>
    );
    const renderGenericTable = (
      tableId: string,
      headers: React.ReactNode[],
      rows: React.ReactNode[][],
      emptyMessage: string,
      extraClassName?: string
    ) => (
      <>
        <div className={`table-container client-dashboard__table-container--paged ${extraClassName || ''}`.trim()}>
          <table className="data-table">
            <thead>
              <tr>{headers.map((header, index) => <th key={`${tableId}-header-${index}`}>{header}</th>)}</tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={headers.length}>{emptyMessage}</td></tr>
              ) : (
                pageRows(tableId, rows).map((cells, rowIndex) => (
                  <tr key={`${tableId}-row-${rowIndex}`}>
                    {cells.map((cell, cellIndex) => <td key={`${tableId}-cell-${rowIndex}-${cellIndex}`}>{cell}</td>)}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {renderTableFooter(tableId, rows.length)}
      </>
    );
    const renderUnavailableStatCard = (label: string, detail: string) => (
      <div className="client-dashboard__single-stat">
        <div className="client-dashboard__single-stat-label">{label}</div>
        <div className="client-dashboard__single-stat-value">N/A</div>
        <div className="client-dashboard__single-stat-subtext">{detail}</div>
      </div>
    );
    const getRowDateValue = (row: Record<string, unknown>, ...keys: string[]): string | null => {
      const matchedKey = keys.find((key) => {
        const value = row[key];
        return typeof value === 'string' && value.trim().length >= 8;
      });
      if (!matchedKey) {
        return null;
      }
      return String(row[matchedKey]).trim().slice(0, 10);
    };
    const renderBreakdownSeriesContent = (
      tableId: string,
      chartType: ChartType,
      horizontal: boolean,
      chartRows: TimeBucketSeriesRow[],
      seriesKeys: string[],
      emptyMessage: string,
      valueFormatter: (value: number) => string,
      yAxisFormatter?: (value: number) => string,
      legendPrefix?: string,
      labelOptions?: { showAllLabels?: boolean }
    ) => {
      if (chartRows.length === 0 || seriesKeys.length === 0) {
        return <div className="client-dashboard__empty">{emptyMessage}</div>;
      }
      if (chartType === 'table') {
        return renderGenericTable(
          tableId,
          ['Period', ...seriesKeys],
          chartRows.map((row) => [
            String(row.bucketLabel),
            ...seriesKeys.map((seriesKey) => valueFormatter(safeNumber(row[seriesKey]))),
          ]),
          emptyMessage
        );
      }

      const isHorizontal = horizontal && chartType === 'bar';
      const densityConfig = getDenseChartConfig(chartRows.length, seriesKeys.length, isMaximizedView);
      const xAxisAngle = chartRows.length > 18 ? -40 : chartRows.length > 10 ? -32 : -25;
      const xAxisHeight = isMaximizedView
        ? (chartRows.length > 18 ? 72 : 58)
        : (chartRows.length > 18 ? 82 : 66);
      const showLegend = isMaximizedView || chartRows.length <= 18 || seriesKeys.length <= 2;
      const legendNode = showLegend
        ? <Legend verticalAlign="top" height={28} wrapperStyle={{ fontSize: '11px', paddingBottom: 4 }} />
        : null;
      const pieData = buildCompactPieSeries(
        chartRows.map((row) => ({
          name: String(row.bucketLabel),
          value: seriesKeys.reduce((sum, seriesKey) => sum + safeNumber(row[seriesKey]), 0),
        })),
        isMaximizedView ? 12 : 8
      );
      const pieLabel = createPieLabelRenderer({
        dataLength: pieData.length,
        maxLabels: pieLabelDensity.maxLabels,
        formatValue: (_value, _payload, percent) => `${Math.round((percent ?? 0) * 100)}%`,
        insideThreshold: pieLabelDensity.insideThreshold,
        minPercentage: pieLabelDensity.minPercentage,
      });
      const pieLabelLine = createPieLabelLineRenderer({
        dataLength: pieData.length,
        maxLabels: pieLabelDensity.maxLabels,
        insideThreshold: pieLabelDensity.insideThreshold,
        minPercentage: pieLabelDensity.minPercentage,
      });
      const commonXAxis = isHorizontal
        ? <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={yAxisFormatter} />
        : <XAxis dataKey="bucketLabel" angle={xAxisAngle} textAnchor="end" tick={{ fontSize: densityConfig.axisFontSize }} interval={densityConfig.tickInterval} height={xAxisHeight} />;
      const commonYAxis = isHorizontal
        ? <YAxis dataKey="bucketLabel" type="category" tick={{ fontSize: densityConfig.axisFontSize }} width={isMaximizedView ? 160 : 130} />
        : <YAxis tick={{ fontSize: 11 }} tickFormatter={yAxisFormatter} />;

      return (
        <ResponsiveContainer width="100%" height="90%">
          {chartType === 'pie' ? (
            <PieChart>
              <Tooltip formatter={(value: number) => valueFormatter(safeNumber(value))} />
              {legendNode}
              <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={pieLabelDensity.outerRadius} label={pieLabel} labelLine={pieLabelLine}>
                {pieData.map((entry, index) => (
                  <Cell key={`${tableId}-pie-${entry.name}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
            </PieChart>
          ) : chartType === 'line' ? (
            <LineChart data={chartRows} margin={{ top: 32, right: 16, left: 0, bottom: xAxisHeight - 2 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
              <XAxis dataKey="bucketLabel" angle={xAxisAngle} textAnchor="end" tick={{ fontSize: densityConfig.axisFontSize }} interval={densityConfig.tickInterval} height={xAxisHeight} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={yAxisFormatter} />
              <Tooltip formatter={(value: number) => valueFormatter(safeNumber(value))} />
              {legendNode}
              {seriesKeys.map((seriesKey, index) => (
                <Line
                  key={`${tableId}-line-${seriesKey}`}
                  type="monotone"
                  dataKey={seriesKey}
                  name={legendPrefix ? `${legendPrefix} - ${seriesKey}` : seriesKey}
                  stroke={PIE_COLORS[index % PIE_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                >
                  <LabelList
                    content={createDashboardLineLabels({
                      rowCount: chartRows.length,
                      seriesCount: seriesKeys.length,
                      formatValue: (value) => valueFormatter(safeNumber(value)),
                      offsetY: densityConfig.lineOffsetY,
                    })}
                  />
                </Line>
              ))}
            </LineChart>
          ) : (
            <BarChart data={chartRows} layout={isHorizontal ? 'vertical' : 'horizontal'} margin={{ top: 32, right: 16, left: isHorizontal ? 12 : 0, bottom: isHorizontal ? 0 : xAxisHeight - 2 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
              {commonXAxis}
              {commonYAxis}
              <Tooltip formatter={(value: number) => valueFormatter(safeNumber(value))} />
              {legendNode}
              {seriesKeys.map((seriesKey, index) => (
                <Bar
                  key={`${tableId}-bar-${seriesKey}`}
                  dataKey={seriesKey}
                  name={legendPrefix ? `${legendPrefix} - ${seriesKey}` : seriesKey}
                  fill={PIE_COLORS[index % PIE_COLORS.length]}
                  radius={[3, 3, 0, 0]}
                >
                  <LabelList
                    content={
                      labelOptions?.showAllLabels
                        ? createAlwaysVisibleBarLabels({
                          formatValue: (value) => valueFormatter(safeNumber(value)),
                          orientation: isHorizontal ? 'horizontal' : 'vertical',
                        })
                        : createDashboardBarLabels({
                          rowCount: chartRows.length,
                          seriesCount: seriesKeys.length,
                          formatValue: (value) => valueFormatter(safeNumber(value)),
                          orientation: isHorizontal ? 'horizontal' : 'vertical',
                        })
                    }
                  />
                </Bar>
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      );
    };

    switch (widgetId) {
      case 'kpi-summary':
        if (isLeadDataUnavailable) {
          return <div className="client-dashboard__empty">Lead metrics are temporarily unavailable for this account or date range. Other dashboard sections can still be reviewed.</div>;
        }
        return (
          <div className="client-dashboard__kpi-grid">
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Total Spend</div>
              <div className="client-dashboard__kpi-value">{formatCurrency(dashboardData.totalSpend, dashboardData.currency)}</div>
              <div className={`client-dashboard__trend ${dashboardData.totalSpendTrend.direction}`}>
                {formatTrendText(dashboardData.totalSpendTrend)}
              </div>
            </div>
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Leads</div>
              <div className="client-dashboard__kpi-value">{formatNumber(dashboardData.leads)}</div>
              <div className={`client-dashboard__trend ${dashboardData.leadsTrend.direction}`}>
                {formatTrendText(dashboardData.leadsTrend)}
              </div>
            </div>
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">MQLs (BOFU)</div>
              <div className="client-dashboard__kpi-value">{formatNumber(dashboardData.mqls)}</div>
              <div className={`client-dashboard__trend ${dashboardData.mqlsTrend.direction}`}>
                {formatTrendText(dashboardData.mqlsTrend)}
              </div>
            </div>
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">SQLs (Qualified)</div>
              <div className="client-dashboard__kpi-value">{formatNumber(dashboardData.sqls)}</div>
              <div className={`client-dashboard__trend ${dashboardData.sqlsTrend.direction}`}>
                {formatTrendText(dashboardData.sqlsTrend)}
              </div>
            </div>
          </div>
        );

      case 'cost-summary':
        if (isLeadDataUnavailable) {
          return <div className="client-dashboard__empty">Cost efficiency metrics need lead data, which is temporarily unavailable for this account or date range.</div>;
        }
        return (
          <div className="client-dashboard__kpi-grid client-dashboard__kpi-grid--three">
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Cost Per Lead</div>
              <div className="client-dashboard__kpi-value">{formatCurrency(dashboardData.cpl, dashboardData.currency)}</div>
              <div className={`client-dashboard__trend ${dashboardData.cplTrend.direction}`}>
                {formatTrendText(dashboardData.cplTrend)}
              </div>
            </div>
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Cost Per MQL</div>
              <div className="client-dashboard__kpi-value">{formatCurrency(dashboardData.cpmql, dashboardData.currency)}</div>
              <div className={`client-dashboard__trend ${dashboardData.cpmqlTrend.direction}`}>
                {formatTrendText(dashboardData.cpmqlTrend)}
              </div>
            </div>
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Cost Per SQL</div>
              <div className="client-dashboard__kpi-value">{formatCurrency(dashboardData.cpsql, dashboardData.currency)}</div>
              <div className={`client-dashboard__trend ${dashboardData.cpsqlTrend.direction}`}>
                {formatTrendText(dashboardData.cpsqlTrend)}
              </div>
            </div>
          </div>
        );

      case 'channel-funnel':
        return (
          <>
            <div className="table-container client-dashboard__table-container--paged">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th>Spend</th>
                    <th>Leads</th>
                    <th>MQLs</th>
                    <th>SQLs</th>
                    <th>CPL</th>
                    <th>Cost/MQL</th>
                    <th>Cost/SQL</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows('channel-funnel', dashboardData.channelRows).map((row) => (
                    <tr key={row.channel}>
                      <td>{CHANNEL_LABELS[row.channel]}</td>
                      <td>{formatCurrency(row.spend, dashboardData.currency)}</td>
                      <td>{formatNumber(row.leads)}</td>
                      <td>{formatNumber(row.mqls)}</td>
                      <td>{formatNumber(row.sqls)}</td>
                      <td>{formatCurrency(row.cpl, dashboardData.currency)}</td>
                      <td>{formatCurrency(row.cpmql, dashboardData.currency)}</td>
                      <td>{formatCurrency(row.cpsql, dashboardData.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderTableFooter('channel-funnel', dashboardData.channelRows.length)}
          </>
        );

      case 'website-lead-flow':
        return (
          <div className="client-dashboard__grid">
            <div className="client-dashboard__card">
              <div className="client-dashboard__card-title">Website Analytics</div>
              {isWebsiteDataUnavailable ? (
                <div className="client-dashboard__empty">Website analytics are temporarily unavailable for this account or date range.</div>
              ) : (
                <>
                  <div className="client-dashboard__metric-row"><span>Total website visitors</span><strong>{formatNumber(dashboardData.websiteVisitors)}</strong></div>
                  <div className="client-dashboard__metric-row"><span>Total organic visitors</span><strong>{formatNumber(dashboardData.organicVisitors)}</strong></div>
                  <div className="client-dashboard__metric-row"><span>Website conversion rate</span><strong>{formatPercent(dashboardData.websiteConversionRate)}</strong></div>
                  <div className="client-dashboard__metric-row"><span>Top country</span><strong>{dashboardData.countryRows[0]?.label || 'N/A'}</strong></div>
                  <div className="client-dashboard__metric-row"><span>Top city</span><strong>{dashboardData.cityRows[0]?.label || 'N/A'}</strong></div>
                </>
              )}
            </div>
            <div className="client-dashboard__card">
              <div className="client-dashboard__card-title">Lead and Meeting Flow</div>
              {isLeadDataUnavailable ? (
                <div className="client-dashboard__empty">Lead and meeting flow data are temporarily unavailable for this account or date range.</div>
              ) : (
                <>
                  <div className="client-dashboard__metric-row"><span>Leads</span><strong>{formatNumber(dashboardData.leads)}</strong></div>
                  <div className="client-dashboard__metric-row"><span>MQLs</span><strong>{formatNumber(dashboardData.mqls)}</strong></div>
                  <div className="client-dashboard__metric-row"><span>SQLs</span><strong>{formatNumber(dashboardData.sqls)}</strong></div>
                  <div className="client-dashboard__flow-list">
                    {dashboardData.leadFlow.map((flow) => {
                      const maxValue = dashboardData.leadFlow.reduce((max, item) => Math.max(max, item.value), 1);
                      const widthPct = maxValue > 0 ? (flow.value / maxValue) * 100 : 0;
                      return (
                        <div key={flow.label} className="client-dashboard__flow-item">
                          <span>{flow.label}</span>
                          <div className="client-dashboard__flow-bar-wrap">
                            <div className="client-dashboard__flow-bar" style={{ width: `${widthPct}%` }} />
                          </div>
                          <strong>{formatNumber(flow.value)}</strong>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        );

      case 'email-followers':
        return (
          <div className="client-dashboard__grid">
            <div className="client-dashboard__card">
              <div className="client-dashboard__card-title">Email Performance</div>
              <div className="client-dashboard__metric-row"><span>Sent</span><strong>{formatNumber(dashboardData.emailSummary.sent)}</strong></div>
              <div className="client-dashboard__metric-row"><span>Delivered</span><strong>{formatNumber(dashboardData.emailSummary.delivered)}</strong></div>
              <div className="client-dashboard__metric-row"><span>Delivery %</span><strong>{formatPercent(dashboardData.emailSummary.deliveryRate)}</strong></div>
              <div className="client-dashboard__metric-row"><span>Opened</span><strong>{formatNumber(dashboardData.emailSummary.opens)}</strong></div>
              <div className="client-dashboard__metric-row"><span>Open rate</span><strong>{formatPercent(dashboardData.emailSummary.openRate)}</strong></div>
              <div className="client-dashboard__metric-row"><span>Clicks</span><strong>{formatNumber(dashboardData.emailSummary.clicks)}</strong></div>
              <div className="client-dashboard__metric-row"><span>CTR</span><strong>{formatPercent(dashboardData.emailSummary.ctr)}</strong></div>
            </div>
            <div className="client-dashboard__card">
              <div className="client-dashboard__card-title">Social Followers Growth by Channel</div>
              {dashboardData.followers.map((row) => (
                <div key={row.channel} className="client-dashboard__metric-row client-dashboard__metric-row--stacked">
                  <div>
                    <strong>{row.channel}</strong>
                    <div className="client-dashboard__status-note">{row.note}</div>
                  </div>
                  <div className="client-dashboard__followers-metrics">
                    <span>{row.totalFollowers === null ? 'N/A' : `${formatNumber(row.totalFollowers)} followers`}</span>
                    <span>{row.netGrowth === null ? 'N/A growth' : `${row.netGrowth >= 0 ? '+' : ''}${formatNumber(row.netGrowth)} growth`}</span>
                    <span className={`client-dashboard__status ${row.status}`}>{statusLabel(row.status)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'traffic-source': {
        const trafficViz = widgetLayouts['traffic-source']?.visualizationConfig;
        const trafficBreakdown = getSelectedBreakdown('traffic-source');
        if (trafficBreakdown !== 'cumulative') {
          if (crossPageLoading['traffic-source'] && ga4SourceTimeseriesRowsRaw.length === 0) {
            return <div className="client-dashboard__empty">Loading source traffic breakdown…</div>;
          }
          const grouped = aggregateSeriesByBreakdown(
            ga4SourceTimeseriesRowsRaw,
            trafficBreakdown,
            'Visitors',
            (row) => getRowDateValue(row, 'date'),
            (row) => String(row.label ?? row.source_medium ?? row.sourceMedium ?? row.name ?? 'Unknown'),
            (row) => getGa4TimeseriesMetricValue(row),
            6
          );
          return renderBreakdownSeriesContent(
            'traffic-source',
            trafficViz?.chartType ?? 'bar',
            Boolean(trafficViz?.horizontal),
            grouped.chartRows,
            grouped.seriesKeys,
            'No source traffic breakdown data is available for the selected range.',
            formatNumber
          );
        }
        const chartData = dashboardData.sourceRows.map((r) => ({ name: r.source, Visitors: r.users }));
        if (trafficViz && trafficViz.chartType !== 'table') {
          const isHorizontal = trafficViz.horizontal && trafficViz.chartType === 'bar';
          const barLabel = createDashboardBarLabels({
            rowCount: chartData.length,
            seriesCount: 1,
            formatValue: (v) => formatNumber(v),
            orientation: isHorizontal ? 'horizontal' : 'vertical',
          });
          const lineLabel = createDashboardLineLabels({
            rowCount: chartData.length,
            seriesCount: 1,
            formatValue: (v) => formatNumber(v),
          });
          const pieData = buildCompactPieSeries(
            chartData.map((item) => ({ name: item.name, value: item.Visitors })),
            isMaximizedView ? 12 : 7
          );
          const pieLabel = createPieLabelRenderer({
            dataLength: pieData.length,
            maxLabels: pieLabelDensity.maxLabels,
            formatValue: (_value, _payload, percent) => `${Math.round((percent ?? 0) * 100)}%`,
            insideThreshold: pieLabelDensity.insideThreshold,
            minPercentage: pieLabelDensity.minPercentage,
          });
          const pieLabelLine = createPieLabelLineRenderer({
            dataLength: pieData.length,
            maxLabels: pieLabelDensity.maxLabels,
            insideThreshold: pieLabelDensity.insideThreshold,
            minPercentage: pieLabelDensity.minPercentage,
          });
          const axes = isHorizontal
            ? <><XAxis type="number" tick={{ fontSize: 11 }} /><YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={110} /></>
            : <><XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 10 }} interval={0} /><YAxis tick={{ fontSize: 11 }} /></>;
          return (
            <ResponsiveContainer width="100%" height="90%">
              {trafficViz.chartType === 'pie' ? (
                <PieChart>
                  <Tooltip formatter={(value: number) => formatNumber(value)} />
                  <Legend />
                  <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={isMaximizedView ? '78%' : '74%'} label={pieLabel} labelLine={pieLabelLine}>
                    {pieData.map((entry, index) => (
                      <Cell key={`${entry.name}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                </PieChart>
              ) : trafficViz.chartType === 'line' ? (
                <LineChart data={chartData} margin={{ top: 24, right: 16, left: 0, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                  <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 10 }} interval={0} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="Visitors" stroke="#496786" strokeWidth={2} dot={{ r: 4 }}>
                    <LabelList content={lineLabel} />
                  </Line>
                </LineChart>
              ) : (
                <BarChart data={chartData} layout={isHorizontal ? 'vertical' : 'horizontal'} margin={{ top: 24, right: 16, left: 0, bottom: isHorizontal ? 0 : 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                  {axes}
                  <Tooltip />
                  <Bar dataKey="Visitors" fill="#496786" radius={[3, 3, 0, 0]}>
                    <LabelList content={barLabel} />
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          );
        }
        const sourcePagedRows = pageRows('traffic-source', dashboardData.sourceRows);
        return (
          <>
            <div className="table-container client-dashboard__table-container--paged">
              <table className="data-table">
                <thead><tr><th>Source</th><th>Visitors</th></tr></thead>
                <tbody>
                  {sourcePagedRows.map((row) => (
                    <tr key={row.source}><td>{row.source}</td><td>{formatNumber(row.users)}</td></tr>
                  ))}
                  {dashboardData.sourceRows.length === 0 && (
                    <tr><td colSpan={2}>No source data available for the selected period.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {renderTableFooter('traffic-source', dashboardData.sourceRows.length)}
          </>
        );
      }

      case 'geo-visitors-country':
      case 'geo-visitors-city': {
        const isCountryWidget = widgetId === 'geo-visitors-country';
        const geoViz = widgetLayouts[widgetId]?.visualizationConfig;
        const geoBreakdown = getSelectedBreakdown(widgetId);
        const rows = isCountryWidget ? dashboardData.countryRows : dashboardData.cityRows;
        const rawTimeseriesRows = isCountryWidget ? ga4CountryTimeseriesRowsRaw : ga4CityTimeseriesRowsRaw;
        const titleKey = isCountryWidget ? 'country' : 'city';
        const emptyBreakdownMessage = isCountryWidget
          ? 'No country-level time-bucketed data is available for the selected range.'
          : 'No city-level time-bucketed data is available for the selected range.';
        const emptyTableMessage = isCountryWidget
          ? 'No country-level data available.'
          : 'No city-level data available.';
        const color = isCountryWidget ? '#4a7fa5' : 'rgba(199,130,68,0.85)';

        if (geoBreakdown !== 'cumulative') {
          if (crossPageLoading[widgetId] && rawTimeseriesRows.length === 0) {
            return <div className="client-dashboard__empty">Loading {titleKey} breakdown…</div>;
          }
          const grouped = aggregateSeriesByBreakdown(
            rawTimeseriesRows,
            geoBreakdown,
            'Visitors',
            (row) => getRowDateValue(row, 'date'),
            (row) => String(isCountryWidget ? (row.label ?? row.country ?? row.name ?? 'Unknown') : (row.label ?? row.city ?? row.name ?? 'Unknown')),
            (row) => getGa4TimeseriesMetricValue(row),
            6
          );
          return renderBreakdownSeriesContent(
            widgetId,
            geoViz?.chartType ?? 'bar',
            Boolean(geoViz?.horizontal),
            grouped.chartRows,
            grouped.seriesKeys,
            emptyBreakdownMessage,
            formatNumber
          );
        }

        const chartData = rows.map((row) => ({ name: row.label, Visitors: row.users }));
        if (geoViz && geoViz.chartType !== 'table') {
          const isHorizontal = geoViz.horizontal && geoViz.chartType === 'bar';
          const barLabel = createDashboardBarLabels({
            rowCount: chartData.length,
            seriesCount: 1,
            formatValue: (value) => formatNumber(value),
            orientation: isHorizontal ? 'horizontal' : 'vertical',
          });
          const lineLabel = createDashboardLineLabels({
            rowCount: chartData.length,
            seriesCount: 1,
            formatValue: (value) => formatNumber(value),
          });
          const pieData = buildCompactPieSeries(
            chartData.map((item) => ({ name: item.name, value: item.Visitors })),
            isMaximizedView ? 12 : 7
          );
          const pieLabel = createPieLabelRenderer({
            dataLength: pieData.length,
            maxLabels: pieLabelDensity.maxLabels,
            formatValue: (_value, _payload, percent) => `${Math.round((percent ?? 0) * 100)}%`,
            insideThreshold: pieLabelDensity.insideThreshold,
            minPercentage: pieLabelDensity.minPercentage,
          });
          const pieLabelLine = createPieLabelLineRenderer({
            dataLength: pieData.length,
            maxLabels: pieLabelDensity.maxLabels,
            insideThreshold: pieLabelDensity.insideThreshold,
            minPercentage: pieLabelDensity.minPercentage,
          });
          const axes = isHorizontal
            ? <><XAxis type="number" tick={{ fontSize: 11 }} /><YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={110} /></>
            : <><XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 10 }} interval={0} /><YAxis tick={{ fontSize: 11 }} /></>;

          return (
            <ResponsiveContainer width="100%" height="90%">
              {geoViz.chartType === 'pie' ? (
                <PieChart>
                  <Tooltip formatter={(value: number) => formatNumber(value)} />
                  <Legend />
                  <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={pieLabelDensity.outerRadius} label={pieLabel} labelLine={pieLabelLine}>
                    {pieData.map((entry, index) => (
                      <Cell key={`${entry.name}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                </PieChart>
              ) : geoViz.chartType === 'line' ? (
                <LineChart data={chartData} margin={{ top: 24, right: 16, left: 0, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                  <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 10 }} interval={0} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="Visitors" stroke={color} strokeWidth={2} dot={{ r: 4 }}>
                    <LabelList content={lineLabel} />
                  </Line>
                </LineChart>
              ) : (
                <BarChart data={chartData} layout={isHorizontal ? 'vertical' : 'horizontal'} margin={{ top: 24, right: 16, left: 0, bottom: isHorizontal ? 0 : 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                  {axes}
                  <Tooltip />
                  <Bar dataKey="Visitors" fill={color} radius={[3, 3, 0, 0]}>
                    <LabelList content={barLabel} />
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          );
        }

        const pagedRows = pageRows(widgetId, rows);
        return (
          <>
            <div className="table-container client-dashboard__table-container--paged">
              <table className="data-table">
                <thead><tr><th>{isCountryWidget ? 'Country' : 'City'}</th><th>Visitors</th></tr></thead>
                <tbody>
                  {pagedRows.map((row) => (
                    <tr key={row.label}>
                      <td>{row.label}</td>
                      <td>{formatNumber(row.users)}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && <tr><td colSpan={2}>{emptyTableMessage}</td></tr>}
                </tbody>
              </table>
            </div>
            {renderTableFooter(widgetId, rows.length)}
          </>
        );
      }

      case 'ga4-fintrans-landing-page-views-by-country': {
        if (crossPageLoading[widgetId] && ga4LandingPageCountryRows.length === 0) {
          return <div className="client-dashboard__empty">Loading landing page views by country…</div>;
        }

        if (ga4LandingPageCountryResponse?.configuration_status === 'not_configured') {
          return (
            <div className="client-dashboard__empty">
              No country routes are configured for this account. Add them under Account Management → Account Mappings.
            </div>
          );
        }

        const countryRows = ga4LandingPageCountryRows.map((row) => ({
          name: String(row.country_name || row.country_code || 'Unknown'),
          code: String(row.country_code || '').toUpperCase(),
          path: String(row.page_path || ''),
          'Page Views': safeNumber(row.screenPageViews),
        }));
        const hasViews = countryRows.some((row) => row['Page Views'] > 0);
        if (countryRows.length === 0 || !hasViews) {
          return <div className="client-dashboard__empty">No views were recorded for the configured country routes in this period.</div>;
        }

        const visualization = widgetLayouts[widgetId]?.visualizationConfig;
        const chartType = visualization?.chartType ?? 'pie';
        if (chartType === 'table') {
          return renderGenericTable(
            widgetId,
            ['Country', 'Country Code', 'Landing Page', 'Page Views'],
            countryRows.map((row) => [row.name, row.code, row.path, formatNumber(row['Page Views'])]),
            'No landing page views by country are available.'
          );
        }

        const isHorizontal = chartType === 'bar' && Boolean(visualization?.horizontal);
        const chartRows = countryRows.filter((row) => row['Page Views'] > 0);
        const barLabel = createDashboardBarLabels({
          rowCount: chartRows.length,
          seriesCount: 1,
          formatValue: (value) => formatNumber(value),
          orientation: isHorizontal ? 'horizontal' : 'vertical',
        });
        const lineLabel = createDashboardLineLabels({
          rowCount: chartRows.length,
          seriesCount: 1,
          formatValue: (value) => formatNumber(value),
        });
        const pieRows = buildCompactPieSeries(
          chartRows.map((row) => ({ name: row.name, value: row['Page Views'] })),
          isMaximizedView ? 12 : 8
        );
        const pieLabel = createPieLabelRenderer({
          dataLength: pieRows.length,
          maxLabels: pieLabelDensity.maxLabels,
          formatValue: (value) => formatNumber(value),
          insideThreshold: pieLabelDensity.insideThreshold,
          minPercentage: pieLabelDensity.minPercentage,
        });
        const pieLabelLine = createPieLabelLineRenderer({
          dataLength: pieRows.length,
          maxLabels: pieLabelDensity.maxLabels,
          insideThreshold: pieLabelDensity.insideThreshold,
          minPercentage: pieLabelDensity.minPercentage,
        });

        return (
          <ResponsiveContainer width="100%" height="90%">
            {chartType === 'pie' ? (
              <PieChart>
                <Tooltip formatter={(value: number) => formatNumber(value)} />
                <Legend />
                <Pie data={pieRows} dataKey="value" nameKey="name" outerRadius={pieLabelDensity.outerRadius} label={pieLabel} labelLine={pieLabelLine}>
                  {pieRows.map((entry, index) => (
                    <Cell key={`${entry.name}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            ) : chartType === 'line' ? (
              <LineChart data={chartRows} margin={{ top: 24, right: 16, left: 0, bottom: 55 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                <XAxis dataKey="name" angle={-30} textAnchor="end" tick={{ fontSize: 10 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip formatter={(value: number) => formatNumber(value)} />
                <Line type="monotone" dataKey="Page Views" stroke="#4285f4" strokeWidth={2} dot={{ r: 4 }}>
                  <LabelList content={lineLabel} />
                </Line>
              </LineChart>
            ) : (
              <BarChart data={chartRows} layout={isHorizontal ? 'vertical' : 'horizontal'} margin={{ top: 24, right: 24, left: 0, bottom: isHorizontal ? 0 : 55 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                {isHorizontal ? (
                  <><XAxis type="number" allowDecimals={false} /><YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 10 }} /></>
                ) : (
                  <><XAxis dataKey="name" angle={-30} textAnchor="end" tick={{ fontSize: 10 }} interval={0} /><YAxis allowDecimals={false} /></>
                )}
                <Tooltip formatter={(value: number) => formatNumber(value)} />
                <Bar dataKey="Page Views" fill="#4285f4" radius={[3, 3, 0, 0]}>
                  <LabelList content={barLabel} />
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        );
      }

      case 'ga4-pageviews-by-path':
      case 'pageviews': {
        const pvViz = widgetLayouts[widgetId]?.visualizationConfig;
        const pageBreakdown = getSelectedBreakdown(widgetId);
        if (pageBreakdown !== 'cumulative') {
          if (crossPageLoading[widgetId] && ga4PageTimeseriesRowsRaw.length === 0) {
            return <div className="client-dashboard__empty">Loading pageview breakdown…</div>;
          }
          const grouped = aggregateSeriesByBreakdown(
            ga4PageTimeseriesRowsRaw,
            pageBreakdown,
            'Pageviews',
            (row) => getRowDateValue(row, 'date'),
            (row) => {
              const rawLabel = String(row.label ?? row.pagePath ?? row.page_path ?? row.name ?? 'Unknown');
              return rawLabel.length > 28 ? `…${rawLabel.slice(-26)}` : rawLabel;
            },
            (row) => getGa4TimeseriesMetricValue(row),
            6
          );
          return renderBreakdownSeriesContent(
            widgetId,
            pvViz?.chartType ?? 'bar',
            Boolean(pvViz?.horizontal),
            grouped.chartRows,
            grouped.seriesKeys,
            'No pageview breakdown data is available for the selected range.',
            formatNumber
          );
        }
        const pvData = dashboardData.pageviewRows.map((r) => ({
          name: r.pagePath.length > 28 ? `…${r.pagePath.slice(-26)}` : r.pagePath,
          Pageviews: r.pageViews,
        }));
        if (pvViz && pvViz.chartType !== 'table') {
          const isHorizontal = pvViz.horizontal && pvViz.chartType === 'bar';
          const barLabel = createDashboardBarLabels({
            rowCount: pvData.length,
            seriesCount: 1,
            formatValue: (v) => formatNumber(v),
            orientation: isHorizontal ? 'horizontal' : 'vertical',
          });
          const lineLabel = createDashboardLineLabels({
            rowCount: pvData.length,
            seriesCount: 1,
            formatValue: (v) => formatNumber(v),
          });
          const pieData = buildCompactPieSeries(
            pvData.map((item) => ({ name: item.name, value: item.Pageviews })),
            isMaximizedView ? 12 : 8
          );
          const pieLabel = createPieLabelRenderer({
            dataLength: pieData.length,
            maxLabels: pieLabelDensity.maxLabels,
            formatValue: (_value, _payload, percent) => `${Math.round((percent ?? 0) * 100)}%`,
            insideThreshold: pieLabelDensity.insideThreshold,
            minPercentage: pieLabelDensity.minPercentage,
          });
          const pieLabelLine = createPieLabelLineRenderer({
            dataLength: pieData.length,
            maxLabels: pieLabelDensity.maxLabels,
            insideThreshold: pieLabelDensity.insideThreshold,
            minPercentage: pieLabelDensity.minPercentage,
          });
          const axes = isHorizontal
            ? <><XAxis type="number" tick={{ fontSize: 11 }} /><YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={130} /></>
            : <><XAxis dataKey="name" angle={-40} textAnchor="end" tick={{ fontSize: 9 }} interval={0} /><YAxis tick={{ fontSize: 11 }} /></>;
          return (
            <ResponsiveContainer width="100%" height="90%">
              {pvViz.chartType === 'pie' ? (
                <PieChart>
                  <Tooltip formatter={(value: number) => formatNumber(value)} />
                  <Legend />
                  <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={pieLabelDensity.outerRadius} label={pieLabel} labelLine={pieLabelLine}>
                    {pieData.map((entry, index) => (
                      <Cell key={`${entry.name}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                </PieChart>
              ) : pvViz.chartType === 'line' ? (
                <LineChart data={pvData} margin={{ top: 24, right: 16, left: 0, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                  <XAxis dataKey="name" angle={-40} textAnchor="end" tick={{ fontSize: 9 }} interval={0} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="Pageviews" stroke="rgba(199,130,68,0.9)" strokeWidth={2} dot={{ r: 4 }}>
                    <LabelList content={lineLabel} />
                  </Line>
                </LineChart>
              ) : (
                <BarChart data={pvData} layout={isHorizontal ? 'vertical' : 'horizontal'} margin={{ top: 24, right: 16, left: 0, bottom: isHorizontal ? 0 : 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                  {axes}
                  <Tooltip />
                  <Bar dataKey="Pageviews" fill="rgba(199,130,68,0.85)" radius={[3, 3, 0, 0]}>
                    <LabelList content={barLabel} />
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          );
        }
        const pagedPageviews = pageRows('pageviews', dashboardData.pageviewRows);
        return (
          <>
            <div className="table-container client-dashboard__table-container--paged">
              <table className="data-table">
                <thead>
                  <tr><th>Page</th><th>Pageviews</th></tr>
                </thead>
                <tbody>
                  {pagedPageviews.map((row) => (
                    <tr key={row.pagePath}><td>{row.pagePath}</td><td>{formatNumber(row.pageViews)}</td></tr>
                  ))}
                  {dashboardData.pageviewRows.length === 0 && (
                    <tr><td colSpan={2}>No pageview data available for the selected period.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {renderTableFooter('pageviews', dashboardData.pageviewRows.length)}
          </>
        );
      }

      case 'keywords':
        return (
          <>
            <div className="table-container client-dashboard__table-container--paged">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Keyword</th>
                    <th>Clicks</th>
                    <th>Impressions</th>
                    <th>CTR</th>
                    <th>Avg Position</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows('keywords', dashboardData.keywordRows).map((row) => (
                    <tr key={row.query}>
                      <td>{row.query}</td>
                      <td>{formatNumber(row.clicks)}</td>
                      <td>{formatNumber(row.impressions)}</td>
                      <td>{formatPercent(row.ctr)}</td>
                      <td>{row.position.toFixed(2)}</td>
                    </tr>
                  ))}
                  {dashboardData.keywordRows.length === 0 && (
                    <tr>
                      <td colSpan={5}>No keyword data available for the selected period.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {renderTableFooter('keywords', dashboardData.keywordRows.length)}
          </>
        );

      case 'social-posts':
        if (isSocialDataUnavailable) {
          return <div className="client-dashboard__empty">Social post metrics are temporarily unavailable for this account or date range.</div>;
        }
        return (
          <>
            <div className="table-container client-dashboard__table-container--paged">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th>Post</th>
                    <th>Impressions</th>
                    <th>Engagement Rate</th>
                    <th>CTR</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows('social-posts', dashboardData.socialPosts).map((post, index) => (
                    <tr key={`${post.channel}-${index}`}>
                      <td>{post.channel}</td>
                      <td>{post.label}</td>
                      <td>{formatNumber(post.impressions)}</td>
                      <td>{formatPercent(post.engagementRate)}</td>
                      <td>{formatPercent(post.ctr)}</td>
                    </tr>
                  ))}
                  {dashboardData.socialPosts.length === 0 && (
                    <tr>
                      <td colSpan={5}>No social post metrics available for the selected period.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {renderTableFooter('social-posts', dashboardData.socialPosts.length)}
          </>
        );

      case 'ads-performance':
        return (
          <>
            <div className="table-container client-dashboard__table-container--paged">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th>Impressions</th>
                    <th>Clicks</th>
                    <th>CTR</th>
                    <th>Conversions</th>
                    <th>Conversion Rate</th>
                    <th>CAC</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows('ads-performance', dashboardData.adsPerformance).map((row) => (
                    <tr key={row.channel}>
                      <td>{row.channel}</td>
                      <td>{formatNumber(row.impressions)}</td>
                      <td>{formatNumber(row.clicks)}</td>
                      <td>{formatPercent(row.ctr)}</td>
                      <td>{formatNumber(row.conversions)}</td>
                      <td>{formatPercent(row.conversionRate)}</td>
                      <td>{formatCurrency(row.cac, dashboardData.currency)}</td>
                      <td>
                        <span className={`client-dashboard__status ${row.status}`}>{statusLabel(row.status)}</span>
                        <div className="client-dashboard__status-note">{row.note}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderTableFooter('ads-performance', dashboardData.adsPerformance.length)}
          </>
        );

      case 'leads-table':
        return (
          <>
            <div className="table-container client-dashboard__table-container--paged">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Name</th>
                    <th>Company</th>
                    <th>Email</th>
                    <th>Stage</th>
                    <th>Qualified</th>
                    <th>First Touch Source</th>
                    <th>Campaign</th>
                    <th>Owner</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedLeads.map((lead) => (
                    <tr key={lead.id || `${lead.email}-${lead.date}`}>
                      <td>{lead.date ? new Date(lead.date).toLocaleDateString() : '-'}</td>
                      <td>{lead.name || '-'}</td>
                      <td>{lead.company || '-'}</td>
                      <td>{lead.email || '-'}</td>
                      <td>{lead.stage || '-'}</td>
                      <td>{lead.qualified ? 'Yes' : 'No'}</td>
                      <td>{lead.first_touch_source || '-'}</td>
                      <td>{lead.first_touch_campaign || '-'}</td>
                      <td>{lead.owner || '-'}</td>
                    </tr>
                  ))}
                  {pagedLeads.length === 0 && (
                    <tr>
                      <td colSpan={9}>No leads available for the selected date range.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="client-dashboard__table-footer">
              <div className="client-dashboard__table-footer-left">
                <span className="client-dashboard__table-footer-label">Rows per page:</span>
                <select
                  className="client-dashboard__table-rows-select"
                  value={leadsRowsPerPage}
                  onChange={(event) => {
                    setLeadsRowsPerPage(Number(event.target.value));
                    setLeadsPage(0);
                  }}
                >
                  {LEADS_TABLE_ROWS.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              <div className="client-dashboard__table-footer-right">
                <span className="client-dashboard__table-footer-count">
                  {dashboardData.leadsTableRows.length === 0 ? '0' : `${leadsPage * leadsRowsPerPage + 1}–${Math.min((leadsPage + 1) * leadsRowsPerPage, dashboardData.leadsTableRows.length)}`} of {dashboardData.leadsTableRows.length}
                </span>
                <button
                  type="button"
                  className="client-dashboard__table-nav-btn"
                  aria-label="Previous page"
                  disabled={leadsPage === 0}
                  onClick={() => setLeadsPage((p) => Math.max(0, p - 1))}
                >
                  &#8249;
                </button>
                <button
                  type="button"
                  className="client-dashboard__table-nav-btn"
                  aria-label="Next page"
                  disabled={leadsPage >= totalLeadsPages - 1}
                  onClick={() => setLeadsPage((p) => Math.min(totalLeadsPages - 1, p + 1))}
                >
                  &#8250;
                </button>
              </div>
            </div>
          </>
        );

      case 'insights': {
        const createBaseInsightDraft = (): InsightDraft => ({
          worked: [...dashboardData.worked],
          attention: [...dashboardData.attention],
          priorities: [...dashboardData.priorities],
        });

        const displayInsights = insightsEdits ?? createBaseInsightDraft();

        const updateInsightItem = (
          section: InsightSection,
          index: number,
          value: string
        ) => {
          setInsightsEdits((prev) => {
            const base = prev ?? createBaseInsightDraft();
            const updated = [...base[section]];
            updated[index] = value;
            return { ...base, [section]: updated };
          });
        };

        const addInsightItem = (section: InsightSection) => {
          setInsightsEdits((prev) => {
            const base = prev ?? createBaseInsightDraft();
            return { ...base, [section]: [...base[section], ''] };
          });
        };

        const removeInsightItem = (section: InsightSection, index: number) => {
          setInsightsEdits((prev) => {
            const base = prev ?? createBaseInsightDraft();
            return {
              ...base,
              [section]: base[section].filter((_, rowIndex) => rowIndex !== index),
            };
          });
        };

        const renderInsightSection = (
          title: string,
          items: string[],
          section: InsightSection
        ) => (
          <div className="client-dashboard__card">
            <div className="client-dashboard__insight-card-header">
              <div className="client-dashboard__card-title">{title}</div>
              {isEditingInsights ? (
                <button
                  type="button"
                  className="client-dashboard__insight-row-btn"
                  aria-label={`Add insight in ${title}`}
                  onClick={() => addInsightItem(section)}
                >
                  +
                </button>
              ) : null}
            </div>
            {isEditingInsights ? (
              items.length > 0 ? (
                <ul className="client-dashboard__list client-dashboard__list--editable">
                  {items.map((item, idx) => (
                    <li key={`${section}-${idx}`} className="client-dashboard__insight-edit-row">
                      <textarea
                        className="client-dashboard__insight-textarea"
                        value={item}
                        rows={2}
                        onChange={(e) => updateInsightItem(section, idx, e.target.value)}
                      />
                      <button
                        type="button"
                        className="client-dashboard__insight-row-btn client-dashboard__insight-row-btn--remove"
                        aria-label={`Remove insight ${idx + 1} from ${title}`}
                        onClick={() => removeInsightItem(section, idx)}
                      >
                        -
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="client-dashboard__insight-empty-hint">No insights yet. Click + to add one.</p>
              )
            ) : (
              items.length > 0 ? (
                <ul className="client-dashboard__list">
                  {items.map((item, idx) => <li key={`${section}-display-${idx}`}>{item}</li>)}
                </ul>
              ) : (
                <p className="client-dashboard__insight-empty-hint">No insights added yet.</p>
              )
            )}
          </div>
        );

        const applyInsightEdits = () => {
          if (!insightsEdits) {
            setIsEditingInsights(false);
            return;
          }
          const sanitized = sanitizeInsightDraft(insightsEdits);
          setInsightsEdits(sanitized);
          setIsEditingInsights(false);
        };

        return (
          <>
            <div className="client-dashboard__insights-toolbar">
              {isEditingInsights ? (
                <>
                  <button
                    type="button"
                    className="client-dashboard__insights-btn"
                    onClick={() => {
                      setInsightsEdits(null);
                      setIsEditingInsights(false);
                    }}
                  >
                    Reset to AI suggestions
                  </button>
                  <button
                    type="button"
                    className="client-dashboard__insights-btn client-dashboard__insights-btn--save"
                    onClick={applyInsightEdits}
                  >
                    Done editing
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="client-dashboard__insights-btn"
                  onClick={() => {
                    if (!insightsEdits) {
                      setInsightsEdits(createBaseInsightDraft());
                    }
                    setIsEditingInsights(true);
                  }}
                >
                  Edit insights
                </button>
              )}
            </div>
            <div className="client-dashboard__insights-grid">
              {renderInsightSection('What Worked', displayInsights.worked, 'worked')}
              {renderInsightSection('Needs Attention', displayInsights.attention, 'attention')}
              {renderInsightSection('Next Priorities', displayInsights.priorities, 'priorities')}
            </div>
          </>
        );
      }

      case 'ga4-users-total-stat':
        if (isWebsiteDataUnavailable) {
          return renderUnavailableStatCard('Users', 'Website analytics unavailable');
        }
        return renderSingleStatCard(
          'Users',
          formatNumber(safeNumber(ga4SummaryTotalsRaw.totalUsers ?? ga4SummaryTotalsRaw.activeUsers)),
          `Organic: ${formatNumber(dashboardData.organicVisitors)}`
        );

      case 'ga4-new-users-stat':
        if (isWebsiteDataUnavailable) {
          return renderUnavailableStatCard('New Users', 'Website analytics unavailable');
        }
        return renderSingleStatCard(
          'New Users',
          formatNumber(safeNumber(ga4SummaryTotalsRaw.newUsers)),
        );

      case 'ga4-sessions-total-stat':
        if (isWebsiteDataUnavailable) {
          return renderUnavailableStatCard('Sessions', 'Website analytics unavailable');
        }
        return renderSingleStatCard(
          'Sessions',
          formatNumber(safeNumber(ga4SummaryTotalsRaw.sessions)),
        );

      case 'ga4-pageviews-total-stat':
        if (isWebsiteDataUnavailable) {
          return renderUnavailableStatCard('Page Views', 'Website analytics unavailable');
        }
        return renderSingleStatCard(
          'Page Views',
          formatNumber(safeNumber(ga4SummaryTotalsRaw.screenPageViews ?? ga4SummaryTotalsRaw.pageViews)),
        );

      case 'ga4-pageviews-per-user-stat': {
        if (isWebsiteDataUnavailable) {
          return renderUnavailableStatCard('Page Views per User', 'Website analytics unavailable');
        }
        const totalUsers = safeNumber(ga4SummaryTotalsRaw.totalUsers ?? ga4SummaryTotalsRaw.activeUsers);
        const totalViews = safeNumber(ga4SummaryTotalsRaw.screenPageViews ?? ga4SummaryTotalsRaw.pageViews);
        return renderSingleStatCard(
          'Page Views per User',
          totalUsers > 0 ? `${(totalViews / totalUsers).toFixed(2)}` : '0.00',
        );
      }

      case 'ga4-avg-time-per-page-stat':
        if (isWebsiteDataUnavailable) {
          return renderUnavailableStatCard('Avg. Time Per Page', 'Website analytics unavailable');
        }
        return renderSingleStatCard(
          'Avg. Time Per Page',
          `${Math.round(safeNumber(ga4SummaryTotalsRaw.averageEngagementTimePerSession ?? ga4SummaryTotalsRaw.avgTimePerPage))}s`,
        );

      case 'ga4-percent-users-stat': {
        if (isWebsiteDataUnavailable) {
          return renderUnavailableStatCard('% Users', 'Website analytics unavailable');
        }
        const totalUsers = safeNumber(ga4SummaryTotalsRaw.totalUsers ?? ga4SummaryTotalsRaw.activeUsers);
        const newUsers = safeNumber(ga4SummaryTotalsRaw.newUsers);
        return renderSingleStatCard(
          '% Users',
          formatPercent(totalUsers > 0 ? (newUsers / totalUsers) * 100 : 0),
          `${formatNumber(newUsers)} new users`,
        );
      }

      case 'ga4-users-stat':
        return (
          <div className="client-dashboard__kpi-grid client-dashboard__kpi-grid--three">
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Website Users</div>
              <div className="client-dashboard__kpi-value">{formatNumber(dashboardData.websiteVisitors)}</div>
              <div className="client-dashboard__kpi-label">Organic: {formatNumber(dashboardData.organicVisitors)}</div>
            </div>
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Conversion Rate</div>
              <div className="client-dashboard__kpi-value">{formatPercent(dashboardData.websiteConversionRate)}</div>
              <div className="client-dashboard__kpi-label">Visitors → Leads</div>
            </div>
          </div>
        );

      case 'leads-stat':
        if (isLeadDataUnavailable) {
          return <div className="client-dashboard__empty">Lead metrics are temporarily unavailable for this account or date range.</div>;
        }
        return (
          <div className="client-dashboard__kpi-grid">
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Total Leads</div>
              <div className="client-dashboard__kpi-value">{formatNumber(dashboardData.leads)}</div>
              <div className={`client-dashboard__trend ${dashboardData.leadsTrend.direction}`}>
                {formatTrendText(dashboardData.leadsTrend)}
              </div>
            </div>
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">MQLs</div>
              <div className="client-dashboard__kpi-value">{formatNumber(dashboardData.mqls)}</div>
              <div className={`client-dashboard__trend ${dashboardData.mqlsTrend.direction}`}>
                {formatTrendText(dashboardData.mqlsTrend)}
              </div>
            </div>
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">SQLs</div>
              <div className="client-dashboard__kpi-value">{formatNumber(dashboardData.sqls)}</div>
              <div className={`client-dashboard__trend ${dashboardData.sqlsTrend.direction}`}>
                {formatTrendText(dashboardData.sqlsTrend)}
              </div>
            </div>
          </div>
        );

      case 'linkedin-campaigns': {
        const liMetrics = Array.isArray(crossPageData['linkedin-campaigns'])
          ? crossPageData['linkedin-campaigns'] as Record<string, unknown>[]
          : [];
        if (liMetrics.length === 0) {
          return <div className="client-dashboard__empty">No LinkedIn campaign data available for this period.</div>;
        }
        const liPaged = pageRows('linkedin-campaigns', liMetrics);
        return (
          <>
            <div className="table-container client-dashboard__table-container--paged">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Campaign</th><th>Impressions</th><th>Clicks</th><th>CTR</th><th>Spend</th><th>Conversions</th>
                  </tr>
                </thead>
                <tbody>
                  {liPaged.map((row, idx) => {
                    const impressions = safeNumber(row.impressions);
                    const clicks = safeNumber(row.clicks);
                    const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
                    const spend = safeNumber(row.cost_in_local_currency ?? row.costInLocalCurrency);
                    const conversions = safeNumber(row.conversions ?? row.externalWebsiteConversions ?? row.leads);
                    return (
                      <tr key={String(row.campaign_id ?? idx)}>
                        <td>{String(row.campaign_name ?? row.campaignName ?? 'Unknown')}</td>
                        <td>{formatNumber(impressions)}</td>
                        <td>{formatNumber(clicks)}</td>
                        <td>{formatPercent(ctr)}</td>
                        <td>{formatCurrency(spend, dashboardData.currency)}</td>
                        <td>{formatNumber(conversions)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {renderTableFooter('linkedin-campaigns', liMetrics.length)}
          </>
        );
      }

      case 'linkedin-campaign-details': {
        const rows = aggregateLinkedInDetailRows(linkedInDetailedMetrics, 'campaign').map((row) => {
          return [
            row.campaignName,
            formatLinkedInCampaignType(row.campaignType),
            formatNumber(row.impressions),
            formatNumber(row.clicks),
            formatPercentTwoDecimals(row.impressions > 0 ? (row.clicks / row.impressions) * 100 : 0),
            formatCurrencyTwoDecimals(row.spend, dashboardData.currency),
            formatNumber(row.conversions),
          ];
        });
        return renderGenericTable(
          'linkedin-campaign-details',
          ['Campaign', 'Type', 'Impressions', 'Clicks', 'CTR', 'Spend', 'Conversions'],
          rows,
          'No LinkedIn campaign details available for this period.'
        );
      }

      case 'linkedin-creative-details': {
        const rows = aggregateLinkedInDetailRows(linkedInDetailedMetrics, 'creative').map((row) => {
          return [
            row.creativeName ?? 'Unknown Creative',
            row.campaignName,
            formatNumber(row.impressions),
            formatNumber(row.clicks),
            formatPercentTwoDecimals(row.impressions > 0 ? (row.clicks / row.impressions) * 100 : 0),
            formatCurrencyTwoDecimals(row.spend, dashboardData.currency),
          ];
        });
        return renderGenericTable(
          'linkedin-creative-details',
          ['Creative', 'Campaign', 'Impressions', 'Clicks', 'CTR', 'Spend'],
          rows,
          'No LinkedIn creative details available for this period.'
        );
      }

      case 'linkedin-organic-total-posts-stat':
        if (isSocialDataUnavailable) {
          return renderUnavailableStatCard('Total Posts', 'Social post data unavailable');
        }
        // Prefer the server-side aggregate total; fall back to the paginated array length.
        return renderSingleStatCard('Total Posts', formatNumber(
          safeNumber(linkedInOrganicSummary?.total_posts) || linkedInOrganicPosts.length
        ));
      case 'linkedin-organic-impressions-stat':
        if (isSocialDataUnavailable) {
          return renderUnavailableStatCard('Impressions', 'Social post data unavailable');
        }
        return renderSingleStatCard('Impressions', formatNumber(
          safeNumber(linkedInOrganicSummary?.impressions)
        ));
      case 'linkedin-organic-clicks-stat':
        if (isSocialDataUnavailable) {
          return renderUnavailableStatCard('Clicks', 'Social post data unavailable');
        }
        return renderSingleStatCard('Clicks', formatNumber(
          safeNumber(linkedInOrganicSummary?.clicks)
        ));
      case 'linkedin-organic-engagements-stat':
        if (isSocialDataUnavailable) {
          return renderUnavailableStatCard('Engagements', 'Social post data unavailable');
        }
        return renderSingleStatCard('Engagements', formatNumber(
          safeNumber(linkedInOrganicSummary?.engagements)
        ));
      case 'linkedin-organic-reactions-stat':
        if (isSocialDataUnavailable) {
          return renderUnavailableStatCard('Reactions', 'Social post data unavailable');
        }
        return renderSingleStatCard('Reactions', formatNumber(
          safeNumber(linkedInOrganicSummary?.reactions)
        ));
      case 'linkedin-organic-comments-stat':
        if (isSocialDataUnavailable) {
          return renderUnavailableStatCard('Comments', 'Social post data unavailable');
        }
        return renderSingleStatCard('Comments', formatNumber(
          safeNumber(linkedInOrganicSummary?.comments)
        ));
      case 'linkedin-organic-reposts-stat':
        if (isSocialDataUnavailable) {
          return renderUnavailableStatCard('Reposts', 'Social post data unavailable');
        }
        return renderSingleStatCard('Reposts', formatNumber(
          safeNumber(linkedInOrganicSummary?.reposts)
        ));
      case 'linkedin-organic-avg-ctr-stat': {
        if (isSocialDataUnavailable) {
          return renderUnavailableStatCard('Avg. CTR', 'Social post data unavailable');
        }
        return renderSingleStatCard('Avg. CTR', formatPercent(normalizePercentValue(linkedInOrganicSummary?.avg_ctr)));
      }
      case 'linkedin-organic-avg-engagement-rate-stat': {
        if (isSocialDataUnavailable) {
          return renderUnavailableStatCard('Avg. Engagement Rate', 'Social post data unavailable');
        }
        return renderSingleStatCard('Avg. Engagement Rate', formatPercent(normalizePercentValue(linkedInOrganicSummary?.avg_engagement_rate)));
      }
      case 'linkedin-organic-followers-stat':
        if (isSocialDataUnavailable) {
          return renderUnavailableStatCard('Followers', 'Social post data unavailable');
        }
        return renderSingleStatCard('Followers', formatNumber(safeNumber(linkedInFollowerCurrent.totalFollowers ?? linkedInFollowerCurrent.followers)));

      case 'linkedin-organic-impressions-vs-clicks': {
        const organicBreakdown = getSelectedBreakdown('linkedin-organic-impressions-vs-clicks');
        if (organicBreakdown !== 'cumulative') {
          const groupedRows = aggregateScalarRowsByBreakdown(
            linkedInOrganicPosts,
            organicBreakdown,
            (row) => getRowDateValue(row, 'published_at', 'publishedAt', 'created_at', 'createdAt', 'date'),
            {
              Impressions: (row) => safeNumber(row.impression_count ?? row.impressions),
              Clicks: (row) => safeNumber(row.click_count ?? row.clicks),
            }
          );
          return renderBreakdownSeriesContent(
            'linkedin-organic-impressions-vs-clicks',
            widgetLayouts['linkedin-organic-impressions-vs-clicks']?.visualizationConfig?.chartType ?? 'bar',
            Boolean(widgetLayouts['linkedin-organic-impressions-vs-clicks']?.visualizationConfig?.horizontal),
            groupedRows,
            ['Impressions', 'Clicks'],
            'No LinkedIn organic time-bucketed performance data is available for the selected range.',
            formatNumber
          );
        }
        // Rank ALL fetched posts by Impressions and take the top 10.
        // Sorting before slicing ensures the chart shows the true highest-performing
        // posts rather than just the 10 most recent ones.
        const rows = [...linkedInOrganicPosts]
          .map((row, index) => ({
            name: String(row.commentary ?? row.text ?? `Post ${index + 1}`).slice(0, 28),
            Impressions: safeNumber(row.impression_count ?? row.impressions),
            Clicks: safeNumber(row.click_count ?? row.clicks),
          }))
          .sort((a, b) => b.Impressions - a.Impressions)
          .slice(0, 10);
        if (rows.length === 0) {
          return <div className="client-dashboard__empty">No LinkedIn organic posts available for this period.</div>;
        }
        const viz = widgetLayouts['linkedin-organic-impressions-vs-clicks']?.visualizationConfig;
        const isHorizontal = Boolean(viz?.horizontal && viz.chartType === 'bar');
        const lineLabel = createDashboardLineLabels({ rowCount: rows.length, seriesCount: 2, formatValue: (v) => formatNumber(v) });
        const impressionsBarLabel = createDashboardBarLabels({ rowCount: rows.length, seriesCount: 2, formatValue: (v) => formatNumber(v), orientation: isHorizontal ? 'horizontal' : 'vertical' });
        const clicksBarLabel = createDashboardBarLabels({ rowCount: rows.length, seriesCount: 2, formatValue: (v) => formatNumber(v), orientation: isHorizontal ? 'horizontal' : 'vertical' });
        const topLegend = (
          <Legend
            verticalAlign="top"
            height={28}
            wrapperStyle={{ fontSize: '11px', paddingBottom: 4 }}
          />
        );
        if (viz?.chartType === 'table') {
          return renderGenericTable(
            'linkedin-organic-impressions-vs-clicks',
            ['Post', 'Impressions', 'Clicks'],
            rows.map((row) => [row.name, formatNumber(row.Impressions), formatNumber(row.Clicks)]),
            'No LinkedIn organic posts available for this period.'
          );
        }
        return (
          <ResponsiveContainer width="100%" height="90%">
            {viz?.chartType === 'line' ? (
              <LineChart data={rows} margin={{ top: 32, right: 16, left: 0, bottom: 86 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 10 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                {topLegend}
                <Line type="monotone" dataKey="Impressions" stroke="#1976d2" strokeWidth={2} dot={{ r: 3 }}>
                  <LabelList content={lineLabel} />
                </Line>
                <Line type="monotone" dataKey="Clicks" stroke="#ed6c02" strokeWidth={2} dot={{ r: 3 }}>
                  <LabelList content={lineLabel} />
                </Line>
              </LineChart>
            ) : (
              <BarChart data={rows} layout={isHorizontal ? 'vertical' : 'horizontal'} margin={{ top: 32, right: 16, left: isHorizontal ? 12 : 0, bottom: isHorizontal ? 16 : 86 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                {isHorizontal ? (
                  <>
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={150} />
                  </>
                ) : (
                  <>
                    <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 10 }} interval={0} />
                    <YAxis tick={{ fontSize: 11 }} />
                  </>
                )}
                <Tooltip />
                {topLegend}
                <Bar dataKey="Impressions" fill="#1976d2" radius={[3, 3, 0, 0]}>
                  <LabelList content={impressionsBarLabel} />
                </Bar>
                <Bar dataKey="Clicks" fill="#ed6c02" radius={[3, 3, 0, 0]}>
                  <LabelList content={clicksBarLabel} />
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        );
      }

      case 'linkedin-organic-ctr-engagement': {
        const organicRateBreakdown = getSelectedBreakdown('linkedin-organic-ctr-engagement');
        if (organicRateBreakdown !== 'cumulative') {
          const buckets = new Map<string, { ctrTotal: number; engagementTotal: number; count: number }>();
          linkedInOrganicPosts.forEach((row) => {
            const dateValue = getRowDateValue(row, 'published_at', 'publishedAt', 'created_at', 'createdAt', 'date');
            if (!dateValue) return;
            const bucketKey = getBreakdownBucketKey(dateValue, organicRateBreakdown);
            const current = buckets.get(bucketKey) || { ctrTotal: 0, engagementTotal: 0, count: 0 };
            current.ctrTotal += normalizePercentValue(row.ctr);
            current.engagementTotal += normalizePercentValue(row.engagement_rate ?? row.engagementRate);
            current.count += 1;
            buckets.set(bucketKey, current);
          });
          const groupedRows = Array.from(buckets.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([bucketKey, metrics]) => ({
              name: formatBreakdownBucketLabel(bucketKey, organicRateBreakdown),
              bucketLabel: formatBreakdownBucketLabel(bucketKey, organicRateBreakdown),
              CTR: metrics.count > 0 ? metrics.ctrTotal / metrics.count : 0,
              EngagementRate: metrics.count > 0 ? metrics.engagementTotal / metrics.count : 0,
            }));
          return renderBreakdownSeriesContent(
            'linkedin-organic-ctr-engagement',
            widgetLayouts['linkedin-organic-ctr-engagement']?.visualizationConfig?.chartType ?? 'line',
            Boolean(widgetLayouts['linkedin-organic-ctr-engagement']?.visualizationConfig?.horizontal),
            groupedRows,
            ['CTR', 'EngagementRate'],
            'No LinkedIn organic rate trend is available for the selected range.',
            (value) => `${value.toFixed(2)}%`,
            (value) => `${value}%`
          );
        }
        // Rank ALL fetched posts by CTR and take the top 10 (same pattern as impressions chart).
        const rows = [...linkedInOrganicPosts]
          .map((row, index) => ({
            name: String(row.commentary ?? row.text ?? `Post ${index + 1}`).slice(0, 28),
            CTR: normalizePercentValue(row.ctr),
            EngagementRate: normalizePercentValue(row.engagement_rate ?? row.engagementRate),
          }))
          .sort((a, b) => b.CTR - a.CTR)
          .slice(0, 10);
        if (rows.length === 0) {
          return <div className="client-dashboard__empty">No LinkedIn organic rate data available for this period.</div>;
        }
        const viz = widgetLayouts['linkedin-organic-ctr-engagement']?.visualizationConfig;
        const isHorizontal = Boolean(viz?.horizontal && viz.chartType === 'bar');
        const lineLabel = createDashboardLineLabels({ rowCount: rows.length, seriesCount: 2, formatValue: (v) => `${v.toFixed(2)}%` });
        const ctrBarLabel = createDashboardBarLabels({ rowCount: rows.length, seriesCount: 2, formatValue: (v) => `${v.toFixed(2)}%`, orientation: isHorizontal ? 'horizontal' : 'vertical' });
        const engagementBarLabel = createDashboardBarLabels({ rowCount: rows.length, seriesCount: 2, formatValue: (v) => `${v.toFixed(2)}%`, orientation: isHorizontal ? 'horizontal' : 'vertical' });
        const topLegend = (
          <Legend
            verticalAlign="top"
            height={28}
            wrapperStyle={{ fontSize: '11px', paddingBottom: 4 }}
          />
        );
        if (viz?.chartType === 'table') {
          return renderGenericTable(
            'linkedin-organic-ctr-engagement',
            ['Post', 'CTR', 'Engagement Rate'],
            rows.map((row) => [row.name, `${row.CTR.toFixed(2)}%`, `${row.EngagementRate.toFixed(2)}%`]),
            'No LinkedIn organic rate data available for this period.'
          );
        }
        return (
          <ResponsiveContainer width="100%" height="90%">
            {viz?.chartType === 'bar' ? (
              <BarChart data={rows} layout={isHorizontal ? 'vertical' : 'horizontal'} margin={{ top: 32, right: 16, left: isHorizontal ? 12 : 0, bottom: isHorizontal ? 16 : 86 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                {isHorizontal ? (
                  <>
                    <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(value) => `${value}%`} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={150} />
                  </>
                ) : (
                  <>
                    <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 10 }} interval={0} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(value) => `${value}%`} />
                  </>
                )}
                <Tooltip formatter={(value: number) => `${value.toFixed(2)}%`} />
                {topLegend}
                <Bar dataKey="CTR" fill="#26a69a" radius={[3, 3, 0, 0]}>
                  <LabelList content={ctrBarLabel} />
                </Bar>
                <Bar dataKey="EngagementRate" fill="#9c27b0" radius={[3, 3, 0, 0]}>
                  <LabelList content={engagementBarLabel} />
                </Bar>
              </BarChart>
            ) : (
              <LineChart data={rows} margin={{ top: 32, right: 16, left: 0, bottom: 86 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 10 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(value) => `${value}%`} />
                <Tooltip formatter={(value: number) => `${value.toFixed(2)}%`} />
                {topLegend}
                <Line type="monotone" dataKey="CTR" stroke="#26a69a" strokeWidth={2} dot={{ r: 3 }}>
                  <LabelList content={lineLabel} />
                </Line>
                <Line type="monotone" dataKey="EngagementRate" stroke="#9c27b0" strokeWidth={2} dot={{ r: 3 }}>
                  <LabelList content={lineLabel} />
                </Line>
              </LineChart>
            )}
          </ResponsiveContainer>
        );
      }

      case 'linkedin-active-lead-forms-stat': {
        const activeForms = linkedInLeadFormRows.filter((row) => String(row.state ?? '').toUpperCase() === 'PUBLISHED').length;
        return renderSingleStatCard('Active Lead Forms', formatNumber(activeForms));
      }

      case 'linkedin-total-leads-captured-stat':
        return renderSingleStatCard('Total Leads Captured', formatNumber(linkedInLeadResponseRows.length));

      case 'gads-keywords': {
        if (crossPageLoading['gads-keywords']) {
          return <div className="client-dashboard__empty">Loading keyword data…</div>;
        }
        const kwData = crossPageData['gads-keywords'] as Record<string, unknown> | undefined;
        const kwRows = Array.isArray(kwData?.keywords)
          ? kwData.keywords as Record<string, unknown>[]
          : Array.isArray(kwData?.data)
            ? kwData.data as Record<string, unknown>[]
            : [];
        if (kwRows.length === 0) {
          return <div className="client-dashboard__empty">No Google Ads keyword data available for this period.</div>;
        }
        const kwPaged = pageRows('gads-keywords', kwRows);
        return (
          <>
            <div className="table-container client-dashboard__table-container--paged">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Keyword</th><th>Match Type</th><th>Impressions</th><th>Clicks</th><th>CTR</th><th>Conversions</th>
                  </tr>
                </thead>
                <tbody>
                  {kwPaged.map((row, idx) => (
                    <tr key={String(row.keyword_text ?? row.keyword ?? idx)}>
                      <td>{String(row.keyword_text ?? row.keyword ?? '-')}</td>
                      <td>{String(row.match_type ?? '-')}</td>
                      <td>{formatNumber(safeNumber(row.impressions))}</td>
                      <td>{formatNumber(safeNumber(row.clicks))}</td>
                      <td>{formatPercent(safeNumber(row.ctr) > 1 ? safeNumber(row.ctr) : safeNumber(row.ctr) * 100)}</td>
                      <td>{formatNumber(safeNumber(row.conversions))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderTableFooter('gads-keywords', kwRows.length)}
          </>
        );
      }

      case 'email-campaigns': {
        if (crossPageLoading['email-campaigns']) {
          return <div className="client-dashboard__empty">Loading campaign data…</div>;
        }
        const emailCampaignData = crossPageData['email-campaigns'] as Record<string, unknown> | undefined;
        const emailRows = Array.isArray(emailCampaignData?.campaigns)
          ? emailCampaignData.campaigns as Record<string, unknown>[]
          : Array.isArray(emailCampaignData?.data)
            ? emailCampaignData.data as Record<string, unknown>[]
            : [];
        if (emailRows.length === 0) {
          return <div className="client-dashboard__empty">No email campaign data available for this period.</div>;
        }
        const normalizedEmailRows = emailRows.map((row) => {
          const sent = safeNumber(row.total_sent ?? row.sent);
          const delivered = safeNumber(row.total_delivered ?? row.delivered);
          const opens = safeNumber(row.opens ?? row.total_opens);
          const clicks = safeNumber(row.clicks ?? row.total_clicks);
          const denominator = delivered > 0 ? delivered : sent;
          const fallbackOpenRate = divideOrNull(opens * 100, denominator) ?? 0;
          const fallbackCtr = divideOrNull(clicks * 100, denominator) ?? 0;

          return {
            campaignId: String(row.campaign_id ?? ''),
            campaignName: String(row.campaign_name ?? row.subject ?? '-'),
            delivered,
            openRate: normalizePercentValue(row.open_rate ?? row.avg_open_rate ?? row.opens_percentage ?? fallbackOpenRate),
            ctr: normalizePercentValue(row.click_rate ?? row.ctr ?? row.avg_click_rate ?? row.clicks_percentage ?? fallbackCtr),
          };
        });
        const emailChartData = normalizedEmailRows.slice(0, 10).map((row) => ({
          name: row.campaignName.substring(0, 14),
          openRate: row.openRate,
          ctr: row.ctr,
        }));
        const emailViz = widgetLayouts['email-campaigns']?.visualizationConfig
          ?? widgetLayouts['email-subject-open-rate-chart']?.visualizationConfig;
        const emailLineLabel = createDashboardLineLabels({
          rowCount: emailChartData.length,
          seriesCount: 2,
          formatValue: (v) => `${v.toFixed(1)}%`,
        });
        const emailPieData = buildCompactPieSeries(
          emailChartData.map((row) => ({ name: row.name, value: row.openRate })),
          isMaximizedView ? 12 : 7
        );
        const emailPieLabel = createPieLabelRenderer({
          dataLength: emailPieData.length,
          maxLabels: pieLabelDensity.maxLabels,
          formatValue: (_value, _payload, percent) => `${Math.round((percent ?? 0) * 100)}%`,
          insideThreshold: pieLabelDensity.insideThreshold,
          minPercentage: pieLabelDensity.minPercentage,
        });
        const emailPieLabelLine = createPieLabelLineRenderer({
          dataLength: emailPieData.length,
          maxLabels: pieLabelDensity.maxLabels,
          insideThreshold: pieLabelDensity.insideThreshold,
          minPercentage: pieLabelDensity.minPercentage,
        });
        const isHorizontal = Boolean(emailViz?.horizontal && emailViz?.chartType === 'bar');
        return (
          <>
            <ResponsiveContainer width="100%" height={isMaximizedView ? 320 : 220}>
              {emailViz?.chartType === 'pie' ? (
                <PieChart>
                  <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
                  <Legend />
                  <Pie data={emailPieData} dataKey="value" nameKey="name" name="Open Rate %" outerRadius={isMaximizedView ? '76%' : '70%'} label={emailPieLabel} labelLine={emailPieLabelLine}>
                    {emailPieData.map((entry, index) => (
                      <Cell key={`email-open-${entry.name}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                </PieChart>
              ) : emailViz?.chartType === 'line' ? (
                <LineChart data={emailChartData} margin={{ top: 24, right: 16, left: 0, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                  <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 11 }} interval={0} />
                  <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => `${v.toFixed(1)}%`} />
                  <Legend />
                  <Line type="monotone" dataKey="openRate" name="Open Rate %" stroke="#496786" strokeWidth={2} dot={{ r: 4 }}>
                    <LabelList content={emailLineLabel} />
                  </Line>
                  <Line type="monotone" dataKey="ctr" name="CTR %" stroke="rgba(199,130,68,0.9)" strokeWidth={2} dot={{ r: 4 }}>
                    <LabelList content={emailLineLabel} />
                  </Line>
                </LineChart>
              ) : (
                <BarChart data={emailChartData} layout={isHorizontal ? 'vertical' : 'horizontal'} margin={{ top: 24, right: 16, left: 0, bottom: isHorizontal ? 0 : 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                  {isHorizontal ? (
                    <>
                      <XAxis type="number" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={120} />
                    </>
                  ) : (
                    <>
                      <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 11 }} interval={0} />
                      <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
                    </>
                  )}
                  <Tooltip formatter={(v: number) => `${v.toFixed(1)}%`} />
                  <Legend />
                  <Bar dataKey="openRate" name="Open Rate %" fill="#496786" radius={[3, 3, 0, 0]}>
                    <LabelList content={createDashboardBarLabels({
                      rowCount: emailChartData.length,
                      seriesCount: 2,
                      formatValue: (v) => `${v.toFixed(1)}%`,
                      orientation: isHorizontal ? 'horizontal' : 'vertical',
                    })} />
                  </Bar>
                  <Bar dataKey="ctr" name="CTR %" fill="rgba(199,130,68,0.85)" radius={[3, 3, 0, 0]}>
                    <LabelList content={createDashboardBarLabels({
                      rowCount: emailChartData.length,
                      seriesCount: 2,
                      formatValue: (v) => `${v.toFixed(1)}%`,
                      orientation: isHorizontal ? 'horizontal' : 'vertical',
                    })} />
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
            <div className="table-container client-dashboard__table-container--paged" style={{ marginTop: '0.5rem' }}>
              <table className="data-table">
                <thead>
                  <tr><th>Campaign</th><th>Delivered</th><th>Open Rate</th><th>CTR</th></tr>
                </thead>
                <tbody>
                  {pageRows('email-campaigns', normalizedEmailRows).map((row, idx) => (
                    <tr key={row.campaignId || String(idx)}>
                      <td>{row.campaignName}</td>
                      <td>{formatNumber(row.delivered)}</td>
                      <td>{formatPercent(row.openRate)}</td>
                      <td>{formatPercent(row.ctr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderTableFooter('email-campaigns', normalizedEmailRows.length)}
          </>
        );
      }

      case 'linkedin-monthly-spend-trend':
      case 'linkedin-monthly-spend': {
        const liMetrics = Array.isArray(crossPageData['linkedin-monthly-spend'])
          ? crossPageData['linkedin-monthly-spend'] as Record<string, unknown>[]
          : [];
        if (liMetrics.length === 0) return <div className="client-dashboard__empty">No LinkedIn spend data for this period.</div>;
        const monthMap: Record<string, number> = {};
        liMetrics.forEach((m) => {
          const dateStr = String(m.start_date ?? m.date ?? '').substring(0, 7);
          if (dateStr) monthMap[dateStr] = (monthMap[dateStr] ?? 0) + safeNumber(m.cost_in_local_currency ?? m.costInLocalCurrency);
        });
        const monthData = Object.entries(monthMap).sort(([a], [b]) => a.localeCompare(b)).map(([month, spend]) => ({ name: month, Spend: spend }));
        const barLabel = createDashboardBarLabels({ rowCount: monthData.length, seriesCount: 1, formatValue: (v) => formatCurrency(v, dashboardData?.currency ?? 'INR') });
        return (
          <ResponsiveContainer width="100%" height="90%">
            <BarChart data={monthData} margin={{ top: 24, right: 16, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="Spend" fill="#0077b5" radius={[3, 3, 0, 0]}><LabelList content={barLabel} /></Bar>
            </BarChart>
          </ResponsiveContainer>
        );
      }

      case 'linkedin-follower-stats': {
        const row = (dashboardData?.followers ?? []).find((r) => r.channel.toLowerCase().includes('linkedin'));
        return (
          <div className="client-dashboard__kpi-grid client-dashboard__kpi-grid--three">
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Total Followers</div>
              <div className="client-dashboard__kpi-value">{row?.totalFollowers != null ? formatNumber(row.totalFollowers) : 'N/A'}</div>
            </div>
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Net Growth</div>
              <div className="client-dashboard__kpi-value">{row?.netGrowth != null ? `${row.netGrowth >= 0 ? '+' : ''}${formatNumber(row.netGrowth)}` : 'N/A'}</div>
            </div>
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Data Status</div>
              <div className="client-dashboard__kpi-value" style={{ fontSize: '0.85rem' }}>{row ? row.note : 'No data'}</div>
            </div>
          </div>
        );
      }

      case 'linkedin-lead-forms': {
        if (crossPageLoading['linkedin-lead-forms']) return <div className="client-dashboard__empty">Loading lead forms…</div>;
        const lfData = crossPageData['linkedin-lead-forms'] as Record<string, unknown> | undefined;
        const lfRows = Array.isArray(lfData?.lead_forms) ? lfData.lead_forms as Record<string, unknown>[] : [];
        if (lfRows.length === 0) return <div className="client-dashboard__empty">No lead form data available for this period.</div>;
        return (
          <>
            <div className="table-container client-dashboard__table-container--paged">
              <table className="data-table">
                <thead><tr><th>Form</th><th>Opens</th><th>Submissions</th><th>Rate</th></tr></thead>
                <tbody>
                  {pageRows('linkedin-lead-forms', lfRows).map((r, i) => (
                    <tr key={String(r.form_id ?? i)}>
                      <td>{String(r.form_name ?? r.name ?? '-')}</td>
                      <td>{formatNumber(safeNumber(r.opens ?? r.impressions))}</td>
                      <td>{formatNumber(safeNumber(r.submissions ?? r.completions))}</td>
                      <td>{formatPercent(safeNumber(r.submission_rate ?? r.completionRate) * (safeNumber(r.submission_rate ?? r.completionRate) > 1 ? 1 : 100))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderTableFooter('linkedin-lead-forms', lfRows.length)}
          </>
        );
      }

      case 'linkedin-organic-posts': {
        const opData = crossPageData['linkedin-organic-posts'] as Record<string, unknown> | undefined;
        const opRows = Array.isArray(opData?.organic_posts)
          ? opData.organic_posts as Record<string, unknown>[]
          : Array.isArray(opData?.posts)
            ? opData.posts as Record<string, unknown>[]
            : Array.isArray(opData)
              ? opData as Record<string, unknown>[]
              : dashboardData?.socialPosts ?? [];
        if (opRows.length === 0) return <div className="client-dashboard__empty">No organic post data available for this period.</div>;
        return (
          <>
            <div className="table-container client-dashboard__table-container--paged">
              <table className="data-table">
                <thead><tr><th>Post</th><th>Impressions</th><th>Eng. Rate</th><th>CTR</th></tr></thead>
                <tbody>
                  {pageRows('linkedin-organic-posts', opRows as Record<string, unknown>[]).map((r: Record<string, unknown>, i: number) => {
                    const postLabel = String(r.label ?? r.commentary ?? r.text ?? r.post_urn ?? '-');
                    const impressions = safeNumber(r.impressions ?? r.impression_count);
                    const engagementRate = normalizePercentValue(r.engagementRate ?? r.engagement_rate ?? r.engagement);
                    const ctr = normalizePercentValue(r.ctr);
                    const rowKey = String(r.post_urn ?? r.id ?? r.urn ?? `${postLabel}-${i}`);

                    return (
                      <tr key={rowKey}>
                        <td>
                          <span className="client-dashboard__table-truncate" title={postLabel}>
                            {postLabel}
                          </span>
                        </td>
                        <td>{formatNumber(impressions)}</td>
                        <td>{formatPercent(engagementRate)}</td>
                        <td>{formatPercent(ctr)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {renderTableFooter('linkedin-organic-posts', opRows.length)}
          </>
        );
      }

      case 'ga4-sessions-stat':
        return (
          <div className="client-dashboard__kpi-grid client-dashboard__kpi-grid--three">
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Website Users</div>
              <div className="client-dashboard__kpi-value">{formatNumber(dashboardData.websiteVisitors)}</div>
            </div>
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Organic Visitors</div>
              <div className="client-dashboard__kpi-value">{formatNumber(dashboardData.organicVisitors)}</div>
            </div>
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Conversion Rate</div>
              <div className="client-dashboard__kpi-value">{formatPercent(dashboardData.websiteConversionRate)}</div>
            </div>
          </div>
        );

      case 'ga4-engagement-stat':
        return (
          <div className="client-dashboard__kpi-grid">
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Top Country</div>
              <div className="client-dashboard__kpi-value" style={{ fontSize: '1.2rem' }}>{dashboardData.countryRows[0]?.label ?? 'N/A'}</div>
              <div className="client-dashboard__kpi-label">{dashboardData.countryRows[0] ? `${formatNumber(dashboardData.countryRows[0].users)} users` : ''}</div>
            </div>
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Top Source</div>
              <div className="client-dashboard__kpi-value" style={{ fontSize: '1.2rem' }}>{dashboardData.sourceRows[0]?.source ?? 'N/A'}</div>
              <div className="client-dashboard__kpi-label">{dashboardData.sourceRows[0] ? `${formatNumber(dashboardData.sourceRows[0].users)} users` : ''}</div>
            </div>
          </div>
        );

      case 'ga4-traffic-by-device': {
        const deviceBreakdown = getSelectedBreakdown('ga4-traffic-by-device');
        if (deviceBreakdown !== 'cumulative') {
          if (crossPageLoading['ga4-traffic-by-device'] && ga4DeviceTimeseriesRowsRaw.length === 0) {
            return <div className="client-dashboard__empty">Loading device traffic breakdown…</div>;
          }
          const grouped = aggregateSeriesByBreakdown(
            ga4DeviceTimeseriesRowsRaw,
            deviceBreakdown,
            'Users',
            (row) => getRowDateValue(row, 'date'),
            (row) => String(row.label ?? row.device ?? row.deviceCategory ?? row.name ?? 'Unknown'),
            (row) => getGa4TimeseriesMetricValue(row),
            6
          );
          return renderBreakdownSeriesContent(
            'ga4-traffic-by-device',
            widgetLayouts['ga4-traffic-by-device']?.visualizationConfig?.chartType ?? 'bar',
            Boolean(widgetLayouts['ga4-traffic-by-device']?.visualizationConfig?.horizontal),
            grouped.chartRows,
            grouped.seriesKeys,
            'No GA4 device breakdown data is available for the selected range.',
            formatNumber
          );
        }
        const deviceData = ga4DeviceRowsRaw.map((row) => ({
          name: String(row._id ?? row.deviceCategory ?? row.device ?? 'Unknown'),
          Users: safeNumber(row.totalUsers ?? row.activeUsers ?? row.users),
        })).filter((row) => row.Users > 0);
        const viz = widgetLayouts['ga4-traffic-by-device']?.visualizationConfig;
        if (deviceData.length === 0) {
          return <div className="client-dashboard__empty">No GA4 device data available for this period.</div>;
        }
        if (viz && viz.chartType !== 'table') {
          const isHorizontal = Boolean(viz.horizontal && viz.chartType === 'bar');
          const lineLabel = createDashboardLineLabels({
            rowCount: deviceData.length,
            seriesCount: 1,
            formatValue: (v) => formatNumber(v),
          });
          const barLabel = createDashboardBarLabels({
            rowCount: deviceData.length,
            seriesCount: 1,
            formatValue: (v) => formatNumber(v),
            orientation: isHorizontal ? 'horizontal' : 'vertical',
          });
          const pieRows = buildCompactPieSeries(
            deviceData.map((item) => ({ name: item.name, value: item.Users })),
            isMaximizedView ? 10 : 6
          );
          const pieLabel = createPieLabelRenderer({
            dataLength: pieRows.length,
            maxLabels: pieLabelDensity.maxLabels,
            formatValue: (_value, _payload, percent) => `${Math.round((percent ?? 0) * 100)}%`,
            insideThreshold: pieLabelDensity.insideThreshold,
            minPercentage: pieLabelDensity.minPercentage,
          });
          const pieLabelLine = createPieLabelLineRenderer({
            dataLength: pieRows.length,
            maxLabels: pieLabelDensity.maxLabels,
            insideThreshold: pieLabelDensity.insideThreshold,
            minPercentage: pieLabelDensity.minPercentage,
          });
          return (
            <ResponsiveContainer width="100%" height="90%">
              {viz.chartType === 'line' ? (
                <LineChart data={deviceData} margin={{ top: 24, right: 16, left: 0, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="Users" stroke="#51cf66" strokeWidth={2} dot={{ r: 4 }}>
                    <LabelList content={lineLabel} />
                  </Line>
                </LineChart>
              ) : viz.chartType === 'pie' ? (
                <PieChart>
                  <Tooltip formatter={(value: number) => formatNumber(value)} />
                  <Legend />
                  <Pie data={pieRows} dataKey="value" nameKey="name" outerRadius={pieLabelDensity.outerRadius} label={pieLabel} labelLine={pieLabelLine}>
                    {pieRows.map((entry, index) => <Cell key={`${entry.name}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />)}
                  </Pie>
                </PieChart>
              ) : (
                <BarChart data={deviceData} layout={isHorizontal ? 'vertical' : 'horizontal'} margin={{ top: 24, right: 16, left: 0, bottom: isHorizontal ? 0 : 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                  {isHorizontal ? (
                    <>
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={110} />
                    </>
                  ) : (
                    <>
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                    </>
                  )}
                  <Tooltip />
                  <Bar dataKey="Users" fill="#51cf66" radius={[3, 3, 0, 0]}>
                    <LabelList content={barLabel} />
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          );
        }
        return renderGenericTable(
          'ga4-traffic-by-device',
          ['Device', 'Users'],
          deviceData.map((row) => [row.name, formatNumber(row.Users)]),
          'No GA4 device data available for this period.'
        );
      }

      case 'ga4-total-vs-organic-weekly': {
        if (crossPageLoading[widgetId] && ga4WeeklyUserAcquisitionRows.length === 0) {
          return <div className="client-dashboard__empty">Loading weekly user-acquisition traffic…</div>;
        }

        const chartRows = ga4WeeklyUserAcquisitionRows.map((row) => {
          const weekStart = String(row.week_start ?? row.weekStart ?? '');
          const weekEnd = String(row.week_end ?? row.weekEnd ?? '');
          return {
            name: `${formatCompactDateLabel(weekStart)} - ${formatCompactDateLabel(weekEnd)}`,
            'Total Traffic': safeNumber(row.totalUsers),
            'Organic Search Traffic': safeNumber(row.organicSearchUsers),
          };
        });

        if (chartRows.length === 0) {
          return <div className="client-dashboard__empty">No GA4 user-acquisition traffic is available for the last eight complete weeks.</div>;
        }

        const visualization = widgetLayouts[widgetId]?.visualizationConfig;
        const chartType = visualization?.chartType ?? 'bar';
        if (chartType === 'table') {
          return renderGenericTable(
            widgetId,
            ['Week', 'Total Traffic', 'Organic Search Traffic'],
            chartRows.map((row) => [
              row.name,
              formatNumber(row['Total Traffic']),
              formatNumber(row['Organic Search Traffic']),
            ]),
            'No weekly GA4 user-acquisition traffic is available.'
          );
        }

        const isHorizontal = chartType === 'bar' && Boolean(visualization?.horizontal);
        const barLabel = createAlwaysVisibleBarLabels({
          formatValue: (value) => formatNumber(value),
          orientation: isHorizontal ? 'horizontal' : 'vertical',
        });
        const lineLabel = createDashboardLineLabels({
          rowCount: chartRows.length,
          seriesCount: 2,
          formatValue: (value) => formatNumber(value),
        });

        return (
          <ResponsiveContainer width="100%" height={330}>
            {chartType === 'line' ? (
              <LineChart data={chartRows} margin={{ top: 28, right: 20, left: 0, bottom: 42 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip formatter={(value: number) => formatNumber(value)} />
                <Legend />
                <Line type="monotone" dataKey="Total Traffic" stroke="#4285f4" strokeWidth={2} dot={{ r: 3 }}>
                  <LabelList content={lineLabel} />
                </Line>
                <Line type="monotone" dataKey="Organic Search Traffic" stroke="#ea4335" strokeWidth={2} dot={{ r: 3 }}>
                  <LabelList content={lineLabel} />
                </Line>
              </LineChart>
            ) : (
              <BarChart data={chartRows} layout={isHorizontal ? 'vertical' : 'horizontal'} margin={{ top: 28, right: isHorizontal ? 64 : 20, left: 0, bottom: isHorizontal ? 0 : 42 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                {isHorizontal ? (
                  <>
                    <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={120} />
                  </>
                ) : (
                  <>
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  </>
                )}
                <Tooltip formatter={(value: number) => formatNumber(value)} />
                <Legend />
                <Bar dataKey="Total Traffic" fill="#4285f4" radius={[3, 3, 0, 0]}>
                  <LabelList content={barLabel} />
                </Bar>
                <Bar dataKey="Organic Search Traffic" fill="#ea4335" radius={[3, 3, 0, 0]}>
                  <LabelList content={barLabel} />
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        );
      }

      case 'ga4-paid-search-campaign-performance': {
        if (crossPageLoading[widgetId] && paidSearchCampaignRows.length === 0) {
          return <div className="client-dashboard__empty">Loading Paid Search campaign performance…</div>;
        }
        if (paidSearchCampaignRows.length === 0) {
          return <div className="client-dashboard__empty">No attributable Paid Search campaign data is available for this period.</div>;
        }

        return (
          <>
            <div className="table-container client-dashboard__table-container--paged">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Campaign Name</th>
                    <th>Spend</th>
                    <th>Impressions</th>
                    <th>Clicks</th>
                    <th>CTR</th>
                    <th>Total Users</th>
                    <th>CPC</th>
                    <th>Ads Conversions</th>
                    <th>CAC</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows(widgetId, paidSearchCampaignRows).map((campaign: PaidSearchCampaignMetric) => (
                    <tr key={campaign.campaignId || campaign.campaignName}>
                      <td>{campaign.campaignName}</td>
                      <td>{formatCurrency(campaign.spend, dashboardData.currency)}</td>
                      <td>{formatNumber(campaign.impressions)}</td>
                      <td>{formatNumber(campaign.clicks)}</td>
                      <td>{formatPercent(campaign.ctr)}</td>
                      <td>{formatNumber(campaign.totalUsers)}</td>
                      <td>{formatCurrency(campaign.cpc, dashboardData.currency)}</td>
                      <td>{formatNumber(campaign.adsConversions)}</td>
                      <td>{formatCurrency(campaign.cac, dashboardData.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderTableFooter(widgetId, paidSearchCampaignRows.length)}
          </>
        );
      }

      case 'ga4-paid-search-users-by-campaign': {
        if (crossPageLoading[widgetId] && paidSearchCampaignRows.length === 0 && !paidSearchUnattributed) {
          return <div className="client-dashboard__empty">Loading Paid Search campaign users…</div>;
        }

        const userRows = [
          ...paidSearchCampaignRows,
          ...(paidSearchUnattributed && paidSearchUnattributed.totalUsers > 0 ? [paidSearchUnattributed] : []),
        ].map((campaign) => ({
          name: campaign.campaignName,
          'Total Users': campaign.totalUsers,
        }));

        if (userRows.length === 0) {
          return <div className="client-dashboard__empty">No Paid Search campaign users are available for this period.</div>;
        }

        const visualization = widgetLayouts[widgetId]?.visualizationConfig;
        const chartType = visualization?.chartType ?? 'bar';
        if (chartType === 'table') {
          return renderGenericTable(
            widgetId,
            ['First User Campaign', 'Total Users'],
            userRows.map((row) => [row.name, formatNumber(row['Total Users'])]),
            'No Paid Search campaign users are available for this period.'
          );
        }

        const isHorizontal = chartType === 'bar' && Boolean(visualization?.horizontal);
        const barLabel = createAlwaysVisibleBarLabels({
          formatValue: (value) => formatNumber(value),
          orientation: isHorizontal ? 'horizontal' : 'vertical',
        });
        const lineLabel = createDashboardLineLabels({
          rowCount: userRows.length,
          seriesCount: 1,
          formatValue: (value) => formatNumber(value),
        });

        return (
          <ResponsiveContainer width="100%" height={330}>
            {chartType === 'line' ? (
              <LineChart data={userRows} margin={{ top: 28, right: 20, left: 0, bottom: 70 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={80} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip formatter={(value: number) => formatNumber(value)} />
                <Line type="monotone" dataKey="Total Users" stroke="#4285f4" strokeWidth={2} dot={{ r: 4 }}>
                  <LabelList content={lineLabel} />
                </Line>
              </LineChart>
            ) : (
              <BarChart
                data={userRows}
                layout={isHorizontal ? 'vertical' : 'horizontal'}
                margin={{ top: 28, right: isHorizontal ? 64 : 20, left: isHorizontal ? 24 : 0, bottom: isHorizontal ? 0 : 70 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                {isHorizontal ? (
                  <>
                    <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={190} />
                  </>
                ) : (
                  <>
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={80} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  </>
                )}
                <Tooltip formatter={(value: number) => formatNumber(value)} />
                <Bar dataKey="Total Users" fill="#4285f4" radius={[3, 3, 0, 0]}>
                  <LabelList content={barLabel} />
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        );
      }

      case 'ga4-seo-weekly-stats':
        return renderGenericTable(
          'ga4-seo-weekly-stats',
          ['Week', 'Users', 'New Users', 'Sessions', 'Page Views', 'Pages / User', 'Avg. Time / Page'],
          ga4WeeklyRowsRaw.map((row) => [
            `${String(row.week_start ?? row.weekStart ?? '-')} to ${String(row.week_end ?? row.weekEnd ?? '-')}`,
            formatNumber(safeNumber(row.totalUsers ?? row.users)),
            formatNumber(safeNumber(row.newUsers)),
            formatNumber(safeNumber(row.sessions)),
            formatNumber(safeNumber(row.pageViews ?? row.screenPageViews)),
            safeNumber(row.pageViewsPerUser ?? row.viewsPerUser).toFixed(2),
            `${Math.round(safeNumber(row.avgTimePerPage))}s`,
          ]),
          'No weekly GA4 stats available for this period.'
        );

      case 'ga4-insights-aggregated': {
        const totalUsers = safeNumber(ga4SummaryTotalsRaw.totalUsers ?? ga4SummaryTotalsRaw.activeUsers);
        const pageViews = safeNumber(ga4SummaryTotalsRaw.screenPageViews ?? ga4SummaryTotalsRaw.pageViews);
        const newUsers = safeNumber(ga4SummaryTotalsRaw.newUsers);
        const topDevice = ga4DeviceRowsRaw
          .map((row) => ({ label: String(row._id ?? row.deviceCategory ?? row.device ?? 'Unknown'), value: safeNumber(row.totalUsers ?? row.activeUsers ?? row.users) }))
          .sort((a, b) => b.value - a.value)[0];
        return renderGenericTable(
          'ga4-insights-aggregated',
          ['Metric', 'Value'],
          [
            ['Total Users', formatNumber(totalUsers)],
            ['New Users', formatNumber(newUsers)],
            ['Sessions', formatNumber(safeNumber(ga4SummaryTotalsRaw.sessions))],
            ['Page Views', formatNumber(pageViews)],
            ['Page Views per User', totalUsers > 0 ? (pageViews / totalUsers).toFixed(2) : '0.00'],
            ['Top Device', topDevice ? `${topDevice.label} (${formatNumber(topDevice.value)})` : 'N/A'],
            ['Top Country', dashboardData.countryRows[0] ? `${dashboardData.countryRows[0].label} (${formatNumber(dashboardData.countryRows[0].users)})` : 'N/A'],
          ],
          'No aggregated GA4 insight data available.'
        );
      }

      case 'ga4-pages-screens':
        return renderGenericTable(
          'ga4-pages-screens',
          ['Page Path', 'Views', 'Active Users', 'Views / Active User', 'Avg. Engagement Time / User'],
          ga4PageRowsRaw.map((row) => {
            const views = safeNumber(row.screenPageViews ?? row.pageViews);
            const activeUsers = safeNumber(row.activeUsers);
            return [
              String(row._id ?? row.pagePath ?? '-'),
              formatNumber(views),
              formatNumber(activeUsers),
              activeUsers > 0 ? (views / activeUsers).toFixed(2) : '0.00',
              `${Math.round(safeNumber(row.averageEngagementTimePerActiveUser ?? row.avgEngagementTime))}s`,
            ];
          }),
          'No Pages & Screens data available for this period.'
        );

      case 'ga4-regions': {
        const rows = [
          ...ga4CountryRowsRaw.map((row) => ['Country', String(row._id ?? '-'), formatNumber(safeNumber(row.activeUsers))]),
          ...ga4CityRowsRaw.map((row) => ['City', String(row._id ?? '-'), formatNumber(safeNumber(row.activeUsers))]),
        ];
        return renderGenericTable(
          'ga4-regions',
          ['Type', 'Region', 'Users'],
          rows,
          'No regional GA4 data available for this period.'
        );
      }

      case 'gsc-total-clicks-stat':
        return renderSingleStatCard('Total Clicks', formatNumber(gscSummaryMetrics.clicks));
      case 'gsc-total-impressions-stat':
        return renderSingleStatCard('Total Impressions', formatNumber(gscSummaryMetrics.impressions));
      case 'gsc-average-ctr-stat': {
        return renderSingleStatCard('Average CTR', formatPercent(gscSummaryMetrics.ctr));
      }
      case 'gsc-average-position-stat':
        return renderSingleStatCard('Average Position', gscSummaryMetrics.position.toFixed(1));

      case 'gsc-weekly-performance-trend': {
        const chartRows = gscWeeklyRows.map((row) => ({
          name: String(row.week ?? row.weekStart ?? row.week_start ?? '-'),
          Clicks: safeNumber(row.clicks),
          Impressions: safeNumber(row.impressions),
        }));
        if (chartRows.length === 0) {
          return <div className="client-dashboard__empty">No weekly Search Console data available for this period.</div>;
        }
        const viz = widgetLayouts['gsc-weekly-performance-trend']?.visualizationConfig;
        if (viz?.chartType === 'table') {
          return renderGenericTable(
            'gsc-weekly-performance-trend',
            ['Week', 'Clicks', 'Impressions'],
            chartRows.map((row) => [row.name, formatNumber(row.Clicks), formatNumber(row.Impressions)]),
            'No weekly Search Console data available for this period.'
          );
        }
        const isHorizontal = Boolean(viz?.horizontal && viz.chartType === 'bar');
        const lineLabel = createDashboardLineLabels({ rowCount: chartRows.length, seriesCount: 2, formatValue: (v) => formatNumber(v) });
        const clicksBarLabel = createDashboardBarLabels({ rowCount: chartRows.length, seriesCount: 2, formatValue: (v) => formatNumber(v), orientation: isHorizontal ? 'horizontal' : 'vertical' });
        const impressionsBarLabel = createDashboardBarLabels({ rowCount: chartRows.length, seriesCount: 2, formatValue: (v) => formatNumber(v), orientation: isHorizontal ? 'horizontal' : 'vertical' });
        return (
          <ResponsiveContainer width="100%" height="90%">
            {viz?.chartType === 'bar' ? (
              <BarChart data={chartRows} layout={isHorizontal ? 'vertical' : 'horizontal'} margin={{ top: 24, right: 16, left: isHorizontal ? 12 : 0, bottom: isHorizontal ? 0 : 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                {isHorizontal ? (
                  <>
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={120} />
                  </>
                ) : (
                  <>
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                  </>
                )}
                <Tooltip />
                <Legend />
                <Bar dataKey="Clicks" fill="#496786" radius={[3, 3, 0, 0]}>
                  <LabelList content={clicksBarLabel} />
                </Bar>
                <Bar dataKey="Impressions" fill="#82ca9d" radius={[3, 3, 0, 0]}>
                  <LabelList content={impressionsBarLabel} />
                </Bar>
              </BarChart>
            ) : (
              <LineChart data={chartRows} margin={{ top: 24, right: 16, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="Clicks" stroke="#496786" strokeWidth={2} dot={{ r: 3 }}>
                  <LabelList content={lineLabel} />
                </Line>
                <Line type="monotone" dataKey="Impressions" stroke="#82ca9d" strokeWidth={2} dot={{ r: 3 }}>
                  <LabelList content={lineLabel} />
                </Line>
              </LineChart>
            )}
          </ResponsiveContainer>
        );
      }

      case 'gsc-queries-table':
        return renderGenericTable(
          'gsc-queries-table',
          ['Query', 'Clicks', 'Impressions', 'CTR', 'Position'],
          gscQueryRows.map((row) => [
            String(row.query ?? '-'),
            formatNumber(safeNumber(row.clicks)),
            formatNumber(safeNumber(row.impressions)),
            `${(safeNumber(row.ctr) * 100).toFixed(2)}%`,
            safeNumber(row.position).toFixed(1),
          ]),
          'No Search Console query data available.'
        );

      case 'gsc-pages-table':
        return renderGenericTable(
          'gsc-pages-table',
          ['Page', 'Clicks', 'Impressions', 'CTR', 'Position'],
          gscPageRows.map((row) => [
            String(row.page ?? '-'),
            formatNumber(safeNumber(row.clicks)),
            formatNumber(safeNumber(row.impressions)),
            `${(safeNumber(row.ctr) * 100).toFixed(2)}%`,
            safeNumber(row.position).toFixed(1),
          ]),
          'No Search Console page data available.'
        );

      case 'gsc-countries-table':
        return renderGenericTable(
          'gsc-countries-table',
          ['Country', 'Clicks', 'Impressions', 'CTR', 'Position'],
          gscCountryRows.map((row) => [
            String(row.country ?? '-'),
            formatNumber(safeNumber(row.clicks)),
            formatNumber(safeNumber(row.impressions)),
            `${(safeNumber(row.ctr) * 100).toFixed(2)}%`,
            safeNumber(row.position).toFixed(1),
          ]),
          'No Search Console country data available.'
        );

      case 'gsc-devices-table':
        return renderGenericTable(
          'gsc-devices-table',
          ['Device', 'Clicks', 'Impressions', 'CTR', 'Position'],
          gscDeviceRows.map((row) => [
            String(row.device ?? '-'),
            formatNumber(safeNumber(row.clicks)),
            formatNumber(safeNumber(row.impressions)),
            `${(safeNumber(row.ctr) * 100).toFixed(2)}%`,
            safeNumber(row.position).toFixed(1),
          ]),
          'No Search Console device data available.'
        );

      case 'gsc-weekly-table':
        return renderGenericTable(
          'gsc-weekly-table',
          ['Week', 'Clicks', 'Impressions', 'Avg CTR', 'Avg Position'],
          gscWeeklyRows.map((row) => [
            `${String(row.weekStart ?? row.week_start ?? '-')} to ${String(row.weekEnd ?? row.week_end ?? '-')}`,
            formatNumber(safeNumber(row.clicks)),
            formatNumber(safeNumber(row.impressions)),
            `${(safeNumber(row.avgCtr ?? row.ctr) * 100).toFixed(2)}%`,
            safeNumber(row.avgPosition ?? row.position).toFixed(1),
          ]),
          'No Search Console weekly data available.'
        );

      case 'email-total-campaigns-stat':
        return renderSingleStatCard('Total Campaigns', formatNumber(safeNumber(emailSummaryMetrics.total_campaigns)));
      case 'email-delivery-rate-stat':
        return renderSingleStatCard('Delivery Rate', formatPercent(normalizePercentValue(emailSummaryMetrics.delivery_rate)));
      case 'email-bounce-rate-stat':
        return renderSingleStatCard('Bounce Rate', formatPercent(normalizePercentValue(emailSummaryMetrics.bounce_rate)));
      case 'email-unsubscribe-rate-stat':
        return renderSingleStatCard('Unsubscribe Rate', formatPercent(normalizePercentValue(emailSummaryMetrics.unsubscribe_rate)));

      case 'email-subject-open-rate-chart': {
        const rows = emailCampaignRows.map((row) => {
          const delivered = safeNumber(row.total_delivered ?? row.delivered);
          const opens = safeNumber(row.opens ?? row.total_opens);
          const subject = String(row.subject_line ?? row.subject ?? row.campaign_name ?? 'Unknown');
          const openRate = normalizePercentValue(row.open_rate ?? (delivered > 0 ? (opens / delivered) * 100 : 0));
          return {
            name: subject.length > 24 ? `${subject.slice(0, 23)}…` : subject,
            value: openRate,
            fullSubject: subject,
          };
        }).sort((a, b) => b.value - a.value).slice(0, 10);
        if (rows.length === 0) {
          return <div className="client-dashboard__empty">No email subject-line data available for this period.</div>;
        }
        const viz = widgetLayouts['email-subject-open-rate-chart']?.visualizationConfig;
        const isHorizontal = Boolean(viz?.horizontal && viz.chartType === 'bar');
        const lineLabel = createDashboardLineLabels({ rowCount: rows.length, seriesCount: 1, formatValue: (v) => `${v.toFixed(1)}%` });
        const pieData = buildCompactPieSeries(rows.map((row) => ({ name: row.name, value: row.value })), isMaximizedView ? 12 : 7);
        const pieLabel = createPieLabelRenderer({
          dataLength: pieData.length,
          maxLabels: pieLabelDensity.maxLabels,
          formatValue: (_value, _payload, percent) => `${Math.round((percent ?? 0) * 100)}%`,
          insideThreshold: pieLabelDensity.insideThreshold,
          minPercentage: pieLabelDensity.minPercentage,
        });
        const pieLabelLine = createPieLabelLineRenderer({
          dataLength: pieData.length,
          maxLabels: pieLabelDensity.maxLabels,
          insideThreshold: pieLabelDensity.insideThreshold,
          minPercentage: pieLabelDensity.minPercentage,
        });
        if (viz?.chartType === 'table') {
          return renderGenericTable(
            'email-subject-open-rate-chart',
            ['Subject', 'Open Rate'],
            rows.map((row) => [row.fullSubject, `${row.value.toFixed(1)}%`]),
            'No email subject-line data available for this period.'
          );
        }
        return (
          <ResponsiveContainer width="100%" height="90%">
            {viz?.chartType === 'line' ? (
              <LineChart data={rows} margin={{ top: 24, right: 16, left: 0, bottom: 70 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 10 }} interval={0} />
                <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number) => `${v.toFixed(1)}%`} labelFormatter={(_, payload) => String(payload?.[0]?.payload?.fullSubject ?? '')} />
                <Line type="monotone" dataKey="value" stroke="#ed6c02" strokeWidth={2} dot={{ r: 3 }}>
                  <LabelList content={lineLabel} />
                </Line>
              </LineChart>
            ) : viz?.chartType === 'pie' ? (
              <PieChart>
                <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
                <Legend />
                <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={pieLabelDensity.outerRadius} label={pieLabel} labelLine={pieLabelLine}>
                  {pieData.map((entry, index) => (
                    <Cell key={`${entry.name}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            ) : (
              <BarChart data={rows} layout={isHorizontal ? 'vertical' : 'horizontal'} margin={{ top: 24, right: 16, left: isHorizontal ? 12 : 0, bottom: isHorizontal ? 0 : 70 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                {isHorizontal ? (
                  <>
                    <XAxis type="number" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={160} />
                  </>
                ) : (
                  <>
                    <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 10 }} interval={0} />
                    <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
                  </>
                )}
                <Tooltip formatter={(v: number) => `${v.toFixed(1)}%`} labelFormatter={(_, payload) => String(payload?.[0]?.payload?.fullSubject ?? '')} />
                <Bar dataKey="value" fill="#ed6c02" radius={[3, 3, 0, 0]}>
                  <LabelList content={createDashboardBarLabels({ rowCount: rows.length, seriesCount: 1, formatValue: (v) => `${v.toFixed(1)}%`, orientation: isHorizontal ? 'horizontal' : 'vertical' })} />
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        );
      }

      case 'gads-impressions-stat':
        return renderSingleStatCard('Impressions', formatNumber(googleAdsMetricsRaw.reduce((sum, row) => sum + safeNumber(row.impressions), 0)));
      case 'gads-clicks-stat':
        return renderSingleStatCard('Clicks', formatNumber(googleAdsMetricsRaw.reduce((sum, row) => sum + safeNumber(row.clicks), 0)));
      case 'gads-total-cost-stat':
        return renderSingleStatCard('Total Cost', formatCurrency(googleAdsMetricsRaw.reduce((sum, row) => sum + safeNumber(row.cost), 0), dashboardData.currency));
      case 'gads-avg-ctr-stat': {
        const impressions = googleAdsMetricsRaw.reduce((sum, row) => sum + safeNumber(row.impressions), 0);
        const clicks = googleAdsMetricsRaw.reduce((sum, row) => sum + safeNumber(row.clicks), 0);
        return renderSingleStatCard('Avg CTR', formatPercent(impressions > 0 ? (clicks / impressions) * 100 : 0));
      }
      case 'gads-avg-cpc-stat': {
        const clicks = googleAdsMetricsRaw.reduce((sum, row) => sum + safeNumber(row.clicks), 0);
        const cost = googleAdsMetricsRaw.reduce((sum, row) => sum + safeNumber(row.cost), 0);
        return renderSingleStatCard('Avg CPC', formatCurrency(clicks > 0 ? cost / clicks : 0, dashboardData.currency));
      }
      case 'gads-conversions-stat':
        return renderSingleStatCard('Conversions', formatNumber(googleAdsMetricsRaw.reduce((sum, row) => sum + safeNumber(row.conversions), 0)));
      case 'gads-conv-rate-stat': {
        const clicks = googleAdsMetricsRaw.reduce((sum, row) => sum + safeNumber(row.clicks), 0);
        const conversions = googleAdsMetricsRaw.reduce((sum, row) => sum + safeNumber(row.conversions), 0);
        return renderSingleStatCard('Conv. Rate', formatPercent(clicks > 0 ? (conversions / clicks) * 100 : 0));
      }
      case 'gads-cost-per-conv-stat': {
        const conversions = googleAdsMetricsRaw.reduce((sum, row) => sum + safeNumber(row.conversions), 0);
        const cost = googleAdsMetricsRaw.reduce((sum, row) => sum + safeNumber(row.cost), 0);
        return renderSingleStatCard('Cost / Conv', formatCurrency(conversions > 0 ? cost / conversions : 0, dashboardData.currency));
      }

      case 'gads-ctr-trend': {
        const ctrBreakdown = getSelectedBreakdown('gads-ctr-trend');
        const chartRows = ctrBreakdown === 'cumulative'
          ? googleAdsMetricsRaw.map((row) => ({
            name: formatCompactDateLabel(row.date ?? row.segment_date),
            bucketLabel: formatCompactDateLabel(row.date ?? row.segment_date),
            CTR: safeNumber(row.impressions) > 0 ? (safeNumber(row.clicks) / safeNumber(row.impressions)) * 100 : 0,
          }))
          : aggregateScalarRowsByBreakdown(
            googleAdsMetricsRaw,
            ctrBreakdown,
            (row) => getRowDateValue(row, 'date', 'segment_date'),
            {
              impressions: (row) => safeNumber(row.impressions),
              clicks: (row) => safeNumber(row.clicks),
            }
          ).map((row) => {
            const impressions = safeNumber(row.impressions);
            const clicks = safeNumber(row.clicks);
            return {
              ...row,
              CTR: impressions > 0 ? (clicks / impressions) * 100 : 0,
            };
          });
        if (chartRows.length === 0) return <div className="client-dashboard__empty">No Google Ads trend data available for this period.</div>;
        const viz = widgetLayouts['gads-ctr-trend']?.visualizationConfig;
        return renderBreakdownSeriesContent(
          'gads-ctr-trend',
          viz?.chartType ?? 'line',
          Boolean(viz?.horizontal),
          chartRows,
          ['CTR'],
          'No Google Ads trend data available for this period.',
          (value) => `${value.toFixed(2)}%`,
          (value) => `${value}%`
        );
      }

      case 'gads-impressions-clicks-trend': {
        const volumeBreakdown = getSelectedBreakdown('gads-impressions-clicks-trend');
        const chartRows = volumeBreakdown === 'cumulative'
          ? googleAdsMetricsRaw.map((row) => ({
            name: formatCompactDateLabel(row.date ?? row.segment_date),
            bucketLabel: formatCompactDateLabel(row.date ?? row.segment_date),
            Impressions: safeNumber(row.impressions),
            Clicks: safeNumber(row.clicks),
          }))
          : aggregateScalarRowsByBreakdown(
            googleAdsMetricsRaw,
            volumeBreakdown,
            (row) => getRowDateValue(row, 'date', 'segment_date'),
            {
              Impressions: (row) => safeNumber(row.impressions),
              Clicks: (row) => safeNumber(row.clicks),
            }
          );
        if (chartRows.length === 0) return <div className="client-dashboard__empty">No Google Ads volume trend data available for this period.</div>;
        const viz = widgetLayouts['gads-impressions-clicks-trend']?.visualizationConfig;
        return renderBreakdownSeriesContent(
          'gads-impressions-clicks-trend',
          viz?.chartType ?? 'bar',
          Boolean(viz?.horizontal),
          chartRows,
          ['Impressions', 'Clicks'],
          'No Google Ads volume trend data available for this period.',
          formatNumber,
          undefined,
          undefined,
          { showAllLabels: true }
        );
      }

      case 'gads-performance-by-device': {
        const devicePerformanceBreakdown = getSelectedBreakdown('gads-performance-by-device');
        if (devicePerformanceBreakdown !== 'cumulative') {
          if (crossPageLoading['gads-performance-by-device'] && googleAdsDeviceTimeseriesRowsRaw.length === 0) {
            return <div className="client-dashboard__empty">Loading Google Ads device breakdown…</div>;
          }
          const grouped = aggregateSeriesByBreakdown(
            googleAdsDeviceTimeseriesRowsRaw,
            devicePerformanceBreakdown,
            'Clicks',
            (row) => getRowDateValue(row, 'date'),
            (row) => String(row.label ?? row.device ?? row.name ?? 'Unknown'),
            (row) => safeNumber(row.clicks),
            6
          );
          return renderBreakdownSeriesContent(
            'gads-performance-by-device',
            widgetLayouts['gads-performance-by-device']?.visualizationConfig?.chartType ?? 'bar',
            Boolean(widgetLayouts['gads-performance-by-device']?.visualizationConfig?.horizontal),
            grouped.chartRows,
            grouped.seriesKeys,
            'No Google Ads device trend data is available for the selected range.',
            formatNumber
          );
        }
        const rows = googleAdsDeviceRows.map((row) => ({
          name: String(row.device ?? row._id ?? 'Unknown'),
          Clicks: safeNumber(row.clicks),
          Conversions: safeNumber(row.conversions),
        }));
        if (rows.length === 0) return <div className="client-dashboard__empty">No Google Ads device data available for this period.</div>;
        const viz = widgetLayouts['gads-performance-by-device']?.visualizationConfig;
        if (viz && viz.chartType !== 'table') {
          const isHorizontal = Boolean(viz.horizontal && viz.chartType === 'bar');
          const pieRows = buildCompactPieSeries(rows.map((row) => ({ name: row.name, value: row.Clicks })), isMaximizedView ? 10 : 6);
          const pieLabel = createPieLabelRenderer({
            dataLength: pieRows.length,
            maxLabels: pieLabelDensity.maxLabels,
            formatValue: (_value, _payload, percent) => `${Math.round((percent ?? 0) * 100)}%`,
            insideThreshold: pieLabelDensity.insideThreshold,
            minPercentage: pieLabelDensity.minPercentage,
          });
          const pieLabelLine = createPieLabelLineRenderer({
            dataLength: pieRows.length,
            maxLabels: pieLabelDensity.maxLabels,
            insideThreshold: pieLabelDensity.insideThreshold,
            minPercentage: pieLabelDensity.minPercentage,
          });
          const lineLabel = createDashboardLineLabels({
            rowCount: rows.length,
            seriesCount: 2,
            formatValue: (value) => formatNumber(value),
          });
          const clicksBarLabel = createDashboardBarLabels({
            rowCount: rows.length,
            seriesCount: 2,
            formatValue: (value) => formatNumber(value),
            orientation: isHorizontal ? 'horizontal' : 'vertical',
          });
          const conversionsBarLabel = createDashboardBarLabels({
            rowCount: rows.length,
            seriesCount: 2,
            formatValue: (value) => formatNumber(value),
            orientation: isHorizontal ? 'horizontal' : 'vertical',
          });
          return (
            <ResponsiveContainer width="100%" height="90%">
              {viz.chartType === 'pie' ? (
                <PieChart>
                  <Tooltip formatter={(value: number) => formatNumber(value)} />
                  <Legend />
                  <Pie data={pieRows} dataKey="value" nameKey="name" outerRadius={pieLabelDensity.outerRadius} label={pieLabel} labelLine={pieLabelLine}>
                    {pieRows.map((entry, index) => <Cell key={`${entry.name}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />)}
                  </Pie>
                </PieChart>
              ) : viz.chartType === 'line' ? (
                <LineChart data={rows} margin={{ top: 24, right: 16, left: 0, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="Clicks" stroke="#496786" strokeWidth={2} dot={{ r: 3 }}>
                    <LabelList content={lineLabel} />
                  </Line>
                  <Line type="monotone" dataKey="Conversions" stroke="#82ca9d" strokeWidth={2} dot={{ r: 3 }}>
                    <LabelList content={lineLabel} />
                  </Line>
                </LineChart>
              ) : (
                <BarChart data={rows} layout={isHorizontal ? 'vertical' : 'horizontal'} margin={{ top: 24, right: 16, left: isHorizontal ? 12 : 0, bottom: isHorizontal ? 0 : 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                  {isHorizontal ? (
                    <>
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={110} />
                    </>
                  ) : (
                    <>
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                    </>
                  )}
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Clicks" fill="#496786" radius={[3, 3, 0, 0]}>
                    <LabelList content={clicksBarLabel} />
                  </Bar>
                  <Bar dataKey="Conversions" fill="#82ca9d" radius={[3, 3, 0, 0]}>
                    <LabelList content={conversionsBarLabel} />
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          );
        }
        return renderGenericTable(
          'gads-performance-by-device',
          ['Device', 'Clicks', 'Conversions'],
          rows.map((row) => [row.name, formatNumber(row.Clicks), formatNumber(row.Conversions)]),
          'No Google Ads device data available for this period.'
        );
      }

      case 'gads-stat-impressions': {
        const gadsSnap = crossPageData['gads-stat-impressions'] as Record<string, unknown> | undefined;
        const imp = gadsSnap ? safeNumber(gadsSnap.impressions) : 0;
        const clicks = gadsSnap ? safeNumber(gadsSnap.clicks) : 0;
        return (
          <div className="client-dashboard__kpi-grid">
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Impressions</div>
              <div className="client-dashboard__kpi-value">{formatNumber(imp)}</div>
            </div>
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Clicks</div>
              <div className="client-dashboard__kpi-value">{formatNumber(clicks)}</div>
            </div>
          </div>
        );
      }

      case 'gads-stat-clicks': {
        const gadsSnap2 = crossPageData['gads-stat-clicks'] as Record<string, unknown> | undefined;
        const ctr = gadsSnap2 ? safeNumber(gadsSnap2.ctr) : 0;
        const cl = gadsSnap2 ? safeNumber(gadsSnap2.clicks) : 0;
        return (
          <div className="client-dashboard__kpi-grid">
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Clicks</div>
              <div className="client-dashboard__kpi-value">{formatNumber(cl)}</div>
            </div>
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">CTR</div>
              <div className="client-dashboard__kpi-value">{formatPercent(ctr ?? 0)}</div>
            </div>
          </div>
        );
      }

      case 'gads-stat-cost': {
        const costSnap = crossPageData['gads-stat-cost'] as Record<string, unknown> | undefined;
        const cost = costSnap ? safeNumber(costSnap.cost) : 0;
        return (
          <div className="client-dashboard__kpi-grid">
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Google Ads Spend</div>
              <div className="client-dashboard__kpi-value">{formatCurrency(cost, dashboardData?.currency ?? 'INR')}</div>
            </div>
          </div>
        );
      }

      case 'gads-campaign-performance-summary':
      case 'gads-campaign-table': {
        const summaryRows = Array.isArray(crossPageData['gads-campaign-performance-summary'])
          ? crossPageData['gads-campaign-performance-summary'] as GoogleAdsCampaignSummary[]
          : [];
        const rawRows = Array.isArray(crossPageData['gads-campaign-table'])
          ? crossPageData['gads-campaign-table'] as GoogleAdsMetric[]
          : [];
        const gdRows = summaryRows.length > 0
          ? summaryRows
          : buildGoogleAdsCampaignSummaryFallback(rawRows);
        const tableStateKey = widgetId === 'gads-campaign-table'
          ? 'gads-campaign-table'
          : 'gads-campaign-performance-summary';
        if (gdRows.length === 0) return <div className="client-dashboard__empty">No Google Ads campaign data for this period.</div>;
        return (
          <>
            <div className="table-container client-dashboard__table-container--paged">
              <table className="data-table">
                <thead><tr><th>Campaign</th><th>Impressions</th><th>Clicks</th><th>CTR</th><th>Cost</th><th>Conversions</th></tr></thead>
                <tbody>
                  {pageRows(tableStateKey, gdRows).map((campaign) => (
                    <tr key={`${campaign.campaign_id}:${campaign.campaign_name}`}>
                      <td>{campaign.campaign_name}</td>
                      <td>{formatNumber(campaign.impressions)}</td>
                      <td>{formatNumber(campaign.clicks)}</td>
                      <td>{formatPercent(campaign.ctr)}</td>
                      <td>{formatCurrency(campaign.cost, dashboardData?.currency ?? 'INR')}</td>
                      <td>{formatNumber(campaign.conversions)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderTableFooter(tableStateKey, gdRows.length)}
          </>
        );
      }

      case 'email-stat-openrate':
        return (
          <div className="client-dashboard__kpi-grid">
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Open Rate</div>
              <div className="client-dashboard__kpi-value">{formatPercent(dashboardData.emailSummary.openRate)}</div>
            </div>
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Total Opens</div>
              <div className="client-dashboard__kpi-value">{formatNumber(dashboardData.emailSummary.opens)}</div>
            </div>
          </div>
        );

      case 'email-stat-ctr':
        return (
          <div className="client-dashboard__kpi-grid">
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Click Rate</div>
              <div className="client-dashboard__kpi-value">{formatPercent(dashboardData.emailSummary.ctr)}</div>
            </div>
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Total Clicks</div>
              <div className="client-dashboard__kpi-value">{formatNumber(dashboardData.emailSummary.clicks)}</div>
            </div>
          </div>
        );

      case 'email-stat-sent':
        return (
          <div className="client-dashboard__kpi-grid">
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Emails Sent</div>
              <div className="client-dashboard__kpi-value">{formatNumber(dashboardData.emailSummary.sent)}</div>
            </div>
            <div className="client-dashboard__kpi-card">
              <div className="client-dashboard__kpi-label">Delivery Rate</div>
              <div className="client-dashboard__kpi-value">{formatPercent(dashboardData.emailSummary.deliveryRate)}</div>
            </div>
          </div>
        );

      case 'email-top-campaigns': {
        const ecData = crossPageData['email-campaigns'] as Record<string, unknown> | undefined;
        const ecRows = Array.isArray(ecData?.campaigns) ? ecData.campaigns as Record<string, unknown>[] :
          Array.isArray(ecData?.data) ? ecData.data as Record<string, unknown>[] : [];
        if (ecRows.length === 0) return <div className="client-dashboard__empty">No email campaign data. Add the "Email Campaign Performance" widget to load data.</div>;
        const normalizedEmailRows = ecRows.map((row) => {
          const sent = safeNumber(row.total_sent ?? row.sent);
          const delivered = safeNumber(row.total_delivered ?? row.delivered);
          const opens = safeNumber(row.opens ?? row.total_opens);
          const clicks = safeNumber(row.clicks ?? row.total_clicks);
          const denominator = delivered > 0 ? delivered : sent;
          const fallbackOpenRate = divideOrNull(opens * 100, denominator) ?? 0;
          const fallbackCtr = divideOrNull(clicks * 100, denominator) ?? 0;
          return {
            campaignId: String(row.campaign_id ?? ''),
            campaignName: String(row.campaign_name ?? row.subject ?? '-'),
            delivered,
            openRate: normalizePercentValue(row.open_rate ?? row.avg_open_rate ?? row.opens_percentage ?? fallbackOpenRate),
            ctr: normalizePercentValue(row.click_rate ?? row.ctr ?? row.avg_click_rate ?? row.clicks_percentage ?? fallbackCtr),
          };
        });
        const normalizedFilter = emailTopCampaignFilter.trim().toLowerCase();
        const filteredEmailRows = normalizedFilter
          ? normalizedEmailRows.filter((row) => row.campaignName.toLowerCase().includes(normalizedFilter))
          : normalizedEmailRows;
        const sortedEmailRows = [...filteredEmailRows].sort((left, right) => {
          if (emailTopCampaignSortBy === 'openRate') {
            return right.openRate - left.openRate;
          }
          if (emailTopCampaignSortBy === 'ctr') {
            return right.ctr - left.ctr;
          }
          return right.delivered - left.delivered;
        });
        return (
          <>
            <div className="client-dashboard__widget-filters client-dashboard__widget-filters--right">
              <div className="client-dashboard__widget-filter">
                <label htmlFor="email-top-campaigns-filter">Filter</label>
                <input
                  id="email-top-campaigns-filter"
                  type="search"
                  value={emailTopCampaignFilter}
                  placeholder="Search campaign"
                  onChange={(event) => setEmailTopCampaignFilter(event.target.value)}
                />
              </div>
              <div className="client-dashboard__widget-filter">
                <label htmlFor="email-top-campaigns-sort">Sort by</label>
                <select
                  id="email-top-campaigns-sort"
                  value={emailTopCampaignSortBy}
                  onChange={(event) => setEmailTopCampaignSortBy(event.target.value as EmailTopCampaignSortBy)}
                >
                  <option value="delivered">Delivered</option>
                  <option value="openRate">Open Rate</option>
                  <option value="ctr">CTR</option>
                </select>
              </div>
            </div>
            <div className="table-container client-dashboard__table-container--paged">
              <table className="data-table">
                <thead><tr><th>Campaign</th><th>Delivered</th><th>Open Rate</th><th>CTR</th></tr></thead>
                <tbody>
                  {pageRows('email-top-campaigns', sortedEmailRows).map((row, i) => (
                    <tr key={row.campaignId || String(i)}>
                      <td>{row.campaignName}</td>
                      <td>{formatNumber(row.delivered)}</td>
                      <td>{formatPercent(row.openRate)}</td>
                      <td>{formatPercent(row.ctr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderTableFooter('email-top-campaigns', sortedEmailRows.length)}
          </>
        );
      }

      case 'bofu-monthly-trend':
      case 'qualified-monthly-trend': {
        const trendKey = widgetId === 'bofu-monthly-trend' ? 'bofu-monthly-trend' : 'qualified-monthly-trend';
        const trendMonths = trendMonthSettings[trendKey];
        if (crossPageLoading[trendKey]) {
          return <div className="client-dashboard__empty">Loading monthly trend data…</div>;
        }
        const trendRows = Array.isArray(crossPageData[trendKey])
          ? crossPageData[trendKey] as MonthlyTrendRow[]
          : [];
        if (trendRows.length === 0) {
          return <div className="client-dashboard__empty">No monthly trend data available for the selected {trendMonths}-month window.</div>;
        }

        const windowRows = [...trendRows]
          .sort((a, b) => a.monthKey.localeCompare(b.monthKey))
          .slice(-trendMonths);

        const viz = widgetLayouts[widgetId]?.visualizationConfig;
        const chartType = viz?.chartType ?? 'bar';
        const isHorizontal = Boolean(viz?.horizontal && chartType === 'bar');
        const chartData = windowRows.map((row) => ({ month: row.monthLabel, value: row.value }));
        const dataKeyLabel = widgetId === 'bofu-monthly-trend' ? 'BOFU Leads' : 'Qualified Leads';
        const color = widgetId === 'bofu-monthly-trend' ? '#496786' : '#C78244';

        if (chartType === 'table') {
          return (
            <>
              <div className="client-dashboard__widget-filters client-dashboard__widget-filters--right">
                <div className="client-dashboard__widget-filter">
                  <label htmlFor={`${trendKey}-months`}>Months</label>
                  <select
                    id={`${trendKey}-months`}
                    className="client-dashboard__table-rows-select"
                    value={trendMonths}
                    onChange={(event) => setTrendMonths(trendKey, Number(event.target.value) as TrendMonthOption)}
                  >
                    {TREND_MONTH_OPTIONS.map((months) => (
                      <option key={months} value={months}>Last {months} months</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="table-container client-dashboard__table-container--paged">
                <table className="data-table">
                  <thead>
                    <tr><th>Month</th><th>{dataKeyLabel}</th></tr>
                  </thead>
                  <tbody>
                    {pageRows(trendKey, windowRows).map((row) => (
                      <tr key={row.monthKey}>
                        <td>{row.monthLabel}</td>
                        <td>{formatNumber(row.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {renderTableFooter(trendKey, windowRows.length)}
            </>
          );
        }

        const barLabel = createDashboardBarLabels({
          rowCount: chartData.length,
          seriesCount: 1,
          formatValue: (value) => formatNumber(value),
          orientation: isHorizontal ? 'horizontal' : 'vertical',
        });
        const lineLabel = createDashboardLineLabels({
          rowCount: chartData.length,
          seriesCount: 1,
          formatValue: (value) => formatNumber(value),
        });

        const pieData = chartData.map((item) => ({ name: item.month, value: item.value }));
        const pieLabel = createPieLabelRenderer({
          dataLength: pieData.length,
          maxLabels: pieLabelDensity.maxLabels,
          formatValue: (_value, _payload, percent) => `${Math.round((percent ?? 0) * 100)}%`,
          insideThreshold: pieLabelDensity.insideThreshold,
          minPercentage: pieLabelDensity.minPercentage,
        });
        const pieLabelLine = createPieLabelLineRenderer({
          dataLength: pieData.length,
          maxLabels: pieLabelDensity.maxLabels,
          insideThreshold: pieLabelDensity.insideThreshold,
          minPercentage: pieLabelDensity.minPercentage,
        });

        return (
          <>
            <div className="client-dashboard__widget-filters client-dashboard__widget-filters--right">
              <div className="client-dashboard__widget-filter">
                <label htmlFor={`${trendKey}-months-chart`}>Months</label>
                <select
                  id={`${trendKey}-months-chart`}
                  className="client-dashboard__table-rows-select"
                  value={trendMonths}
                  onChange={(event) => setTrendMonths(trendKey, Number(event.target.value) as TrendMonthOption)}
                >
                  {TREND_MONTH_OPTIONS.map((months) => (
                    <option key={months} value={months}>Last {months} months</option>
                  ))}
                </select>
              </div>
            </div>
            <ResponsiveContainer width="100%" height="88%">
              {chartType === 'line' ? (
              <LineChart data={chartData} margin={{ top: 24, right: 16, left: 0, bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                <XAxis dataKey="month" angle={-30} textAnchor="end" tick={{ fontSize: 10 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip formatter={(value: number) => formatNumber(value)} />
                <Line type="monotone" dataKey="value" name={dataKeyLabel} stroke={color} strokeWidth={2} dot={{ r: 4 }}>
                  <LabelList content={lineLabel} />
                </Line>
              </LineChart>
              ) : chartType === 'pie' ? (
                <PieChart>
                  <Tooltip formatter={(value: number) => formatNumber(value)} />
                  <Legend />
                  <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={pieLabelDensity.outerRadius} label={pieLabel} labelLine={pieLabelLine}>
                    {pieData.map((entry, index) => (
                      <Cell key={`${entry.name}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                </PieChart>
              ) : (
                <BarChart
                  data={chartData}
                  layout={isHorizontal ? 'vertical' : 'horizontal'}
                  margin={{ top: 24, right: 16, left: 0, bottom: isHorizontal ? 0 : 50 }}
                >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,56,88,0.1)" />
                {isHorizontal ? (
                  <>
                    <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis dataKey="month" type="category" tick={{ fontSize: 10 }} width={92} />
                  </>
                ) : (
                  <>
                    <XAxis dataKey="month" angle={-30} textAnchor="end" tick={{ fontSize: 10 }} interval={0} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  </>
                )}
                <Tooltip formatter={(value: number) => formatNumber(value)} />
                <Bar dataKey="value" fill={color} radius={[3, 3, 0, 0]}>
                  <LabelList content={barLabel} />
                </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          </>
        );
      }

      case 'keyword-ranking-monthly': {
        const trendMonths = trendMonthSettings['keyword-ranking-monthly'];
        const trendRowsRaw = crossPageData['keyword-ranking-monthly'];
        const hasLoadedTrendRows = Array.isArray(trendRowsRaw);
        const trendRows = hasLoadedTrendRows
          ? trendRowsRaw as MonthlyKeywordTrendRow[]
          : [];

        const matchTypeFilter = keywordTrendMatchType;
        const metric = keywordTrendRankBy;
        const monthKeysDesc = buildLastNMonthWindows(trendMonths, activeRange.end)
          .map((window) => window.key)
          .sort((a, b) => b.localeCompare(a));
        const monthKeySet = new Set(monthKeysDesc);

        const filteredRows = (matchTypeFilter === 'ALL'
          ? trendRows
          : trendRows.filter((row) => row.matchType === matchTypeFilter)
        ).filter((row) => monthKeySet.has(row.monthKey));

        const monthlyBuckets = new Map<string, MonthlyKeywordTrendRow[]>();
        filteredRows.forEach((row) => {
          const existing = monthlyBuckets.get(row.monthKey) || [];
          existing.push(row);
          monthlyBuckets.set(row.monthKey, existing);
        });

        const rankByMonthKeyword = new Map<string, number>();
        monthKeysDesc.forEach((monthKey) => {
          const rows = monthlyBuckets.get(monthKey) || [];
          const sorted = [...rows].sort((a, b) => {
            const metricA = metric === 'impressions'
              ? a.impressions
              : metric === 'clicks'
                ? a.clicks
                : metric === 'ctr'
                  ? a.ctr
                  : a.conversions;
            const metricB = metric === 'impressions'
              ? b.impressions
              : metric === 'clicks'
                ? b.clicks
                : metric === 'ctr'
                  ? b.ctr
                  : b.conversions;
            if (metricB !== metricA) return metricB - metricA;
            if (a.matchType !== b.matchType) return a.matchType.localeCompare(b.matchType);
            return a.keyword.localeCompare(b.keyword);
          });

          sorted.forEach((row, index) => {
            rankByMonthKeyword.set(`${monthKey}__${row.keyword}__${row.matchType}`, index + 1);
          });
        });

        interface KeywordRankingMatrixRow {
          keyword: string;
          matchType: string;
          monthlyRank: Record<string, number | null>;
          primarySortRank: number;
        }

        const matrixRowsMap = new Map<string, KeywordRankingMatrixRow>();
        filteredRows.forEach((row) => {
          const key = `${row.keyword}__${row.matchType}`;
          if (!matrixRowsMap.has(key)) {
            const monthlyRank: Record<string, number | null> = {};
            monthKeysDesc.forEach((monthKey) => {
              monthlyRank[monthKey] = null;
            });
            matrixRowsMap.set(key, {
              keyword: row.keyword,
              matchType: row.matchType,
              monthlyRank,
              primarySortRank: Number.MAX_SAFE_INTEGER,
            });
          }

          const matrixRow = matrixRowsMap.get(key);
          if (!matrixRow) return;

          const rank = rankByMonthKeyword.get(`${row.monthKey}__${row.keyword}__${row.matchType}`) ?? null;
          matrixRow.monthlyRank[row.monthKey] = rank;
        });

        const firstAvailableMonthKey = monthKeysDesc.find((monthKey) =>
          Array.from(matrixRowsMap.values()).some((row) => row.monthlyRank[monthKey] !== null)
        ) || monthKeysDesc[0];
        const matrixRows = Array.from(matrixRowsMap.values())
          .map((row) => ({
            ...row,
            primarySortRank: firstAvailableMonthKey
              ? (row.monthlyRank[firstAvailableMonthKey] ?? Number.MAX_SAFE_INTEGER)
              : Number.MAX_SAFE_INTEGER,
          }))
          .sort((a, b) => {
            if (a.primarySortRank !== b.primarySortRank) return a.primarySortRank - b.primarySortRank;
            if (a.matchType !== b.matchType) return a.matchType.localeCompare(b.matchType);
            return a.keyword.localeCompare(b.keyword);
          });

        const showLoadingState = crossPageLoading['keyword-ranking-monthly'] || !hasLoadedTrendRows;
        const isEmptyWindow = !showLoadingState && trendRows.length === 0;

        return (
          <>
            <div className="client-dashboard__widget-filters client-dashboard__widget-filters--right">
              <div className="client-dashboard__widget-filter">
                <label htmlFor="keyword-monthly-months">Months</label>
                <select
                  id="keyword-monthly-months"
                  className="client-dashboard__table-rows-select"
                  value={trendMonths}
                  onChange={(event) => setTrendMonths('keyword-ranking-monthly', Number(event.target.value) as TrendMonthOption)}
                >
                  {TREND_MONTH_OPTIONS.map((months) => (
                    <option key={months} value={months}>Last {months} months</option>
                  ))}
                </select>
              </div>
              <div className="client-dashboard__widget-filter">
                <label htmlFor="keyword-monthly-match-type">Match Type</label>
                <select
                  id="keyword-monthly-match-type"
                  className="client-dashboard__table-rows-select"
                  value={keywordTrendMatchType}
                  onChange={(event) => setKeywordTrendMatchType(event.target.value as KeywordMatchTypeFilter)}
                >
                  <option value="ALL">All Match Types</option>
                  <option value="BROAD">BROAD</option>
                  <option value="EXACT">EXACT</option>
                  <option value="PHRASE">PHRASE</option>
                </select>
              </div>
              <div className="client-dashboard__widget-filter">
                <label htmlFor="keyword-monthly-rank-by">Ranking By</label>
                <select
                  id="keyword-monthly-rank-by"
                  className="client-dashboard__table-rows-select"
                  value={keywordTrendRankBy}
                  onChange={(event) => setKeywordTrendRankBy(event.target.value as KeywordRankingMetric)}
                >
                  <option value="impressions">Impressions</option>
                  <option value="clicks">Clicks</option>
                  <option value="ctr">CTR</option>
                  <option value="conversions">Conversions</option>
                </select>
              </div>
            </div>
            <div className="client-dashboard__table-note">Values shown below are monthly ranks based on the selected metric (1 = highest).</div>
            {showLoadingState ? (
              <div className="client-dashboard__empty">Loading monthly keyword ranking data…</div>
            ) : (
              <>
                <div className="table-container client-dashboard__table-container--paged">
                  <table className="data-table client-dashboard__keyword-trend-table">
                    <thead className="client-dashboard__keyword-trend-head">
                      <tr>
                        <th className="client-dashboard__keyword-header-cell">Keyword</th>
                        <th className="client-dashboard__keyword-header-cell">Match Type</th>
                        {monthKeysDesc.map((monthKey) => (
                          <th key={monthKey} className="client-dashboard__keyword-header-cell">{toMonthShortLabel(monthKey)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows('keyword-ranking-monthly', matrixRows).map((row, index) => (
                        <tr key={`${row.keyword}-${row.matchType}-${index}`}>
                          <td>{row.keyword}</td>
                          <td>{row.matchType}</td>
                          {monthKeysDesc.map((monthKey) => {
                            const rank = row.monthlyRank[monthKey];
                            return <td key={`${row.keyword}-${row.matchType}-${monthKey}`}>{rank === null ? '-' : String(rank)}</td>;
                          })}
                        </tr>
                      ))}
                      {matrixRows.length === 0 && (
                        <tr>
                          <td colSpan={2 + monthKeysDesc.length}>
                            {isEmptyWindow
                              ? `No Google Ads keyword data found for the selected ${trendMonths}-month window.`
                              : 'No keywords match the selected filters.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {renderTableFooter('keyword-ranking-monthly', matrixRows.length)}
              </>
            )}
          </>
        );
      }

      default:
        return null;
    }
  };

  if (rbacLoading) {
    return <LoadingSpinner message="Loading dashboard access..." />;
  }

  if (accounts.length === 0) {
    return (
      <div className="page-content">
        <h1 className="page-title">Client Marketing Portal</h1>
        <div className="under-construction">
          <h2>No Account Access</h2>
          <p>You do not have account access configured yet. Contact admin to enable dashboard access.</p>
        </div>
      </div>
    );
  }

  if (!canViewDashboardPage) {
    return (
      <div className="page-content">
        <h1 className="page-title">Client Marketing Portal</h1>
        <div className="under-construction">
          <h2>Access Restricted</h2>
          <p>You do not have access to the Dashboard page. Contact admin to enable page access.</p>
        </div>
      </div>
    );
  }

  if (loading && !dashboardData) {
    return <LoadingSpinner message="Loading dashboard metrics..." />;
  }

  return (
    <div className="page-content client-dashboard">
      <div className="client-dashboard__hero">
        <div className="client-dashboard__hero-left">
          <div className="client-dashboard__kicker">Wizrdom</div>
          <h1 className="client-dashboard__title">Client Marketing Portal</h1>
          <p className="client-dashboard__subtitle">
            Dashboard for integrated full-funnel marketing performance reporting.
          </p>
        </div>

        <div className="client-dashboard__hero-right">
          <label className="client-dashboard__field-label" htmlFor="dashboard-account-select">
            Account
          </label>
          <select
            id="dashboard-account-select"
            className="client-dashboard__select"
            value={draftFilters.account || ''}
            onChange={(event) => {
              const nextAccount = event.target.value || null;
              setDraftFilters((current) => ({ ...current, account: nextAccount }));
              setSelectedAccount(nextAccount);
            }}
          >
            {accounts.map((account) => (
              <option key={account} value={account}>
                {account}
              </option>
            ))}
          </select>

          <div className="client-dashboard__sync-pill">
            Range: {activeRange.label} | Last sync: {lastSyncedAt || 'Not synced yet'}
          </div>

          <button
            type="button"
            className="wizrdom-button small"
            onClick={() => {
              const nextApplied = applyDraftFilters(true);
              if (nextApplied) {
                runBackgroundSync(nextApplied).catch((syncError) => {
                  logger.warn('[Dashboard] Background sync orchestration failed', syncError);
                  setSyncState('partial_error');
                  setSyncMessage('Saved data is shown, but background sync could not be completed right now.');
                  // Ensure the button is re-enabled even if the sync throws.
                  setRefreshing(false);
                });
              }
            }}
            disabled={refreshing || loading}
          >
            {refreshing ? 'Refreshing...' : loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="client-dashboard__panel">
        <div className="client-dashboard__panel-left">
          <h2 className="client-dashboard__panel-title">{dashboardData?.clientName || appliedFilters?.account || draftFilters.account}</h2>
        </div>
      </div>

      <div className="client-dashboard__controls">
        <div className="client-dashboard__preset-group">
          <button
            type="button"
            className={`client-dashboard__preset ${draftFilters.datePreset === '7d' ? 'active' : ''}`}
            onClick={() => setDraftFilters((current) => ({ ...current, datePreset: '7d' }))}
          >
            Last 1 Week
          </button>
          <button
            type="button"
            className={`client-dashboard__preset ${draftFilters.datePreset === '30d' ? 'active' : ''}`}
            onClick={() => setDraftFilters((current) => ({ ...current, datePreset: '30d' }))}
          >
            Last 30 Days
          </button>
          <button
            type="button"
            className={`client-dashboard__preset ${draftFilters.datePreset === 'custom' ? 'active' : ''}`}
            onClick={() => setDraftFilters((current) => ({ ...current, datePreset: 'custom' }))}
          >
            Custom
          </button>
        </div>

        {draftFilters.datePreset === 'custom' && (
          <div className="client-dashboard__custom-range">
            <label htmlFor="dashboard-start-date">From</label>
            <input
              id="dashboard-start-date"
              type="date"
              value={draftFilters.customStartDate}
              onChange={(event) => setDraftFilters((current) => ({ ...current, customStartDate: event.target.value }))}
            />
            <label htmlFor="dashboard-end-date">To</label>
            <input
              id="dashboard-end-date"
              type="date"
              value={draftFilters.customEndDate}
              onChange={(event) => setDraftFilters((current) => ({ ...current, customEndDate: event.target.value }))}
            />
          </div>
        )}
      </div>

      {error && (
        <div className="client-dashboard__error">
          {error}
        </div>
      )}
      {!error && syncMessage && (
        <div className={`client-dashboard__warning ${syncState === 'partial_error' ? 'client-dashboard__warning--soft' : ''}`}>
          <strong>
            {syncState === 'syncing'
              ? 'Background refresh in progress.'
              : syncState === 'complete'
                ? 'Background refresh complete.'
                : syncState === 'partial_error'
                  ? 'Background refresh partially completed.'
                  : 'Dashboard status.'}
          </strong>{' '}
          <span>{syncMessage}</span>
        </div>
      )}
      {!error && endpointWarningText && (
        <div className="client-dashboard__warning">
          <strong>{endpointWarningText.title}</strong>{' '}
          <span>{endpointWarningText.detail}</span>
        </div>
      )}

      {dashboardData && (
        <>
          <div className="client-dashboard__customize-bar">
            <div className="client-dashboard__customize-left">
              <span>{visibleWidgets.length} active widgets</span>
              {canCustomizeDashboard && (
                <button
                  type="button"
                  className="client-dashboard__add-widget-btn"
                  onClick={() => { setShowWidgetGallery(true); setGalleryTab('dashboard'); setGallerySearch(''); }}
                >
                  <AddIcon fontSize="small" />
                  Add Widget
                </button>
              )}
            </div>
            {canCustomizeDashboard && (
              <div className="client-dashboard__customize-actions">
                <button
                  type="button"
                  className="client-dashboard__reset-layout"
                  title="Save the current layout as your baseline"
                  onClick={handleSaveLayout}
                >
                  Save layout
                </button>
                <button
                  type="button"
                  className="client-dashboard__reset-layout"
                  onClick={handleLayoutReset}
                >
                  <RestartAltIcon fontSize="small" />
                  {savedSnapshot ? 'Restore layout' : 'Reset layout'}
                </button>
              </div>
            )}
          </div>

          {renderMaximizedPortal()}
          {renderWidgetGallery()}
          {renderChartEditorPanel()}

          <div className="client-dashboard__widget-grid">
            {visibleWidgets.map((widget) => renderWidgetFrame(
              widget,
              renderDashboardWidgetContent(widget.id)
            ))}
          </div>
        </>
      )}

      {createPortal(
        <Snackbar
          open={layoutNotice.open}
          autoHideDuration={layoutNotice.removedWidgetId ? 6000 : 3500}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={(_event, reason) => {
            if (reason !== 'clickaway') {
              setLayoutNotice((current) => ({ ...current, open: false }));
            }
          }}
        >
          <Alert
            severity="success"
            variant="filled"
            onClose={() => setLayoutNotice((current) => ({ ...current, open: false }))}
            action={layoutNotice.removedWidgetId ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  const removedWidgetId = layoutNotice.removedWidgetId;
                  if (!removedWidgetId) return;
                  handleWidgetRestore(removedWidgetId);
                  const widgetTitle = getWidgetDefinition(removedWidgetId)?.title || 'Widget';
                  setLayoutNotice({
                    open: true,
                    message: `${widgetTitle} restored to the dashboard.`,
                    removedWidgetId: null,
                  });
                }}
              >
                Undo
              </Button>
            ) : undefined}
            sx={{ width: '100%' }}
          >
            {layoutNotice.message}
          </Alert>
        </Snackbar>,
        document.body
      )}
    </div>
  );
};

export default Dashboard;
